import React, { useState, useEffect, useRef } from 'react';
import GutachtenVorschau from './GutachtenVorschau';
import GutachtenFormPDF from '../components/GutachtenFormPDF';
import SimpleImageEditor from '../components/SimpleImageEditor';
import InsuranceSearchCombobox from '../components/InsuranceSearchCombobox';
import InsuranceLogo from '../components/InsuranceLogo';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import AkteSidebar from '../components/AkteSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Save, TrendingDown, AlertCircle, AlertTriangle, BarChart3, ArrowRight, Camera, Upload, Sparkles, FileText, Users, Car, PenTool, Calculator, Wrench, Clock, Search, Eye, ClipboardList, DollarSign, FolderOpen, Mail, Plus, X, Check, CheckCircle, RefreshCw, Printer, Download, Calendar, Shield, UserCog, Zap, Info } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { faelleAPI } from '../utils/api';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';
import ContractForm from '../components/dat/ContractForm';

// Importierte Komponenten für Refaktorierung
import { 
  DocumentHeaderNew, 
  DocumentFooterNew, 
  PrintStyles,
  printStylesCSS,
  LeerFormContent,
  // Textbausteine Konstanten
  besichtigungsTexte,
  besichtigungszustandTexte,
  identifizierungTexte,
  scheckheftTexte,
  probelaufTexte,
  probefahrtTexte,
  fehlerspeicherTexte,
  allgemeinzustandTexte,
  karosseriezustandTexte,
  lackzustandTexte,
  innenraumzustandTexte,
  schadenhergangTexte,
  unfallprotokollTexte,
  plausibilitaetTexte,
  kompatibilitaetTexte
} from './NeueAkte/components';

// DAT Wiederbeschaffungswert Komponente
const DATWiederbeschaffungswert = ({ formData }) => {
  const [datLoading, setDatLoading] = useState(false);
  const [datResult, setDatResult] = useState(null);
  const [datError, setDatError] = useState(null);
  const [europaCode, setEuropaCode] = useState('');
  const [kilometerstand, setKilometerstand] = useState('');

  const handleDATBerechnung = async () => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('autogutachter24_token');

    // Validierung
    if (!europaCode && (!formData.marke || !formData.modell)) {
      setDatError('Bitte geben Sie entweder den DAT Europa-Code oder Marke/Modell ein.');
      return;
    }

    if (!formData.erstzulassung) {
      setDatError('Bitte geben Sie das Erstzulassungsdatum ein.');
      return;
    }

    if (!kilometerstand) {
      setDatError('Bitte geben Sie den Kilometerstand ein.');
      return;
    }

    setDatLoading(true);
    setDatError(null);
    setDatResult(null);

    try {
      const request_data = {
        dat_europacode: europaCode || null,
        hersteller: formData.marke || null,
        modell: formData.modell || null,
        erstzulassung: formData.erstzulassung,
        kilometerstand: parseInt(kilometerstand),
        kraftstoff: formData.kraftstoff || null,
        leistung_kw: formData.leistung_kw || null,
        vin: formData.vin || formData.vin_optional || null
      };

      const response = await fetch(`${BACKEND_URL}/api/dat-wiederbeschaffungswert/berechnen`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(request_data)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Fehler bei der Berechnung');
      }

      setDatResult(data);

    } catch (error) {
      setDatError(error.message);
    } finally {
      setDatLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="border-l-4 border-blue-500 pl-4 mb-6">
        <h3 className="text-lg font-bold text-gray-900 mb-2">DAT SilverDAT Wiederbeschaffungswert</h3>
        <p className="text-sm text-gray-600">Offizielle Fahrzeugbewertung nach DAT Europa-Code oder Fahrzeugdaten</p>
      </div>

      {/* Fahrzeugdaten Übersicht */}
      <div className="bg-white rounded-md border border-gray-200">
        <div className="p-6">
          <h4 className="font-semibold text-gray-900 mb-4">Aktuelle Fahrzeugdaten</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-600">Marke</p>
              <p className="font-medium">{formData.marke || '-'}</p>
            </div>
            <div>
              <p className="text-gray-600">Modell</p>
              <p className="font-medium">{formData.modell || '-'}</p>
            </div>
            <div>
              <p className="text-gray-600">Erstzulassung</p>
              <p className="font-medium">{formData.erstzulassung || '-'}</p>
            </div>
            <div>
              <p className="text-gray-600">VIN</p>
              <p className="font-medium">{formData.vin || formData.vin_optional || '-'}</p>
            </div>
            <div>
              <p className="text-gray-600">Kraftstoff</p>
              <p className="font-medium">{formData.kraftstoff || '-'}</p>
            </div>
            <div>
              <p className="text-gray-600">Leistung</p>
              <p className="font-medium">{formData.leistung_kw ? `${formData.leistung_kw} kW` : '-'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Eingabefelder */}
      <div className="bg-white rounded-md border border-gray-200">
        <div className="p-6">
          <h4 className="font-semibold text-gray-900 mb-4">Bewertungsparameter</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                DAT Europa-Code (optional)
              </label>
              <Input
                type="text"
                placeholder="z.B. 015700722040003"
                value={europaCode}
                onChange={(e) => setEuropaCode(e.target.value)}
                className="h-10"
                maxLength={15}
              />
              <p className="text-xs text-gray-500 mt-1">
                15-stelliger Code - wird bevorzugt verwendet
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kilometerstand <span className="text-red-500">*</span>
              </label>
              <Input
                type="number"
                placeholder="z.B. 85000"
                value={kilometerstand}
                onChange={(e) => setKilometerstand(e.target.value)}
                className="h-10"
                required
              />
            </div>
          </div>

          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-xs text-blue-800">
              <strong>Hinweis:</strong> Für die genaueste Bewertung verwenden Sie den DAT Europa-Code. 
              Alternativ werden Marke, Modell und weitere Fahrzeugdaten für die Bewertung verwendet.
            </p>
          </div>
        </div>
      </div>

      {/* Fehler Anzeige */}
      {datError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800">❌ {datError}</p>
        </div>
      )}

      {/* Ergebnis Anzeige */}
      {datResult && datResult.success && (
        <div className="border-green-500">
          <div className="p-6">
            <div className="flex items-start space-x-3 mb-6">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-green-600 text-xl">✓</span>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 text-lg">Bewertung erfolgreich</h4>
                <p className="text-sm text-gray-600 mt-1">Quelle: {datResult.quelle}</p>
                <p className="text-xs text-gray-500 mt-1">Bewertet am: {new Date(datResult.bewertungsdatum).toLocaleDateString('de-DE')}</p>
              </div>
            </div>

            {/* Bewertungsergebnisse */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Wiederbeschaffungswert</p>
                <p className="text-2xl font-bold text-blue-900">
                  {datResult.wiederbeschaffungswert ? 
                    `€${datResult.wiederbeschaffungswert.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` 
                    : 'N/A'}
                </p>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Händler-Einkaufswert</p>
                <p className="text-2xl font-bold text-gray-900">
                  {datResult.haendler_einkauf ? 
                    `€${datResult.haendler_einkauf.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` 
                    : 'N/A'}
                </p>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Händler-Verkaufswert</p>
                <p className="text-2xl font-bold text-gray-900">
                  {datResult.haendler_verkauf ? 
                    `€${datResult.haendler_verkauf.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` 
                    : 'N/A'}
                </p>
              </div>
            </div>

            {/* Fahrzeugdaten aus Bewertung */}
            {datResult.fahrzeugdaten && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h5 className="font-semibold text-gray-900 mb-3">Bewertete Fahrzeugdaten</h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  {Object.entries(datResult.fahrzeugdaten).map(([key, value]) => (
                    <div key={key}>
                      <p className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}</p>
                      <p className="font-medium">{value || '-'}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {datResult.error && (
              <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">⚠️ {datResult.error}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Berechnung Button */}
      <div className="flex justify-end">
        <Button
          type="button"
          onClick={handleDATBerechnung}
          disabled={datLoading || (!europaCode && (!formData.marke || !formData.modell)) || !formData.erstzulassung || !kilometerstand}
          className="bg-blue-600 hover:bg-blue-700 text-white px-8"
        >
          {datLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              Berechnung läuft...
            </>
          ) : (
            <>
              <Calculator className="w-4 h-4 mr-2" />
              DAT Bewertung abrufen
            </>
          )}
        </Button>
      </div>

      {/* Hinweise */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h5 className="font-semibold text-gray-900 mb-2">Wichtige Hinweise:</h5>
        <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
          <li>Die Bewertung basiert auf der DAT SilverDAT Datenbank</li>
          <li>Der Europa-Code liefert die genaueste Bewertung</li>
          <li>Ohne Europa-Code wird ein Fallback-Algorithmus verwendet</li>
          <li>Alle Preise verstehen sich als Richtwerte</li>
        </ul>
      </div>
    </div>
  );
};

// Restwert Abfrage Komponente
const RestwertAbfrage = ({ formData }) => {
  const [restwertLoading, setRestwertLoading] = useState(false);
  const [restwertResponse, setRestwertResponse] = useState(null);
  const [restwertError, setRestwertError] = useState(null);
  const [additionalData, setAdditionalData] = useState({
    mileage: '',
    location_postal_code: formData.adresse2 ? formData.adresse2.match(/\d{5}/)?.[0] || '' : '',
    engine_type: 'Benzin',
    cost_of_repair: '',
    cost_of_replace: '',
    damage_description: ''
  });

  const handleRestwertAbfrage = async () => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    
    // Validierung
    if (!formData.vin && !formData.vin_optional) {
      setRestwertError('Bitte geben Sie die VIN/Fahrgestellnummer ein.');
      return;
    }
    
    if (!formData.marke || !formData.modell) {
      setRestwertError('Bitte geben Sie Marke und Modell ein.');
      return;
    }
    
    if (!additionalData.mileage) {
      setRestwertError('Bitte geben Sie den Kilometerstand ein.');
      return;
    }

    setRestwertLoading(true);
    setRestwertError(null);
    setRestwertResponse(null);

    try {
      // Erstzulassung Jahr extrahieren
      let year_of_construction = new Date().getFullYear();
      if (formData.erstzulassung) {
        const match = formData.erstzulassung.match(/\d{4}/);
        if (match) {
          year_of_construction = parseInt(match[0]);
        }
      }

      const request_data = {
        vin: formData.vin || formData.vin_optional,
        manufacturer: formData.marke,
        model: formData.modell,
        vehicle_type: formData.fahrzeugtyp || 'PKW',
        mileage: parseInt(additionalData.mileage),
        year_of_construction: year_of_construction,
        engine_type: additionalData.engine_type,
        location_postal_code: additionalData.location_postal_code,
        cost_of_repair: additionalData.cost_of_repair ? parseFloat(additionalData.cost_of_repair) : null,
        cost_of_replace: additionalData.cost_of_replace ? parseFloat(additionalData.cost_of_replace) : null,
        damage_description: additionalData.damage_description || null,
        first_registration: formData.erstzulassung || null
      };

      const token = localStorage.getItem('autogutachter24_token');
      
      if (!token) {
        throw new Error('Sitzung abgelaufen. Bitte melden Sie sich erneut an.');
      }
      
      const response = await fetch(`${BACKEND_URL}/api/restwert/submit-vehicle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(request_data)
      });

      const data = await response.json();
      
      if (response.status === 401) {
        localStorage.removeItem('autogutachter24_token');
        throw new Error('Sitzung abgelaufen. Bitte melden Sie sich erneut an.');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Fehler bei der Restwertabfrage');
      }

      setRestwertResponse(data);
      
      // Wenn erfolgreich und Vehicle ID vorhanden, nach 5 Sekunden Angebote abrufen
      if (data.success && data.vehicle_id) {
        setTimeout(async () => {
          try {
            const offersResponse = await fetch(`${BACKEND_URL}/api/restwert/retrieve-offers/${data.vehicle_id}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            const offersData = await offersResponse.json();
            if (offersData.success) {
              setRestwertResponse(offersData);
            }
          } catch (error) {
            console.error('Fehler beim Abrufen der Angebote:', error);
          }
        }, 5000);
      }

    } catch (error) {
      setRestwertError(error.message);
    } finally {
      setRestwertLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="border-l-4 border-blue-500 pl-4 mb-6">
        <h3 className="text-lg font-bold text-gray-900 mb-2">Restwertermittlung via net.casion</h3>
        <p className="text-sm text-gray-600">Fahrzeug zur Restwertermittlung einreichen und Angebote erhalten</p>
      </div>

      {/* Fahrzeugdaten Übersicht */}
      <div className="bg-white rounded-md border border-gray-200">
        <div className="p-6">
          <h4 className="font-semibold text-gray-900 mb-4">Fahrzeugdaten</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-600">VIN</p>
              <p className="font-medium">{formData.vin || formData.vin_optional || '-'}</p>
            </div>
            <div>
              <p className="text-gray-600">Marke</p>
              <p className="font-medium">{formData.marke || '-'}</p>
            </div>
            <div>
              <p className="text-gray-600">Modell</p>
              <p className="font-medium">{formData.modell || '-'}</p>
            </div>
            <div>
              <p className="text-gray-600">Kennzeichen</p>
              <p className="font-medium">{formData.kennzeichen || '-'}</p>
            </div>
            <div>
              <p className="text-gray-600">Erstzulassung</p>
              <p className="font-medium">{formData.erstzulassung || '-'}</p>
            </div>
            <div>
              <p className="text-gray-600">Kraftstoff</p>
              <p className="font-medium">{formData.kraftstoff || '-'}</p>
            </div>
            <div>
              <p className="text-gray-600">Hubraum</p>
              <p className="font-medium">{formData.hubraum ? `${formData.hubraum} cm³` : '-'}</p>
            </div>
            <div>
              <p className="text-gray-600">Leistung</p>
              <p className="font-medium">{formData.leistung ? `${formData.leistung} kW` : '-'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Zusätzliche Daten */}
      <div className="bg-white rounded-md border border-gray-200">
        <div className="p-6">
          <h4 className="font-semibold text-gray-900 mb-4">Zusätzliche Angaben</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kilometerstand <span className="text-red-500">*</span>
              </label>
              <Input
                type="number"
                placeholder="z.B. 85000"
                value={additionalData.mileage}
                onChange={(e) => setAdditionalData({...additionalData, mileage: e.target.value})}
                className="h-10"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Motortyp <span className="text-red-500">*</span>
              </label>
              <select
                value={additionalData.engine_type}
                onChange={(e) => setAdditionalData({...additionalData, engine_type: e.target.value})}
                className="h-10 w-full px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="Benzin">Benzin</option>
                <option value="Diesel">Diesel</option>
                <option value="Elektro">Elektro</option>
                <option value="Hybrid">Hybrid</option>
                <option value="Plugin-Hybrid">Plugin-Hybrid</option>
                <option value="Autogas">Autogas (LPG)</option>
                <option value="Erdgas">Erdgas (CNG)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Standort PLZ <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                placeholder="z.B. 50667"
                value={additionalData.location_postal_code}
                onChange={(e) => setAdditionalData({...additionalData, location_postal_code: e.target.value})}
                className="h-10"
                maxLength={5}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reparaturkosten (€)
              </label>
              <Input
                type="number"
                placeholder="z.B. 5000"
                value={additionalData.cost_of_repair}
                onChange={(e) => setAdditionalData({...additionalData, cost_of_repair: e.target.value})}
                className="h-10"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Wiederbeschaffungswert (€)
              </label>
              <Input
                type="number"
                placeholder="z.B. 15000"
                value={additionalData.cost_of_replace}
                onChange={(e) => setAdditionalData({...additionalData, cost_of_replace: e.target.value})}
                className="h-10"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Schadenbeschreibung
              </label>
              <Textarea
                rows={3}
                placeholder="Beschreiben Sie den Schaden am Fahrzeug..."
                value={additionalData.damage_description}
                onChange={(e) => setAdditionalData({...additionalData, damage_description: e.target.value})}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Fehler Anzeige */}
      {restwertError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800">
            ❌ {restwertError}
          </p>
        </div>
      )}

      {/* Erfolg Anzeige */}
      {restwertResponse && restwertResponse.success && (
        <div className="border-green-500">
          <div className="p-6">
            <div className="flex items-start space-x-3 mb-4">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-green-600">✓</span>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900">Fahrzeug erfolgreich eingereicht</h4>
                <p className="text-sm text-gray-600 mt-1">{restwertResponse.message}</p>
                {restwertResponse.vehicle_id && (
                  <p className="text-xs text-gray-500 mt-2">
                    Fahrzeug-ID: {restwertResponse.vehicle_id}
                  </p>
                )}
              </div>
            </div>

            {/* Angebote */}
            {restwertResponse.offers && restwertResponse.offers.length > 0 && (
              <div className="mt-6">
                <h5 className="font-semibold text-gray-900 mb-3">Eingang Angebote ({restwertResponse.offers.length})</h5>
                <div className="space-y-4">
                  {restwertResponse.offers.map((offer, index) => (
                    <div key={offer.offer_id || index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-lg text-gray-900">
                            {offer.offer_value_eur.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                          </p>
                          {offer.company_name && (
                            <p className="text-sm text-gray-600">{offer.company_name}</p>
                          )}
                          {offer.location && (
                            <p className="text-xs text-gray-500">{offer.location}</p>
                          )}
                          {offer.distance && (
                            <p className="text-xs text-gray-500">Entfernung: {offer.distance} km</p>
                          )}
                        </div>
                        <div className="text-right text-xs text-gray-500">
                          <p>Gültig bis:</p>
                          <p>{new Date(offer.valid_until).toLocaleDateString('de-DE')}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(!restwertResponse.offers || restwertResponse.offers.length === 0) && (
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  ℹ️ Noch keine Angebote vorhanden. Angebote werden in Kürze eingehen.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Config Warning */}
      {restwertResponse && !restwertResponse.success && restwertResponse.error_code === 'CONFIG_MISSING' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            {restwertResponse.message}
          </p>
        </div>
      )}

      {/* Abfrage Button */}
      <div className="flex justify-end space-x-3">
        <Button
          type="button"
          onClick={handleRestwertAbfrage}
          disabled={restwertLoading || !formData.vin && !formData.vin_optional || !formData.marke || !formData.modell}
          className="bg-blue-600 hover:bg-blue-700 text-white px-8"
        >
          {restwertLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              Anfrage wird gesendet...
            </>
          ) : (
            <>
              <TrendingDown className="w-4 h-4 mr-2" />
              Restwert abfragen
            </>
          )}
        </Button>
      </div>

      {/* Hinweis */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <p className="text-xs text-gray-600">
          <strong>Hinweis:</strong> Die Restwertabfrage erfolgt über die net.casion Plattform. 
          Nach Einreichung des Fahrzeugs werden Sie Angebote von verschiedenen Restwert-Aufkäufern erhalten. 
          Die Angebote sind unverbindlich.
        </p>
      </div>
    </div>
  );
};

const NeueAkte = () => {
  const navigate = useNavigate();
  const { akte_id } = useParams(); // Hole akte_id aus URL wenn vorhanden
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('akte');
  const [akteId, setAkteId] = useState(akte_id || null);
  const [aktenzeichen, setAktenzeichen] = useState('');
  const [autoSaving, setAutoSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(!!akte_id); // true wenn akte_id vorhanden
  
  // Kalkulation States
  const [kalkulationStatus, setKalkulationStatus] = useState(null);
  const [kalkulationData, setKalkulationData] = useState(null);
  const [submittingKalkulation, setSubmittingKalkulation] = useState(false);
  const [kalkulationsModus, setKalkulationsModus] = useState(null); // 'wir_kalkulieren' oder 'selbst_kalkulieren'
  const [datClaimId, setDatClaimId] = useState(null);
  const [datKalkulationsDaten, setDatKalkulationsDaten] = useState(null);
  
  // Adresssuche States
  const [adressSuggestions, setAdressSuggestions] = useState([]);
  const [showAdressSuggestions, setShowAdressSuggestions] = useState(false);
  const [adressLoading, setAdressLoading] = useState(false);
  const [selectedFieldForAddress, setSelectedFieldForAddress] = useState(null); // 'adresse1' oder 'adresse2'
  
  const [formData, setFormData] = useState({
    // Kundendaten
    kunde: '',
    fahrzeugtyp: 'PKW',
    adresse1: '',
    adresse2: '',
    kunde_telefon: '',
    kunde_email: '',
    
    // Schadendaten
    schadentag: '',
    anfrageart: '2 - Kasko',
    schadenort: '',
    schadennummer: '',
    
    // Fahrzeugdaten
    kennzeichen: '',
    vers_nummer: '',
    selbstbeteiligung: '150',
    marke: '',
    hersteller: '',
    modell: '',
    vin: '',
    vin_optional: '',
    erstzulassung: '',
    hsn: '',
    tsn: '',
    hu_datum: '',
    fahrzeugklasse: '',
    fahrzeugart: '',
    laufleistung: '',
    kraftstoff: '',
    hubraum: '',
    leistung: '',
    
    // Auftragsdaten
    welche_scheibe: 'Frontscheibe',
    auftragstyp: 'Gutachten',
    vorsteuerabzugsberechtigt: 'Nein',
    
    // GDV Abfrage
    gdv_abfrageart: 'haftpflicht',
    
    // Versicherungslogo
    versicherung_logo: null,
    
    // Unterschrift-Seite Felder
    versicherungsname: '',
    versicherungsscheinnummer: '',
    mobilnummer: '',
    // Auftraggeber
    auftraggeber_name: '',
    auftraggeber_vorname: '',
    auftraggeber_strasse: '',
    auftraggeber_ort: '',
    auftraggeber_kennzeichen: '',
    // Gegner
    gegner_vorname: '',
    gegner_strasse: '',
    gegner_ort: '',
    gegner_kennzeichen: '',
    gegner_telefon: '',
    gegner_email: '',
    gegner_fahrzeug_marke: '',
    gegner_fahrzeug_modell: '',
    // Sachverständiger
    sv_firmenname: '',
    sv_name: '',
    sv_vorname: '',
    sv_strasse: '',
    sv_ort: '',
    // Besichtigungsbedingungen
    besichtigungsbedingungen: '',
    dokumentationstext: '',
    // Besichtigungszustand
    besichtigungszustand: '',
    zustandstext: '',
    // Identifizierung
    identifizierung: '',
    identifizierungstext: '',
    // Scheckheftgepflegt
    scheckheftgepflegt: '',
    scheckhefttext: '',
    // Probelauf Antrieb
    probelauf_antrieb: '',
    probelauftext: '',
    // Probefahrt
    probefahrt: '',
    probefahrttext: '',
    // Fehlerspeicher ausgelesen
    fehlerspeicher: '',
    fehlerspeichertext: '',
    // Allgemeinzustand
    allgemeinzustand: '',
    allgemeinzustandtext: '',
    // Karosseriezustand
    karosseriezustand: '',
    karosseriezustandtext: '',
    // Lackzustand
    lackzustand: '',
    lackzustandtext: '',
    // Innenraumzustand
    innenraumzustand: '',
    innenraumzustandtext: '',
    // Schadenhergang
    schadenhergang: '',
    schadenhergangtext: '',
    // Unfallprotokoll
    unfallprotokoll: '',
    unfallprotokolltext: '',
    // Plausibilität
    plausibilitaet: '',
    plausibilitaettext: '',
    // Kompatibilität
    kompatibilitaet: '',
    kompatibilitaettext: ''
  });

  // Setze Auftragstyp aus URL-Parameter
  useEffect(() => {
    const typParam = searchParams.get('typ');
    if (typParam) {
      setFormData(prev => ({
        ...prev,
        auftragstyp: typParam
      }));
    }
  }, [searchParams]);

  // Laden oder Erstellen der Akte
  useEffect(() => {
    const initAkte = async () => {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      // FALL 1: Existierende Akte laden (wenn akte_id vorhanden)
      if (akte_id) {
        try {
          const response = await fetch(`${BACKEND_URL}/api/akten/details/${akte_id}`);
          
          if (response.ok) {
            const data = await response.json();
            
            // Lade alle Daten in formData
            if (data.akte && data.akte.formData) {
              setFormData({
                ...data.akte.formData,
                id: akte_id  // Setze die ID für Kalkulation-Einreichung
              });
              setAktenzeichen(data.akte.aktenzeichen || '');
              setLichtbilder(data.akte.lichtbilder || []);
              
              toast.success('Akte geladen', {
                description: `Aktenzeichen: ${data.akte.aktenzeichen}`
              });
            }
          } else {
            toast.error('Akte nicht gefunden');
            navigate('/akten');
          }
        } catch (error) {
          console.error('Fehler beim Laden der Akte:', error);
          toast.error('Fehler beim Laden der Akte');
          navigate('/akten');
        }
      } 
      // FALL 2: Neue Akte - WARTEN bis Fahrzeugschein gescannt wird
      else {
        // Zeige Info-Toast
        toast.info('📄 Scannen Sie einen Fahrzeugschein', {
          description: 'Die Akte wird automatisch erstellt, sobald Sie einen Fahrzeugschein scannen',
          duration: 5000
        });
      }
    };
    
    initAkte();
  }, [akte_id]); // Abhängig von akte_id

  // Extrahiere Initialen aus Kundenname
  const extrahiereInitialen = (name) => {
    if (!name || name.trim().length === 0) {
      return 'XX'; // Default wenn kein Name
    }
    
    const teile = name.trim().split(/\s+/); // Split bei Leerzeichen
    
    if (teile.length === 1) {
      // Nur ein Name (z.B. "Max")
      return teile[0].substring(0, 2).toUpperCase();
    } else {
      // Vor- und Nachname (z.B. "Max Mustermann")
      const vorname = teile[0];
      const nachname = teile[teile.length - 1]; // Letzter Teil ist Nachname
      return (vorname.charAt(0) + nachname.charAt(0)).toUpperCase();
    }
  };

  // Lade Kalkulations-Status wenn Sektion aktiv ist
  useEffect(() => {
    const loadKalkulationStatus = async () => {
      if (!formData.id) return;
      try {
        const token = localStorage.getItem('autogutachter24_token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/kalkulation/status/${formData.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setKalkulationStatus(data.status);
          setKalkulationData(data.kalkulation);
        }
      } catch (error) {
        console.error('Fehler beim Laden des Kalkulations-Status:', error);
      }
    };
    if (activeSection === 'kalkulation') {
      loadKalkulationStatus();
    }
  }, [formData.id, activeSection]);

  // Erstelle Akte automatisch beim ersten Input
  const erstelleAkteAutomatisch = async (initialData = {}) => {
    if (akteId || isEditMode) return; // Nicht wenn bereits existiert
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Extrahiere Kundenname
      const kundenname = initialData.kunde || formData.kunde || '';
      const initialen = extrahiereInitialen(kundenname);
      
      // Generiere Basis-Aktenzeichen mit Initialen
      const heute = new Date();
      const tag = String(heute.getDate()).padStart(2, '0');
      const monat = String(heute.getMonth() + 1).padStart(2, '0');
      const jahr = heute.getFullYear().toString().slice(-2);
      const basisAktenzeichen = `KH-${initialen} ${tag}${monat}${jahr}`;
      
      // Prüfe ob bereits existiert und finde nächste freie Nummer
      const checkResponse = await fetch(`${BACKEND_URL}/api/akten/check-aktenzeichen?basis=${basisAktenzeichen}`);
      const checkData = await checkResponse.json();
      
      let neuesAktenzeichen;
      if (checkData.exists) {
        // Duplikat gefunden, füge Nummer mit Schrägstrich hinzu
        neuesAktenzeichen = `${basisAktenzeichen}/${String(checkData.nextNumber).padStart(2, '0')}`;
      } else {
        // Erste Akte, keine Nummer
        neuesAktenzeichen = basisAktenzeichen;
      }
      
      setAktenzeichen(neuesAktenzeichen);
      
      // Erstelle Akte in DB
      const response = await fetch(`${BACKEND_URL}/api/akten/speichern`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          aktenzeichen: neuesAktenzeichen,
          gutachtentyp: 'Haftpflichtgutachten',
          arbeitsschritt: 'ERSTELT',
          status: 'OFFEN',
          ...initialData
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setAkteId(data.akte_id);
        setFormData(prev => ({
          ...prev,
          id: data.akte_id,
          aktenzeichen: neuesAktenzeichen
        }));
        console.log('✅ Akte automatisch erstellt:', data.akte_id);
      }
    } catch (error) {
      console.error('Fehler beim automatischen Erstellen der Akte:', error);
    }
  };

  // Auto-Save bei Änderungen (speichert alle Daten)
  const autoSaveAkte = async (partialUpdate = {}) => {
    if (!akteId) return;
    
    try {
      setAutoSaving(true);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Sammle ALLE aktuellen Daten
      // WICHTIG: partialUpdate kommt NACH lichtbilder, damit neue Lichtbilder nicht überschrieben werden
      const completeData = {
        ...formData,
        lichtbilder: partialUpdate.lichtbilder || lichtbilder,
        wiederbeschaffungswert: wiederbeschaffungswertErgebnis,
        bewertung: bewertungData,
        ...partialUpdate
      };
      
      await fetch(`${BACKEND_URL}/api/akten/aktualisieren/${akteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(completeData)
      });
      
      setAutoSaving(false);
    } catch (error) {
      console.error('Auto-Save Fehler:', error);
      setAutoSaving(false);
    }
  };

  const handleChange = async (e) => {
    const updatedFormData = {
      ...formData,
      [e.target.name]: e.target.value
    };
    setFormData(updatedFormData);
    
    // Wenn noch keine Akte existiert, erstelle sie JETZT
    if (!akteId) {
      await erstelleAkteAutomatisch(updatedFormData);
    }
    // Auto-Save wird automatisch durch den zentralen useEffect getriggert (2 Sekunden Debounce)
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Finale Speicherung und Navigation zur Akten-Liste
    try {
      await autoSaveAkte({
        ...formData,
        lichtbilder,
        wiederbeschaffungswert: wiederbeschaffungswertErgebnis
      });
      
      toast.success('✅ Akte abgeschlossen!', {
        description: `Aktenzeichen: ${aktenzeichen}`
      });
      
      // Navigate zur Akten-Übersicht
      setTimeout(() => {
        navigate('/akten');
      }, 1000);
    } catch (err) {
      console.error('Failed to finalize akte:', err);
      toast.error('Fehler beim Abschließen der Akte');
    }
  };

  const [signatureData, setSignatureData] = useState({
    schadenbeschreibung: `Schadens- und Abtretungserklärung

Am Fahrzeug des Kunden (Name des Geschädigten), amtliches Kennzeichen (Kennzeichen eintragen), Fahrzeugtyp (Fahrzeugmarke und Modell), ist am ${new Date().toLocaleDateString('de-DE')} während der Fahrt auf der (Straße/Ort des Schadens) ein Schaden entstanden.

Abtretung / Auftrag

Im Zusammenhang mit dem oben beschriebenen Schadenereignis beauftrage ich das genannte Kfz-Sachverständigenbüro mit der Erstellung eines Gutachtens zur Feststellung der Schadenhöhe. Die Vergütung des Sachverständigen richtet sich nach der ermittelten Schadenhöhe gemäß der Honorartabelle des Sachverständigenbüros zuzüglich der erforderlichen Nebenkosten.

Ich trete hiermit meinen Anspruch auf Erstattung der Sachverständigenkosten – in Höhe des Bruttorechnungsbetrags bzw. in Höhe des Nettobetrags, sofern ich zum Vorsteuerabzug berechtigt bin – unwiderruflich, erstrangig und erfüllungshalber an das beauftragte Kfz-Sachverständigenbüro ab. Die Abtretung richtet sich gegen den Fahrer, den Halter sowie gegen den Versicherer des unfallbeteiligten Fahrzeugs.

Ich weise den regulierungspflichtigen Versicherer ausdrücklich an, die entstehenden Sachverständigenkosten direkt an das von mir beauftragte Sachverständigenbüro auszuzahlen.

Das Kfz-Sachverständigenbüro ist berechtigt, diese Abtretung gegenüber allen Anspruchsgegnern offenzulegen und den abgetretenen Anspruch im eigenen Namen geltend zu machen. Die Abtretung berührt meine Verpflichtungen aus dem Sachverständigenvertrag nicht. Sollte der regulierungspflichtige Versicherer keine oder nur eine teilweise Zahlung leisten, bleibt das Sachverständigenbüro berechtigt, die offenen Forderungen gegenüber mir geltend zu machen.

Eine Inanspruchnahme meiner Person erfolgt dabei selbstverständlich ausschließlich Zug um Zug gegen Rückabtretung der noch offenen Forderung.

Die Frontscheibe weist einen (Steinschlag / Riss / Bruch) auf. Dieser Schaden beeinträchtigt die Sicht des Fahrers, die Stabilität der Scheibe sowie die Verkehrssicherheit des Fahrzeugs. Eine Reparatur bzw. ein Austausch der Scheibe ist zwingend erforderlich.

Der Kunde bestätigt hiermit, dass der oben beschriebene Schaden durch äußere Einwirkung (z. B. Steinschlag) während der Fahrt entstanden ist.

Abtretungserklärung

Der Unterzeichner tritt seine Ansprüche aus dem Schadenereignis gegenüber der zuständigen Versicherungsgesellschaft (Versicherungsname) unwiderruflich an die Werkstatt AutoGlas-Austausch-Musterfirma GmbH ab.

Die Werkstatt ist hiermit berechtigt, die Reparatur- bzw. Austauschkosten direkt mit der Versicherung abzurechnen.

Dem Kunden entstehen aus dieser Abtretung keine zusätzlichen Kosten, soweit die Versicherung die Regulierung übernimmt.

Besteht jedoch in der Kaskoversicherung des Kunden eine Selbstbeteiligung, verpflichtet sich der Kunde, diesen Betrag nach Rechnungsstellung direkt an die Werkstatt zu zahlen.`,
    kaskoversicherung: true,
    haftpflicht: false
  });

  const [signatureImage, setSignatureImage] = useState(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const canvasRef = React.useRef(null);
  const [isDrawing, setIsDrawing] = React.useState(false);
  const autoSaveTimerRef = React.useRef(null);

  // Canvas Signature Pad Functions - FIX: Scroll-Position und Skalierung berücksichtigen
  const getCanvasCoordinates = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
    const clientY = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : 0);
    
    // Berechne relative Position innerhalb des Canvas
    const relativeX = clientX - rect.left;
    const relativeY = clientY - rect.top;
    
    // Berücksichtige Skalierung (Canvas internal size vs display size)
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = relativeX * scaleX;
    const y = relativeY * scaleY;
    
    return { x, y };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const { x, y } = getCanvasCoordinates(e, canvas);
    
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const { x, y } = getCanvasCoordinates(e, canvas);
    
    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    // Trigger Auto-Save nach dem Zeichnen
    triggerAutoSave();
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Lösche auch die gespeicherte Unterschrift und den Auto-Save-Timer
    setSignatureImage(null);
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
  };

  const saveSignature = (autoSave = false) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Verwende JPEG mit 0.8 Qualität für kleinere Dateigröße und schnelleres Speichern
    const dataURL = canvas.toDataURL('image/jpeg', 0.8);
    setSignatureImage(dataURL);
    
    // Speichere auch in localStorage für sofortigen Zugriff
    try {
      localStorage.setItem(`signature_${akteId}`, dataURL);
    } catch (e) {
      console.warn('localStorage voll, konnte Unterschrift nicht zwischenspeichern');
    }
    
    if (!autoSave) {
      setShowSignaturePad(false);
      toast.success('Unterschrift gespeichert', {
        description: 'Die Unterschrift wurde erfolgreich gespeichert'
      });
    }
  };
  
  // Auto-Save-Funktion: Speichert automatisch 500ms nach dem letzten Strich (schneller)
  const triggerAutoSave = () => {
    // Lösche den vorherigen Timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // Setze einen neuen Timer für 500ms (0.5 Sekunden) - 4x schneller!
    autoSaveTimerRef.current = setTimeout(() => {
      saveSignature(true); // Auto-Save mit true Flag
    }, 500);
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setSignatureImage(reader.result);
      toast.success('Unterschrift hinzugefügt', {
        description: 'Die Unterschrift wurde erfolgreich hochgeladen'
      });
    };
    reader.readAsDataURL(file);
  };

  const handleClearSignature = () => {
    setSignatureImage(null);
    toast.success('Unterschrift entfernt', {
        description: 'Die Unterschrift wurde gelöscht'
      });
  };

  const handleGenerateVorlage = () => {
    // Vorlage mit aktuellen Daten generieren
    const updatedText = `Schadens- und Abtretungserklärung

Am Fahrzeug des Kunden ${formData.kunde || '(Name des Geschädigten)'}, amtliches Kennzeichen ${formData.kennzeichen || '(Kennzeichen eintragen)'}, Fahrzeugtyp ${formData.marke || ''} ${formData.modell || '(Fahrzeugmarke und Modell)'}, ist am ${formData.schadentag ? new Date(formData.schadentag).toLocaleDateString('de-DE') : new Date().toLocaleDateString('de-DE')} während der Fahrt auf der ${formData.schadenort || '(Straße/Ort des Schadens)'} ein Schaden entstanden.

Die ${formData.welche_scheibe || 'Frontscheibe'} weist einen Schaden auf. Dieser Schaden beeinträchtigt die Sicht des Fahrers, die Stabilität der Scheibe sowie die Verkehrssicherheit des Fahrzeugs. Eine Reparatur bzw. ein Austausch der Scheibe ist zwingend erforderlich.

Der Kunde bestätigt hiermit, dass der oben beschriebene Schaden durch äußere Einwirkung (z. B. Steinschlag) während der Fahrt entstanden ist.

Abtretungserklärung

Der Unterzeichner tritt seine Ansprüche aus dem Schadenereignis gegenüber der zuständigen Versicherungsgesellschaft unwiderruflich an die Werkstatt AutoGlas-Austausch-Musterfirma GmbH ab.

Die Werkstatt ist hiermit berechtigt, die Reparatur- bzw. Austauschkosten direkt mit der Versicherung abzurechnen.

Dem Kunden entstehen aus dieser Abtretung keine zusätzlichen Kosten, soweit die Versicherung die Regulierung übernimmt.

Besteht jedoch in der Kaskoversicherung des Kunden eine Selbstbeteiligung, verpflichtet sich der Kunde, diesen Betrag nach Rechnungsstellung direkt an die Werkstatt zu zahlen.`;

    setSignatureData({ ...signatureData, schadenbeschreibung: updatedText });
    
    toast.success('Vorlage generiert', {
        description: 'Die Schadenbeschreibung wurde mit Ihren Daten aktualisiert'
      });
  };

  const [bewertungTab, setBewertungTab] = useState('restwert');
  const [bewertungData, setBewertungData] = useState({
    // Restwert
    restwert_zustand: 'Totalschaden',
    wiederbeschaffungswert: '',
    restwert: '',
    restwert_berechnung: '',
    
    // Minderwert
    minderwert_prozent: '10',
    minderwert_betrag: '',
    minderwert_begruendung: '',
    
    // Beurteilung
    beurteilung_schadenumfang: '',
    beurteilung_reparaturkosten: '',
    beurteilung_unfallrekonstruktion: '',
    beurteilung_haftungsverteilung: '',
    beurteilung_zusammenfassung: ''
  });

  // Hilfsfunktion für Input-Styling (hellblauer Hintergrund #e8f5fe wenn ausgefüllt)
  const getInputClassName = (value, baseClassName = 'border-gray-300') => {
    return `${baseClassName} ${value ? 'bg-filled-field' : ''}`;
  };

  // State für Email
  const [emailData, setEmailData] = useState({
    empfaenger: '',
    cc: '',
    betreff: '',
    nachricht: ''
  });

  // State für Restwertbörse Auto-Polling
  const [restwertAutoPolling, setRestwertAutoPolling] = useState(true);
  const [restwertLastCheck, setRestwertLastCheck] = useState(null);
  const [restwertPollingActive, setRestwertPollingActive] = useState(false);

  // Auto-Polling für Restwertbörse Angebote (alle 5 Minuten)
  useEffect(() => {
    let pollingInterval = null;
    
    const fetchRestwertOffers = async () => {
      // Nur ausführen wenn Fahrzeug eingestellt ist und Auto-Polling aktiviert
      if (!formData.restwert_vehicle_id || !formData.restwert_vehicle_id_extern || !restwertAutoPolling) {
        return;
      }
      
      const token = localStorage.getItem('autogutachter24_token');
      if (!token) return;
      
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      try {
        setRestwertPollingActive(true);
        console.log('🔄 Auto-Polling: Prüfe Restwertbörse auf neue Angebote...');
        
        const response = await fetch(
          `${API_URL}/api/restwert/retrieve-offers/${formData.restwert_vehicle_id}?vehicle_id_extern=${formData.restwert_vehicle_id_extern}`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        
        const result = await response.json();
        setRestwertLastCheck(new Date());
        
        if (result.success && result.offers) {
          const currentCount = formData.restwert_angebote?.length || 0;
          const newCount = result.offers.length;
          
          // Nur aktualisieren wenn sich etwas geändert hat
          if (newCount !== currentCount || JSON.stringify(result.offers) !== JSON.stringify(formData.restwert_angebote)) {
            setFormData(prev => ({
              ...prev,
              restwert_angebote: result.offers
            }));
            
            if (newCount > currentCount && currentCount > 0) {
              toast.success(`🎉 ${newCount - currentCount} neue(s) Angebot(e) eingegangen!`, {
                duration: 10000
              });
            } else if (newCount > 0 && currentCount === 0) {
              toast.success(`🎉 Erstes Angebot eingegangen! Insgesamt ${newCount} Angebot(e).`, {
                duration: 10000
              });
            }
          }
          
          console.log(`✅ Auto-Polling: ${newCount} Angebot(e) gefunden`);
        }
      } catch (error) {
        console.warn('Auto-Polling Fehler:', error.message);
      } finally {
        setRestwertPollingActive(false);
      }
    };
    
    // Sofort beim Mount prüfen wenn Fahrzeug eingestellt
    if (formData.restwert_vehicle_id && restwertAutoPolling) {
      fetchRestwertOffers();
    }
    
    // Interval alle 5 Minuten (300000ms)
    if (formData.restwert_vehicle_id && restwertAutoPolling) {
      pollingInterval = setInterval(fetchRestwertOffers, 300000); // 5 Minuten
    }
    
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [formData.restwert_vehicle_id, formData.restwert_vehicle_id_extern, restwertAutoPolling]);

  // Auto-fill Email-Betreff mit Aktenzeichen
  useEffect(() => {
    if (formData.aktenzeichen && !emailData.betreff) {
      setEmailData(prev => ({
        ...prev,
        betreff: `Gutachten - Aktenzeichen: ${formData.aktenzeichen}`
      }));
    }
  }, [formData.aktenzeichen]);

  // State für Gutachten
  const [gutachtenData, setGutachtenData] = useState({
    // Header
    aktenzeichen: '',
    auftragsdatum: '',
    dokumentdatum: '',
    
    // Zusammenfassung
    reparaturkosten_netto: '',
    reparaturkosten_brutto: '',
    wiederbeschaffungswert: '',
    reparaturdauer: '',
    nutzungsausfall_tag: '',
    mietwagenklasse: '',
    wiederbeschaffungsdauer: '',
    reparaturbeurteilung: 'Reparatur technisch/wirtschaftlich sinnvoll',
    fahrbereitschaft: 'Fahrbereit',
    
    // Fahrzeugbeschreibung
    hsn_tsn: '',
    farbe: '',
    erstzulassung: '',
    baujahr: '',
    laufleistung: '',
    naechste_hu: '',
    motor: '',
    kraftstoff: '',
    leistung: '',
    hubraum: '',
    getriebe: '',
    schadstoffklasse: '',
    
    // Ausstattung
    serienausstattung: '',
    sonderausstattung: '',
    
    // Besichtigung & Zustand
    besichtigung_datum: '',
    besichtigung_adresse: '',
    besichtigt_durch: '',
    zustand_allgemein: '',
    zustand_karosserie: '',
    zustand_lack: '',
    zustand_innenraum: '',
    
    // Schadenhergang
    schadenhergang: '',
    
    // Schadenfeststellung
    schadenbeschreibung: '',
    schadenregionen: [],
    plausibilitaet: '',
    vorschaeden_repariert: '',
    vorschaeden_nicht_repariert: '',
    vorschaeden_unfachgerecht: '',
    
    // Instandsetzung
    reparaturauftrag: '',
    lohnkosten: '',
    lackierkosten: '',
    ersatzteilkosten: '',
    verbringungskosten: '',
    
    // Wertermittlung
    wertverbesserung: '',
    wiederbeschaffungswert_berechnung: '',
    
    // Fahrzeugausfall
    verkehrssicherheit: '',
    reparaturdauer_details: '',
    wiederbeschaffungsdauer_details: '',
    nutzungsausfall_details: '',
    
    // Schlusswort
    schlusswort: ''
  });

  // State für Beteiligte
  const [beteiligte, setBeteiligte] = useState([]);
  const [showBeteiligterForm, setShowBeteiligterForm] = useState(false);
  const [newBeteiligter, setNewBeteiligter] = useState({
    beteiligung: 'Fahrzeughalter',
    kontakttyp: 'Person',
    bezeichnung: '',
    plz: '',
    ort: '',
    email: '',
    telefon: ''
  });

  // State für Besichtigungen
  const [besichtigungen, setBesichtigungen] = useState([]);
  const [showBesichtigungForm, setShowBesichtigungForm] = useState(false);
  const [editingBesichtigung, setEditingBesichtigung] = useState(null);
  const [newBesichtigung, setNewBesichtigung] = useState({
    besichtigungsort: '',
    besichtigungsbedingungen: '', // NEU: Dropdown für Bedingungen
    dokumentationstext: '', // NEU: Automatischer Text
    teilnehmer: '',
    ortFirma: '',
    weitereTeilnehmer: '',
    adresse: '',
    externerSachverstaendiger: '',
    vonDatum: '',
    vonUhrzeit: '',
    bisDatum: '',
    bisUhrzeit: '',
    ersteller: 'Herr Özer Kanatli',
    sachverstaendiger: 'Herr Özer Kanatli',
    teilnehmerBenachrichtigen: false,
    bemerkung: '',
    erinnerungAktivieren: false,
    entfernung: '',
    fahrtzeit: ''
  });
  
  // Textbausteine werden jetzt aus ./NeueAkte/components/index.js importiert
  const [distanceLoading, setDistanceLoading] = useState(false);
  const [mapUrl, setMapUrl] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  
  // Unfallbericht OCR States
  const [unfallberichtLoading, setUnfallberichtLoading] = useState(false);
  const [unfallberichtDragging, setUnfallberichtDragging] = useState(false);
  const [unfallberichtData, setUnfallberichtData] = useState(null);
  
  // DAT API States
  const [datLoading, setDatLoading] = useState(false);
  const [datMyClaimUrl, setDatMyClaimUrl] = useState(null);
  const [showDatIframe, setShowDatIframe] = useState(false);

  // Minderwert States
  const [minderwertData, setMinderwertData] = useState({
    wiederbeschaffungswert: '',
    reparaturkosten: '',
    fahrzeugalter_monate: '',
    laufleistung_km: '',
    neupreis: ''
  });
  const [minderwertErgebnis, setMinderwertErgebnis] = useState({
    ruhkopf_sahm: null,
    halbgewachs: null
  });

  // Nutzungsausfall States
  const [nutzungsausfallData, setNutzungsausfallData] = useState({
    fahrzeugklasse: '',
    ausfalltage: '14', // Standard: 14 Tage
    fahrzeugalter_jahre: ''
  });
  const [nutzungsausfallErgebnis, setNutzungsausfallErgebnis] = useState(null);
  const [mietwagenspiegelLoading, setMietwagenspiegelLoading] = useState(false);

  // Wiederbeschaffungswert State
  const [wiederbeschaffungswertErgebnis, setWiederbeschaffungswertErgebnis] = useState(null);
  const [wiederbeschaffungswertLoading, setWiederbeschaffungswertLoading] = useState(false);

  // Lichtbilder/Schadensfotos State
  const [lichtbilder, setLichtbilder] = useState([]);
  const [lichtbilderUploading, setLichtbilderUploading] = useState(false);
  const [isDraggingLichtbild, setIsDraggingLichtbild] = useState(false);
  const [draggedImageIndex, setDraggedImageIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  
  // Bildbearbeitung State
  const [editingImage, setEditingImage] = useState(null);
  const [editingImageIndex, setEditingImageIndex] = useState(null);

  // Automatische Berechnung des Fahrzeugalters aus Erstzulassung
  useEffect(() => {
    if (formData.erstzulassung) {
      const alter = berechneFahrzeugalter(formData.erstzulassung);
      if (alter !== null) {
        setNutzungsausfallData(prev => ({
          ...prev,
          fahrzeugalter_jahre: alter.toString()
        }));
      }
    }
  }, [formData.erstzulassung]);

  // Automatische Berechnung des Wiederbeschaffungswerts nach Fahrzeugschein-Scan
  useEffect(() => {
    const berechneWiederbeschaffungswert = async () => {
      // Prüfe ob genügend Daten vorhanden sind
      if (!formData.marke || !formData.modell || !formData.erstzulassung) {
        return;
      }

      try {
        console.log('🚗 Berechne Wiederbeschaffungswert...');
        setWiederbeschaffungswertLoading(true);
        
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${BACKEND_URL}/api/wiederbeschaffungswert/berechnen`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            marke: formData.marke,
            modell: formData.modell,
            erstzulassung: formData.erstzulassung,
            kilometerstand: formData.laufleistung ? parseInt(formData.laufleistung) : null,
            kraftstoff: formData.kraftstoff,
            leistung_kw: formData.leistung_kw ? parseInt(formData.leistung_kw) : null,
            getriebe: formData.getriebe
          })
        });

        if (!response.ok) {
          throw new Error('API-Aufruf fehlgeschlagen');
        }

        const data = await response.json();
        console.log('✅ Wiederbeschaffungswert erhalten:', data);

        // Speichere das vollständige Ergebnis
        setWiederbeschaffungswertErgebnis(data);

        // Setze den Wert im Bewertungs-Tab
        setBewertungData(prev => ({
          ...prev,
          wiederbeschaffungswert: Math.round(data.durchschnittspreis).toString()
        }));

        // Zeige Erfolgs-Toast
        toast.success('Wiederbeschaffungswert ermittelt', {
          description: `€${data.durchschnittspreis.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})} (Ø aus ${data.quellen.length} Quellen)`
        });

      } catch (error) {
        console.error('❌ Fehler bei Wiederbeschaffungswert-Berechnung:', error);
        setWiederbeschaffungswertErgebnis(null);
        toast.error('Wiederbeschaffungswert konnte nicht berechnet werden', {
          description: 'Bitte versuchen Sie es später erneut.'
        });
      } finally {
        setWiederbeschaffungswertLoading(false);
      }
    };

    // Verzögere die Berechnung um 1 Sekunde nach Datenänderung
    const timer = setTimeout(() => {
      berechneWiederbeschaffungswert();
    }, 1000);

    return () => clearTimeout(timer);
  }, [formData.marke, formData.modell, formData.erstzulassung, formData.laufleistung]);

  // Automatische Nutzungsausfall-Berechnung wenn alle Daten vorhanden sind
  useEffect(() => {
    const { fahrzeugklasse, ausfalltage, fahrzeugalter_jahre } = nutzungsausfallData;
    
    // Prüfe ob alle notwendigen Daten vorhanden sind
    if (fahrzeugklasse && ausfalltage && fahrzeugalter_jahre) {
      // Verzögere die Berechnung um sicherzustellen, dass alle State-Updates abgeschlossen sind
      const timer = setTimeout(() => {
        berechneNutzungsausfall();
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [nutzungsausfallData.fahrzeugklasse, nutzungsausfallData.ausfalltage, nutzungsausfallData.fahrzeugalter_jahre]);

  // Lichtbilder Upload Handler
  const handleLichtbilderUpload = async (files) => {
    try {
      setLichtbilderUploading(true);
      
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const formData = new FormData();
      
      files.forEach(file => {
        formData.append('files', file);
      });
      
      const response = await fetch(`${BACKEND_URL}/api/lichtbilder/upload`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) throw new Error('Upload fehlgeschlagen');
      
      const data = await response.json();
      
      // Füge neue Bilder hinzu
      const neueLichtbilder = [...lichtbilder, ...data.files];
      setLichtbilder(neueLichtbilder);
      
      // Auto-Save mit neuen Bildern
      if (akteId) {
        await autoSaveAkte({ lichtbilder: neueLichtbilder });
      }
      
      toast.success('Bilder hochgeladen!', {
        description: `${data.files.length} ${data.files.length === 1 ? 'Bild' : 'Bilder'} erfolgreich hochgeladen`
      });
      
    } catch (error) {
      console.error('Lichtbilder-Upload Fehler:', error);
      toast.error('Upload fehlgeschlagen', {
        description: 'Bitte versuchen Sie es erneut'
      });
    } finally {
      setLichtbilderUploading(false);
    }
  };

  // KI-Sortierung der Bilder
  const [kiSortierungLoading, setKiSortierungLoading] = useState(false);
  const [bildAnalyseErgebnisse, setBildAnalyseErgebnisse] = useState({});
  
  const handleKiSortierung = async () => {
    try {
      setKiSortierungLoading(true);
      
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const bilderZumAnalysieren = lichtbilder.filter(f => f.type === 'image');
      
      if (bilderZumAnalysieren.length === 0) {
        toast.error('Keine Bilder zum Sortieren vorhanden');
        return;
      }
      
      toast.loading('🤖 KI analysiert Bilder...', { id: 'ai-analysis' });
      
      const analyseResponse = await fetch(`${BACKEND_URL}/api/bild-analyse/analysieren`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_urls: bilderZumAnalysieren.map(b => b.url)
        })
      });
      
      if (!analyseResponse.ok) {
        throw new Error('Bildanalyse fehlgeschlagen');
      }
      
      const analyseData = await analyseResponse.json();
      
      // Erstelle Mapping von URL zu Analyse-Ergebnis
      const analyseMap = {};
      analyseData.results.forEach(r => {
        analyseMap[r.url] = r;
      });
      
      // Speichere Analyse-Ergebnisse im State
      setBildAnalyseErgebnisse(analyseMap);
      
      // Sortiere alle Lichtbilder
      const sortiert = [...lichtbilder].sort((a, b) => {
        const aInfo = analyseMap[a.url];
        const bInfo = analyseMap[b.url];
        
        // Nicht-Bilder ans Ende
        if (a.type !== 'image') return 1;
        if (b.type !== 'image') return -1;
        
        // Nach Priorität sortieren
        const aPrio = aInfo?.sortier_prioritaet || 1000;
        const bPrio = bInfo?.sortier_prioritaet || 1000;
        
        return aPrio - bPrio;
      });
      
      setLichtbilder(sortiert);
      
      // Auto-Save nach KI-Sortierung
      if (akteId) {
        await autoSaveAkte({ lichtbilder: sortiert });
      }
      
      toast.success('✅ Bilder sortiert!', { 
        id: 'ai-analysis',
        description: `${bilderZumAnalysieren.length} Bilder nach Perspektive sortiert`
      });
      
    } catch (error) {
      console.error('KI-Sortierung Fehler:', error);
      toast.error('Sortierung fehlgeschlagen', {
        id: 'ai-analysis',
        description: 'Bitte versuchen Sie es erneut'
      });
    } finally {
      setKiSortierungLoading(false);
    }
  };

  // Formatiere Perspektive für Anzeige
  const formatPerspektive = (perspektive, schadenstyp) => {
    const perspektivenMap = {
      'vorne': 'Vorne',
      'hinten': 'Hinten',
      'links': 'Links',
      'rechts': 'Rechts',
      'innen': 'Innen',
      'motor': 'Motor',
      'kofferraum': 'Kofferraum',
      'dach': 'Dach',
      'detail': 'Detail',
      'sonstiges': 'Sonstiges'
    };
    
    const schadenstypMap = {
      'lackschaden': 'Lackschaden',
      'delle': 'Delle',
      'kratzer': 'Kratzer',
      'glasschaden': 'Glasschaden',
      'stossfänger': 'Stoßfänger',
      'felge': 'Felge',
      'reifen': 'Reifen',
      'spiegel': 'Spiegel',
      'tür': 'Tür',
      'haube': 'Haube',
      'schweller': 'Schweller',
      'komplettschaden': 'Komplettschaden',
      'sonstiges': 'Sonstiges'
    };
    
    const perspektiveText = perspektivenMap[perspektive?.toLowerCase()] || perspektive;
    const schadenstypText = schadenstypMap[schadenstyp?.toLowerCase()] || schadenstyp;
    
    if (perspektive && schadenstyp && perspektive !== 'sonstiges' && schadenstyp !== 'sonstiges') {
      return `${perspektiveText} (${schadenstypText})`;
    } else if (perspektive && perspektive !== 'sonstiges') {
      return perspektiveText;
    } else if (schadenstyp && schadenstyp !== 'sonstiges') {
      return schadenstypText;
    }
    return null;
  };

  // Drag & Drop Handler für Bilder-Sortierung
  const handleImageDragStart = (e, index) => {
    setDraggedImageIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleImageDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleImageDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedImageIndex === null || draggedImageIndex === dropIndex) {
      setDraggedImageIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Bilder neu anordnen
    const newBilder = [...lichtbilder];
    const draggedItem = newBilder[draggedImageIndex];
    
    // Entferne das gezogene Element
    newBilder.splice(draggedImageIndex, 1);
    
    // Füge es an der neuen Position ein
    newBilder.splice(dropIndex, 0, draggedItem);
    
    setLichtbilder(newBilder);
    setDraggedImageIndex(null);
    setDragOverIndex(null);
    
    // Auto-Save nach Neuordnung
    if (akteId) {
      autoSaveAkte({ lichtbilder: newBilder });
    }
    
    toast.success('Reihenfolge geändert', {
      description: 'Die Bilder wurden neu sortiert'
    });
  };

  const handleImageDragEnd = () => {
    setDraggedImageIndex(null);
    setDragOverIndex(null);
  };

  // Bildbearbeitung öffnen
  const openImageEditor = (datei, index) => {
    // Nur für Bilder, nicht für PDFs/Videos
    if (datei.type !== 'image') {
      toast.error('Bearbeitung nur für Bilder verfügbar');
      return;
    }
    
    setEditingImage(datei);
    setEditingImageIndex(index);
  };

  // Bildbearbeitung schließen
  const closeImageEditor = () => {
    setEditingImage(null);
    setEditingImageIndex(null);
  };

  // Zum nächsten Bild
  const goToNextImage = () => {
    if (editingImageIndex === null) return;
    
    // Finde nächstes Bild (nur type === 'image')
    for (let i = editingImageIndex + 1; i < lichtbilder.length; i++) {
      if (lichtbilder[i].type === 'image') {
        setEditingImage(lichtbilder[i]);
        setEditingImageIndex(i);
        return;
      }
    }
    
    toast.info('Kein weiteres Bild vorhanden');
  };

  // Zum vorherigen Bild
  const goToPreviousImage = () => {
    if (editingImageIndex === null) return;
    
    // Finde vorheriges Bild (nur type === 'image')
    for (let i = editingImageIndex - 1; i >= 0; i--) {
      if (lichtbilder[i].type === 'image') {
        setEditingImage(lichtbilder[i]);
        setEditingImageIndex(i);
        return;
      }
    }
    
    toast.info('Kein vorheriges Bild vorhanden');
  };

  // Funktion zur Berechnung des Fahrzeugalters in Jahren
  const berechneFahrzeugalter = (erstzulassungDatum) => {
    if (!erstzulassungDatum) return null;
    
    try {
      // Parse verschiedene Datumsformate: DD.MM.YYYY, DD-MM-YYYY, DD/MM/YYYY
      let tag, monat, jahr;
      
      if (erstzulassungDatum.includes('.')) {
        [tag, monat, jahr] = erstzulassungDatum.split('.');
      } else if (erstzulassungDatum.includes('-')) {
        const parts = erstzulassungDatum.split('-');
        // Check if it's DD-MM-YYYY or YYYY-MM-DD
        if (parts[0].length === 4) {
          [jahr, monat, tag] = parts;
        } else {
          [tag, monat, jahr] = parts;
        }
      } else if (erstzulassungDatum.includes('/')) {
        const parts = erstzulassungDatum.split('/');
        // Check if it's DD/MM/YYYY or YYYY/MM/DD
        if (parts[0].length === 4) {
          [jahr, monat, tag] = parts;
        } else {
          [tag, monat, jahr] = parts;
        }
      } else {
        return null;
      }
      
      // Erstelle Datumsobjekte
      const erstzulassung = new Date(parseInt(jahr), parseInt(monat) - 1, parseInt(tag));
      const heute = new Date();
      
      // Berechne die Differenz in Jahren
      let alter = heute.getFullYear() - erstzulassung.getFullYear();
      
      // Korrigiere, falls der Geburtstag in diesem Jahr noch nicht war
      const monatsUnterschied = heute.getMonth() - erstzulassung.getMonth();
      if (monatsUnterschied < 0 || (monatsUnterschied === 0 && heute.getDate() < erstzulassung.getDate())) {
        alter--;
      }
      
      return alter;
    } catch (error) {
      console.error('Fehler bei Fahrzeugalter-Berechnung:', error);
      return null;
    }
  };

  // Automatische Entfernungsberechnung beim Wechsel zu Besichtigungen
  useEffect(() => {
    if (activeSection === 'besichtigungen') {
      calculateDistance();
    }
  }, [activeSection]);

  // Funktion zur Entfernungsberechnung
  const calculateDistance = async () => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    
    try {
      // Büro-Adresse aus localStorage holen
      const storedUser = localStorage.getItem('autogutachter24_user');
      if (!storedUser) {
        console.log('Kein Benutzer gefunden');
        return;
      }

      const userData = JSON.parse(storedUser);
      const bueroAdresse = userData.buero_adresse;

      if (!bueroAdresse) {
        toast.success('Hinweis', {
        description: 'Bitte hinterlegen Sie Ihre Büro-Adresse in den Einstellungen für die automatische Entfernungsberechnung.'
      });
        return;
      }

      // Kunden-Adresse aus formData
      const kundenAdresse = `${formData.adresse1 || ''} ${formData.adresse2 || ''}`.trim();

      if (!kundenAdresse) {
        console.log('Keine Kunden-Adresse vorhanden');
        return;
      }

      setDistanceLoading(true);

      const response = await fetch(`${BACKEND_URL}/api/distance/calculate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          origin: bueroAdresse,
          destination: kundenAdresse
        })
      });

      if (response.ok) {
        const data = await response.json();
        setNewBesichtigung(prev => ({
          ...prev,
          entfernung: data.distance_text,
          fahrtzeit: data.duration_text
        }));
        
        // Google Maps URL erstellen
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
        const mapUrlGenerated = `https://www.google.com/maps/embed/v1/directions?key=${apiKey}&origin=${encodeURIComponent(bueroAdresse)}&destination=${encodeURIComponent(kundenAdresse)}&mode=driving&language=de`;
        setMapUrl(mapUrlGenerated);
        
        toast.success('Entfernung berechnet', {
        description: `${data.distance_text} (ca. ${data.duration_text} Fahrzeit)`
      });
      } else {
        const error = await response.json();
        console.error('Fehler bei Entfernungsberechnung:', error);
        toast.success('Hinweis', {
        description: 'Entfernung konnte nicht berechnet werden. Bitte prüfen Sie die Adressen.'
      });
      }
    } catch (error) {
      console.error('Fehler bei Entfernungsberechnung:', error);
    } finally {
      setDistanceLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Automatische Firmen-Erkennung für Vorsteuerabzugsberechtigt
    let updatedFormData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    };
    
    // Wenn das Kundenname-Feld geändert wird, prüfe auf Firmenendungen
    if (name === 'kunde' && value) {
      const firmenEndungen = [
        'gmbh', 'ug', 'ag', 'kg', 'ohg', 'gbr', 'eg', 'ev', 'e.v.',
        'gmbh & co. kg', 'gmbh & co kg', 'co. kg', 'co kg',
        'limited', 'ltd', 'inc', 'corp', 'corporation',
        'gesellschaft', 'unternehmen', 'betrieb', 'firma'
      ];
      
      const nameNormalized = value.toLowerCase().replace(/[.\s]/g, '');
      const istFirma = firmenEndungen.some(endung => 
        nameNormalized.includes(endung.replace(/[.\s]/g, ''))
      );
      
      // Setze Vorsteuerabzugsberechtigt automatisch (nur wenn es sich ändert)
      const neuerWert = istFirma ? 'Ja' : 'Nein';
      if (updatedFormData.vorsteuerabzugsberechtigt !== neuerWert) {
        updatedFormData.vorsteuerabzugsberechtigt = neuerWert;
      }
    }
    
    setFormData(updatedFormData);
    
    // Liste aller Adressfelder für Auto-Complete
    const adressFields = [
      'adresse1', 'adresse2', 
      'gegner_strasse', 'gegner_ort',
      'auftraggeber_strasse', 'auftraggeber_ort',
      'sv_strasse', 'sv_ort',
      'schadenort'
    ];
    
    // Adresssuche bei Eingabe triggern für alle Adressfelder
    if (adressFields.includes(name)) {
      setSelectedFieldForAddress(name);
      if (value && value.length >= 3) {
        debouncedAdressSearch(value, name);
      } else {
        setShowAdressSuggestions(false);
        setAdressSuggestions([]);
      }
    }
  };
  
  // Debounced Adresssuche
  const debounceTimer = useRef(null);
  const debouncedAdressSearch = (query, fieldName) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    setAdressLoading(true);
    debounceTimer.current = setTimeout(() => {
      searchAddress(query, fieldName);
    }, 500);
  };
  
  // Adresssuche API Call
  const searchAddress = async (query, fieldName) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/adresssuche/suche?query=${encodeURIComponent(query)}&limit=10`);
      const data = await response.json();
      
      if (data.success && data.ergebnisse.length > 0) {
        setAdressSuggestions(data.ergebnisse);
        setShowAdressSuggestions(true);
      } else {
        setAdressSuggestions([]);
        setShowAdressSuggestions(false);
      }
    } catch (error) {
      console.error('Adresssuche Fehler:', error);
      setAdressSuggestions([]);
      setShowAdressSuggestions(false);
    } finally {
      setAdressLoading(false);
    }
  };
  
  // Adresse auswählen
  const selectAddress = (adresse) => {
    const strasseUndNr = adresse.strasse 
      ? `${adresse.strasse}${adresse.hausnummer ? ' ' + adresse.hausnummer : ''}`
      : adresse.display_name.split(',')[0];
    const plzOrt = `${adresse.plz || ''} ${adresse.ort || ''}`.trim();
    
    // Mapping: Welches Feld triggerte die Suche?
    const fieldMapping = {
      // Kunde
      'adresse1': { strasse: 'adresse1', plzOrt: 'adresse2' },
      'adresse2': { strasse: 'adresse1', plzOrt: 'adresse2' },
      
      // Gegner
      'gegner_strasse': { strasse: 'gegner_strasse', plzOrt: 'gegner_ort' },
      'gegner_ort': { strasse: 'gegner_strasse', plzOrt: 'gegner_ort' },
      
      // Auftraggeber
      'auftraggeber_strasse': { strasse: 'auftraggeber_strasse', plzOrt: 'auftraggeber_ort' },
      'auftraggeber_ort': { strasse: 'auftraggeber_strasse', plzOrt: 'auftraggeber_ort' },
      
      // Sachverständiger
      'sv_strasse': { strasse: 'sv_strasse', plzOrt: 'sv_ort' },
      'sv_ort': { strasse: 'sv_strasse', plzOrt: 'sv_ort' },
      
      // Schadenort (nur ein Feld)
      'schadenort': { strasse: 'schadenort', plzOrt: null }
    };
    
    const mapping = fieldMapping[selectedFieldForAddress];
    if (mapping) {
      const updates = {};
      
      // Wenn Straßenfeld aktiv war, fülle beide
      if (selectedFieldForAddress.includes('strasse') || selectedFieldForAddress === 'adresse1') {
        updates[mapping.strasse] = strasseUndNr;
        if (mapping.plzOrt) {
          updates[mapping.plzOrt] = plzOrt;
        }
      } 
      // Wenn PLZ/Ort-Feld aktiv war, fülle nur PLZ/Ort
      else if (selectedFieldForAddress.includes('ort') || selectedFieldForAddress === 'adresse2') {
        if (mapping.plzOrt) {
          updates[mapping.plzOrt] = plzOrt;
        }
      }
      // Schadenort: Komplette Adresse
      else if (selectedFieldForAddress === 'schadenort') {
        updates.schadenort = `${strasseUndNr}, ${plzOrt}`;
      }
      
      setFormData(prev => ({
        ...prev,
        ...updates
      }));
    }
    
    setShowAdressSuggestions(false);
    setAdressSuggestions([]);
  };
  
  // Click outside handler für Adress-Dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAdressSuggestions && !event.target.closest('.relative')) {
        setShowAdressSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAdressSuggestions]);
  
  // Vorsteuerabzug automatisch übernehmen
  useEffect(() => {
    if (formData.vorsteuerabzugsberechtigt && !formData.vorsteuerabzug) {
      const wert = formData.vorsteuerabzugsberechtigt.toLowerCase() === 'ja' ? 'ja' : 'nein';
      setFormData(prev => ({
        ...prev,
        vorsteuerabzug: wert
      }));
    }
  }, [formData.vorsteuerabzugsberechtigt]);
  
  // Lade Unterschrift aus localStorage beim Start (schneller als DB)
  useEffect(() => {
    if (akteId) {
      try {
        const savedSignature = localStorage.getItem(`signature_${akteId}`);
        if (savedSignature && !signatureImage) {
          setSignatureImage(savedSignature);
        }
      } catch (e) {
        console.warn('Konnte Unterschrift nicht aus localStorage laden');
      }
    }
  }, [akteId]);

  // GDV Lookup Handler
  const handleGDVLookup = async () => {
    const kennzeichen = formData.gegner_kennzeichen;
    const schadentag = formData.schadentag;
    const abfrageart = formData.gdv_abfrageart || 'haftpflicht';

    if (!kennzeichen || !kennzeichen.trim()) {
      toast.error('Kennzeichen fehlt', {
        description: 'Bitte geben Sie ein Kennzeichen ein'
      });
      return;
    }

    if (!schadentag) {
      toast.error('Schadentag fehlt', {
        description: 'Bitte geben Sie einen Schadentag ein'
      });
      return;
    }

    setLoading(true);
    let loadingToastId = null;
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('autogutachter24_token');
      
      if (!token) {
        toast.error('Nicht angemeldet', {
          description: 'Bitte melden Sie sich an, um die GDV-Abfrage zu nutzen'
        });
        setLoading(false);
        return;
      }
      
      const abfrageartText = abfrageart === 'kasko' ? 'Kasko' : 'Haftpflicht';
      loadingToastId = toast.loading(`GDV-Abfrage läuft... 🔍`, {
        description: `${abfrageartText}-Versicherungsdaten werden abgerufen`,
        duration: 60000
      });

      // Generate unique request ID
      const request_id = `REQ-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      // Map request_type: 1 = Haftpflicht, 2 = Kasko
      const request_type = abfrageart === 'kasko' ? '2' : '1';

      console.log('🚀 Starting GDV bearer request:', { 
        kennzeichen, 
        schadentag, 
        abfrageart,
        request_type,
        request_id 
      });

      // Timeout-Controller für die Anfrage (60 Sekunden)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);
      
      const response = await fetch(`${BACKEND_URL}/api/gdv/bearer-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          request_id: request_id,
          request_type: request_type,
          date_of_loss: schadentag,
          country: 'D',
          licence_number: kennzeichen,
          admission_office_request_desired: '0'
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      console.log('📡 API Response Status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || `GDV-Abfrage fehlgeschlagen (Status: ${response.status})`;
        console.error('❌ API Error:', errorMessage);
        
        // Schließe den Loading-Toast
        toast.dismiss(loadingToastId);
        
        toast.error('GDV-Abfrage fehlgeschlagen', {
          description: errorMessage
        });
        
        setLoading(false);
        return;
      }

      const result = await response.json();
      console.log('📡 GDV API Response:', result);
      console.log('📊 GDV Response Data:', JSON.stringify(result, null, 2));
      console.log('🔍 Checking result.success:', result.success);
      console.log('🔍 Checking result.data:', result.data);

      // Prüfe explizit auf success-Flag
      if (result.success === false) {
        // API hat einen Fehler zurückgegeben (z.B. keine Daten gefunden)
        const errorMsg = result.error || 'Keine Versicherungsdaten gefunden';
        console.warn('⚠️ GDV API returned error:', errorMsg);
        
        // Schließe den Loading-Toast
        toast.dismiss(loadingToastId);
        
        toast.error('GDV-Abfrage: Keine Daten', {
          description: errorMsg
        });
        
        setLoading(false);
        return;
      }

      if (result.success && result.data) {
        console.log('✅ GDV Data to merge:', result.data);
        console.log('📊 Number of GDV fields:', Object.keys(result.data).length);
        console.log('🔍 Sample fields:', {
          versicherung_gesellschaft: result.data.versicherung_gesellschaft,
          versicherung_nummer: result.data.versicherung_nummer,
          versicherung_telefon: result.data.versicherung_telefon,
          versicherung_email: result.data.versicherung_email,
          marke: result.data.marke,
          modell: result.data.modell
        });
        
        // Update formData with Versicherungsdaten
        setFormData(prev => {
          console.log('📋 Previous formData keys:', Object.keys(prev).length);
          const newData = {
            ...prev,
            ...result.data,
            // Fahrzeugdaten vom Gegner explizit setzen
            gegner_fahrzeug_marke: result.data.marke || result.data.gdv_manufacturer_name || prev.gegner_fahrzeug_marke,
            gegner_fahrzeug_modell: result.data.modell || result.data.gdv_type_name || prev.gegner_fahrzeug_modell
          };
          console.log('📦 New formData keys:', Object.keys(newData).length);
          console.log('🔍 Checking specific fields in newData:', {
            versicherung_gesellschaft: newData.versicherung_gesellschaft,
            versicherung_nummer: newData.versicherung_nummer,
            versicherung_telefon: newData.versicherung_telefon,
            marke: newData.marke,
            modell: newData.modell,
            gegner_fahrzeug_marke: newData.gegner_fahrzeug_marke,
            gegner_fahrzeug_modell: newData.gegner_fahrzeug_modell
          });
          return newData;
        });

        const fieldCount = Object.keys(result.data).length;
        
        // Schließe den Loading-Toast
        toast.dismiss(loadingToastId);
        
        toast.success('GDV-Abfrage erfolgreich ✅', {
          description: `${fieldCount} Versicherungsfelder wurden geladen`
        });
        
        console.log('✅ GDV-Abfrage abgeschlossen, setLoading(false) wird im finally aufgerufen');
      } else {
        // Unerwartetes Response-Format
        console.warn('⚠️ Unexpected GDV response format:', result);
        
        // Schließe den Loading-Toast
        toast.dismiss(loadingToastId);
        
        toast.error('GDV-Abfrage: Unerwartetes Format', {
          description: 'Die API-Antwort konnte nicht verarbeitet werden'
        });
      }
    } catch (error) {
      console.error('❌ GDV Lookup Error:', error);
      console.error('❌ Error stack:', error.stack);
      
      // Schließe den Loading-Toast im Fehlerfall
      toast.dismiss(loadingToastId);
      
      let errorMessage = 'Fehler bei der Versicherungsabfrage';
      let errorTitle = 'GDV-Abfrage fehlgeschlagen';
      
      // Spezifische Fehlerbehandlung
      if (error.name === 'AbortError') {
        errorTitle = 'Zeitüberschreitung';
        errorMessage = 'Die GDV-Abfrage hat zu lange gedauert (>60 Sek.). Bitte versuchen Sie es später erneut oder kontaktieren Sie den Support.';
      } else if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        errorMessage = 'Verbindungsfehler: Backend nicht erreichbar';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorTitle, {
        description: errorMessage
      });
      
      console.log('🔧 Catch block complete, proceeding to finally');
    } finally {
      console.log('🔧 Finally block executing, setting loading to false');
      setLoading(false);
      console.log('✅ Loading set to false');
    }
  };

  const handleBewertungChange = (e) => {
    const { name, value } = e.target;
    setBewertungData({
      ...bewertungData,
      [name]: value
    });
    // Auto-Save wird automatisch durch den zentralen useEffect getriggert
  };

  // Handler für Beteiligte
  const handleAddBeteiligter = () => {
    if (!newBeteiligter.bezeichnung) {
      toast.error('Fehler', {
        description: 'Bitte geben Sie mindestens eine Bezeichnung ein'
      });
      return;
    }

    setBeteiligte([...beteiligte, { ...newBeteiligter, id: Date.now() }]);
    setNewBeteiligter({
      beteiligung: 'Fahrzeughalter',
      kontakttyp: 'Person',
      bezeichnung: '',
      plz: '',
      ort: '',
      email: '',
      telefon: ''
    });
    setShowBeteiligterForm(false);
    
    toast.error('Erfolgreich', {
        description: 'Beteiligter wurde hinzugefügt'
    });
  };

  const handleDeleteBeteiligter = (id) => {
    setBeteiligte(beteiligte.filter(b => b.id !== id));
    toast.success('Gelöscht', {
        description: 'Beteiligter wurde entfernt'
      });
  };

  // Handler für Besichtigungen
  const handleAddBesichtigung = () => {
    if (!newBesichtigung.vonDatum || !newBesichtigung.vonUhrzeit) {
      toast.error('Fehler', {
        description: 'Bitte geben Sie mindestens Start-Datum und Uhrzeit ein'
      });
      return;
    }

    if (editingBesichtigung) {
      // Update existing
      setBesichtigungen(besichtigungen.map(b => 
        b.id === editingBesichtigung.id ? { ...newBesichtigung, id: editingBesichtigung.id } : b
      ));
      setEditingBesichtigung(null);
      toast.error('Aktualisiert', {
        description: 'Besichtigung wurde aktualisiert'
      });
    } else {
      // Add new
      setBesichtigungen([...besichtigungen, { ...newBesichtigung, id: Date.now() }]);
      toast.success('Erfolgreich', {
        description: 'Besichtigung wurde hinzugefügt'
      });
    }

    // Reset form
    setNewBesichtigung({
      besichtigungsort: '',
      besichtigungsbedingungen: '',
      dokumentationstext: '',
      teilnehmer: '',
      ortFirma: '',
      weitereTeilnehmer: '',
      adresse: '',
      externerSachverstaendiger: '',
      vonDatum: '',
      vonUhrzeit: '',
      bisDatum: '',
      bisUhrzeit: '',
      ersteller: 'Herr Özer Kanatli',
      sachverstaendiger: 'Herr Özer Kanatli',
      teilnehmerBenachrichtigen: false,
      bemerkung: '',
      erinnerungAktivieren: false,
      entfernung: '',
      fahrtzeit: ''
    });
    setShowBesichtigungForm(false);
  };

  const handleEditBesichtigung = (besichtigung) => {
    setNewBesichtigung(besichtigung);
    setEditingBesichtigung(besichtigung);
    setShowBesichtigungForm(true);
  };

  const handleDeleteBesichtigung = (id) => {
    setBesichtigungen(besichtigungen.filter(b => b.id !== id));
    toast.success('Gelöscht', {
        description: 'Besichtigung wurde entfernt'
      });
  };

  const handleBesichtigungChange = (field, value) => {
    setNewBesichtigung({
      ...newBesichtigung,
      [field]: value
    });
  };

  // Unfallbericht OCR Handlers
  const handleUnfallberichtUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    await analyzeUnfallbericht(file);
  };

  const handleUnfallberichtDrop = async (e) => {
    e.preventDefault();
    setUnfallberichtDragging(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    await analyzeUnfallbericht(file);
  };

  const handleUnfallberichtDragOver = (e) => {
    e.preventDefault();
    setUnfallberichtDragging(true);
  };

  const handleUnfallberichtDragLeave = () => {
    setUnfallberichtDragging(false);
  };

  const analyzeUnfallbericht = async (file) => {
    try {
      setUnfallberichtLoading(true);
      setUnfallberichtData(null);
      
      const formData = new FormData();
      formData.append('file', file);
      
      toast.success('Analyse gestartet', {
        description: 'Der Unfallbericht wird mit KI analysiert...'
      });
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/unfallbericht/analyze`, {
        method: 'POST',
        body: formData,
      });
      
      const result = await response.json();
      
      if (result.success && result.data) {
        setUnfallberichtData(result.data);
        toast.success('Analyse erfolgreich!', {
        description: 'Der Unfallbericht wurde erfolgreich ausgelesen.'
      });
      } else if (result.raw_text) {
        // Wenn JSON-Parsing fehlgeschlagen ist, zeige rohen Text
        toast.success('Text erkannt', {
        description: 'Bericht wurde gelesen, aber nicht strukturiert.'
      });
        console.log('Raw text:', result.raw_text);
      } else {
        throw new Error(result.error || 'Analyse fehlgeschlagen');
      }
    } catch (error) {
      console.error('Unfallbericht Analyse Error:', error);
      toast.error('Fehler bei der Analyse', {
        description: error.message || 'Der Unfallbericht konnte nicht analysiert werden.'
      });
    } finally {
      setUnfallberichtLoading(false);
    }
  };

  const applyUnfallberichtData = () => {
    if (!unfallberichtData) return;
    
    console.log('📋 Unfallbericht Data:', unfallberichtData);
    
    const updates = {};
    
    // Map Unfallbericht data to form fields
    // Allgemeine Unfalldaten
    if (unfallberichtData.unfalldatum) {
      updates.unfalldatum = unfallberichtData.unfalldatum;
      console.log('✓ unfalldatum:', unfallberichtData.unfalldatum);
    }
    if (unfallberichtData.unfallzeit) {
      updates.unfallzeit = unfallberichtData.unfallzeit;
      console.log('✓ unfallzeit:', unfallberichtData.unfallzeit);
    }
    if (unfallberichtData.unfallort) {
      updates.unfallort = unfallberichtData.unfallort;
      console.log('✓ unfallort:', unfallberichtData.unfallort);
    }
    
    // Gegner-Daten (Position 1, linke Spalte)
    if (unfallberichtData.gegner) {
      const gegner = unfallberichtData.gegner;
      console.log('👤 Gegner Daten:', gegner);
      
      // Name - ins Feld "gegner_name"
      if (gegner.name) {
        const fullName = `${gegner.vorname || ''} ${gegner.name}`.trim();
        updates.gegner_name = fullName;
        console.log('✓ gegner_name:', fullName);
      }
      
      // Kennzeichen - ins Feld "gegner_kennzeichen"
      if (gegner.kennzeichen) {
        updates.gegner_kennzeichen = gegner.kennzeichen;
        console.log('✓ gegner_kennzeichen:', gegner.kennzeichen);
      }
    }
    
    // Versicherungsdaten - ins Feld "gegner_versicherung"
    if (unfallberichtData.versicherung) {
      if (unfallberichtData.versicherung.gesellschaft) {
        updates.gegner_versicherung = unfallberichtData.versicherung.gesellschaft;
        console.log('✓ gegner_versicherung:', unfallberichtData.versicherung.gesellschaft);
      }
      if (unfallberichtData.versicherung.versicherungsnummer) {
        updates.gegner_schadennummer = unfallberichtData.versicherung.versicherungsnummer;
        console.log('✓ gegner_schadennummer:', unfallberichtData.versicherung.versicherungsnummer);
      }
    }
    
    console.log('📝 Final updates:', updates);
    console.log('📝 Number of fields to update:', Object.keys(updates).length);
    
    if (Object.keys(updates).length === 0) {
      toast.error('Keine Daten gefunden', {
        description: 'Es konnten keine passenden Daten extrahiert werden.'
      });
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      ...updates
    }));
    
    toast.success('Gegner-Daten übernommen!', {
        description: `${Object.keys(updates).length} Felder wurden automatisch ausgefüllt: ${Object.keys(updates).join(', ')}`
      });
  };

  // Automatische Aktenzeichen-Generierung
  const generateAktenzeichen = (kundenName) => {
    if (!kundenName || kundenName.trim() === '') {
      return '';
    }

    // Namen aufteilen (z.B. "Max Mustermann" → ["Max", "Mustermann"])
    const nameParts = kundenName.trim().split(/\s+/);
    
    let initials = '';
    
    if (nameParts.length >= 2) {
      // Wenn mindestens Vorname und Nachname vorhanden
      const vorname = nameParts[0];
      const nachname = nameParts[nameParts.length - 1]; // Letzter Teil ist Nachname
      
      // Erste Buchstaben nehmen und in Großbuchstaben umwandeln
      initials = (vorname[0] + nachname[0]).toUpperCase();
    } else if (nameParts.length === 1) {
      // Wenn nur ein Name vorhanden (z.B. Firma)
      initials = nameParts[0].substring(0, 2).toUpperCase();
    }
    
    // Aktuelles Datum im Format TTMMJJ
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = String(now.getFullYear()).substring(2); // Nur die letzten 2 Ziffern
    
    const dateStr = `${day}${month}${year}`;
    
    // Format: KH-MM 121125
    return `KH-${initials} ${dateStr}`;
  };

  // useEffect um Aktenzeichen zu generieren wenn Kundenname sich ändert
  useEffect(() => {
    if (formData.kunde && formData.kunde.trim() !== '') {
      const newAktenzeichen = generateAktenzeichen(formData.kunde);
      if (newAktenzeichen !== formData.aktenzeichen) {
        setFormData(prev => ({
          ...prev,
          aktenzeichen: newAktenzeichen
        }));
      }
    }
  }, [formData.kunde]);

  // useEffect um Anfrageart automatisch zu setzen basierend auf Auftragstyp
  useEffect(() => {
    if (formData.auftragstyp === 'Gutachten' || formData.auftragstyp === 'Kurzgutachten' || formData.auftragstyp === 'Wertgutachten') {
      // Bei Gutachten → Anfrageart auf Haftpflicht setzen
      if (formData.anfrageart !== 'Haftpflicht') {
        setFormData(prev => ({
          ...prev,
          anfrageart: 'Haftpflicht'
        }));
      }
    } else if (formData.auftragstyp === 'Kasko') {
      // Bei Kasko → Anfrageart auf Kasko setzen
      if (formData.anfrageart !== 'Kasko') {
        setFormData(prev => ({
          ...prev,
          anfrageart: 'Kasko'
        }));
      }
    }
  }, [formData.auftragstyp]);

  // useEffect: Automatische Synchronisation - Auftraggeber (Unterschrift) ← Auftrag (Kundendaten)
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      auftraggeber_name: prev.kunde || '',
      auftraggeber_strasse: prev.adresse1 || '',
      auftraggeber_ort: prev.adresse2 || '',
      auftraggeber_kennzeichen: prev.kennzeichen || ''
    }));
  }, [formData.kunde, formData.adresse1, formData.adresse2, formData.kennzeichen]);

  // useEffect: Automatische Synchronisation - Versicherter/Gegner (Unterschrift) ← Versicherung (Gegner-Daten)
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      gegner_vorname: prev.gegner_name ? prev.gegner_name.split(' ')[0] : '',
      gegner_name: prev.gegner_name || '',
      gegner_kennzeichen: prev.gegner_kennzeichen || ''
    }));
  }, [formData.gegner_name, formData.gegner_kennzeichen]);

  // useEffect: Automatische Synchronisation - Versicherung (Unterschrift) ← Versicherung (Versicherungsdaten)
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      versicherungsname: prev.versicherung_gesellschaft || '',
      versicherungsscheinnummer: prev.versicherung_nummer || '',
      schadennummer: prev.schadennummer || '',
      schadentag: prev.schadentag || ''
    }));
  }, [formData.versicherung_gesellschaft, formData.versicherung_nummer, formData.schadennummer, formData.schadentag]);

  // useEffect: Sachverständiger-Daten vom Backend laden (bei erstem Laden)
  useEffect(() => {
    const loadProfileData = async () => {
      // Nur laden, wenn die Felder noch leer sind
      if (formData.sv_firmenname || formData.sv_name) {
        return;
      }

      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('autogutachter24_token');
        
        if (!token) {
          console.log('Kein Token vorhanden, Profil wird nicht geladen');
          return;
        }

        console.log('📥 Lade Sachverständiger-Daten vom Profil...');
        
        const response = await fetch(`${BACKEND_URL}/api/profile/`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const profileData = await response.json();
          console.log('✅ Profildaten geladen:', profileData);
          
          // Sachverständiger-Daten in formData übernehmen
          setFormData(prev => ({
            ...prev,
            sv_firmenname: profileData.sv_firmenname || '',
            sv_name: profileData.sv_name || '',
            sv_vorname: profileData.sv_vorname || '',
            sv_strasse: profileData.sv_strasse || '',
            sv_ort: profileData.sv_ort || ''
          }));
        } else {
          console.warn('⚠️ Profil konnte nicht geladen werden:', response.status);
        }
      } catch (error) {
        console.error('❌ Fehler beim Laden der Profildaten:', error);
      }
    };

    loadProfileData();
  }, []);

  // useEffect: Automatische Mietwagenspiegel-Abfrage bei VIN-Änderung
  useEffect(() => {
    const holeMietwagenklasse = async () => {
      const vin = formData.vin || formData.fahrgestellnummer;
      
      if (!vin || vin.length < 17) {
        return; // VIN muss 17 Zeichen haben
      }

      // Verhindere mehrfache Abfragen für die gleiche VIN
      if (nutzungsausfallData.fahrzeugklasse && mietwagenspiegelLoading) {
        return;
      }

      setMietwagenspiegelLoading(true);
      
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        console.log('🚗 Rufe DAT Mietwagenspiegel für VIN ab:', vin);

        const response = await fetch(`${BACKEND_URL}/api/dat/mietwagenspiegel/abfrage`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            vin: vin,
            erstzulassung: formData.erstzulassung
          })
        });

        const data = await response.json();
        
        if (data.success && data.fahrzeugklasse) {
          console.log('✅ Mietwagenspiegel-Klasse erhalten:', data.fahrzeugklasse, '-', data.fahrzeugbeschreibung);
          
          // Setze die Fahrzeugklasse automatisch
          setNutzungsausfallData(prev => ({
            ...prev,
            fahrzeugklasse: data.fahrzeugklasse
          }));

          // Wenn Ausfalltage vorhanden, berechne automatisch
          if (nutzungsausfallData.ausfalltage) {
            setTimeout(() => {
              berechneNutzungsausfall();
            }, 500);
          }

          toast.success('Mietwagenspiegel-Klasse automatisch ermittelt', {
            description: `Klasse ${data.fahrzeugklasse} - ${data.fahrzeugbeschreibung} (${data.tagessatz}€/Tag)`,
            duration: 5000
          });
        }
      } catch (error) {
        console.error('❌ Fehler beim Abruf der Mietwagenspiegel-Klasse:', error);
      } finally {
        setMietwagenspiegelLoading(false);
      }
    };

    holeMietwagenklasse();
  }, [formData.vin, formData.fahrgestellnummer]);

  // useEffect: Sachverständiger-Daten ins Backend-Profil speichern (wenn geändert)
  useEffect(() => {
    const saveProfileData = async () => {
      // Nur speichern, wenn mindestens ein Feld ausgefüllt ist
      if (!formData.sv_firmenname && !formData.sv_name) {
        return;
      }

      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('autogutachter24_token');
        
        if (!token) {
          return;
        }

        console.log('💾 Speichere Sachverständiger-Daten ins Profil...');
        
        const response = await fetch(`${BACKEND_URL}/api/profile/`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            sv_firmenname: formData.sv_firmenname,
            sv_name: formData.sv_name,
            sv_vorname: formData.sv_vorname,
            sv_strasse: formData.sv_strasse,
            sv_ort: formData.sv_ort
          })
        });

        if (response.ok) {
          console.log('✅ Profildaten gespeichert');
        } else {
          console.warn('⚠️ Profildaten konnten nicht gespeichert werden:', response.status);
        }
      } catch (error) {
        console.error('❌ Fehler beim Speichern der Profildaten:', error);
      }
    };

    // Debounce: Nur speichern, wenn sich die Daten nicht für 2 Sekunden ändern
    const timeoutId = setTimeout(() => {
      saveProfileData();
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [formData.sv_firmenname, formData.sv_name, formData.sv_vorname, formData.sv_strasse, formData.sv_ort]);

  // ⚡ ZENTRALER AUTO-SAVE useEffect - Überwacht ALLE wichtigen Datenänderungen
  useEffect(() => {
    // Nicht auto-saven wenn:
    // 1. Keine akteId existiert (Akte wurde noch nicht erstellt)
    // 2. Gerade geladen wird (initiales Laden)
    if (!akteId || autoSaving) {
      return;
    }
    
    // Debounced Auto-Save: Warte 2 Sekunden nach letzter Änderung
    const autoSaveTimer = setTimeout(() => {
      console.log('⚡ Auto-Save: Speichere alle Änderungen...');
      autoSaveAkte();
    }, 2000);
    
    // Cleanup: Lösche Timer wenn sich Daten erneut ändern
    return () => clearTimeout(autoSaveTimer);
  }, [
    // Überwache ALLE wichtigen States
    formData,
    lichtbilder,
    bewertungData,
    wiederbeschaffungswertErgebnis,
    // Hinweis: akteId und autoSaving werden NICHT überwacht, um Endlosschleife zu vermeiden
  ]);


  const renderBewertungForm = () => (
    <div className="max-w-full 2xl:max-w-[1500px] mx-auto space-y-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3">
          <BarChart3 className="w-6 h-6" />
          <h2 className="text-xl font-semibold">Bewertung</h2>
        </div>
        <p className="text-sm text-gray-500 mt-1">Restwert und Minderwert des Fahrzeugs</p>
      </div>

      {/* Tabs für Restwert, Minderwert, Beurteilung und Übersicht */}
      <Tabs value={bewertungTab} onValueChange={setBewertungTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 bg-gray-100">
          <TabsTrigger value="restwert">
            <TrendingDown className="w-4 h-4 mr-2" />
            Restwert
          </TabsTrigger>
          <TabsTrigger value="minderwert">
            <AlertCircle className="w-4 h-4 mr-2" />
            Minderwert
          </TabsTrigger>
          <TabsTrigger value="beurteilung">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Beurteilung
          </TabsTrigger>
          <TabsTrigger value="uebersicht">
            <BarChart3 className="w-4 h-4 mr-2" />
            Übersicht
          </TabsTrigger>
        </TabsList>

        {/* Restwert Tab */}
        <TabsContent value="restwert" className="space-y-6">
          <div className="bg-white rounded-md border border-gray-200">
        <div className="px-4 py-3 bg-blue-50 border-b border-blue-100">
          <h3 className="flex items-center space-x-2 text-blue-900 text-sm font-medium">
            <TrendingDown className="w-5 h-5" />
            <span>Restwert</span>
          </h3>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Zustand des Fahrzeugs
            </label>
            <select
              name="restwert_zustand"
              value={bewertungData.restwert_zustand}
              onChange={handleBewertungChange}
              className="w-full h-12 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="Totalschaden">Totalschaden</option>
              <option value="Reparabel">Reparabel</option>
              <option value="Wirtschaftlicher Totalschaden">Wirtschaftlicher Totalschaden</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Wiederbeschaffungswert (EUR) <span className="text-gray-400 text-xs">(wird automatisch berechnet)</span>
              </label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  name="wiederbeschaffungswert"
                  placeholder="z.B. 25000"
                  value={bewertungData.wiederbeschaffungswert}
                  onChange={handleBewertungChange}
                  className="h-12 flex-1"
                />
                <Button
                  type="button"
                  onClick={async () => {
                    if (!formData.marke || !formData.modell) {
                      toast.error('Fahrzeugdaten fehlen', {
                        description: 'Marke und Modell werden benötigt'
                      });
                      return;
                    }
                    
                    try {
                      setWiederbeschaffungswertLoading(true);
                      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
                      toast.loading('🤖 KI analysiert Marktpreise...', { id: 'ki-bewertung' });
                      const response = await fetch(`${BACKEND_URL}/api/ki-wiederbeschaffungswert/berechnen`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          hersteller: formData.marke,
                          modell: formData.modell,
                          erstzulassung: formData.erstzulassung,
                          kilometerstand: formData.laufleistung ? parseInt(formData.laufleistung) : 50000,
                          kraftstoff: formData.kraftstoff,
                          hubraum: formData.hubraum,
                          leistung_kw: formData.leistung_kw,
                          farbe: formData.farbe
                        })
                      });
                      
                      if (!response.ok) throw new Error('API-Fehler');
                      
                      const data = await response.json();
                      setWiederbeschaffungswertErgebnis(data);
                      const neuesBewertungData = {
                        ...bewertungData,
                        wiederbeschaffungswert: Math.round(data.durchschnittspreis).toString()
                      };
                      setBewertungData(neuesBewertungData);
                      
                      // Auto-Save nach Wiederbeschaffungswert-Berechnung
                      if (akteId) {
                        await autoSaveAkte({ 
                          wiederbeschaffungswert: data,
                          bewertung: neuesBewertungData
                        });
                      }
                      
                      toast.success('✅ KI-Marktanalyse abgeschlossen!', {
                        id: 'ki-bewertung',
                        description: `Ø ${data.durchschnittspreis.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})} € • ${data.vergleichsfahrzeuge?.length || 0} Vergleichsfahrzeuge analysiert`
                      });
                    } catch (error) {
                      toast.error('KI-Bewertung fehlgeschlagen', { id: 'ki-bewertung' });
                      setWiederbeschaffungswertErgebnis(null);
                    } finally {
                      setWiederbeschaffungswertLoading(false);
                    }
                  }}
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-4 font-medium shadow-md hover:shadow-lg transition-all"
                  title="🤖 KI-basierte Marktanalyse (AutoScout24 + mobile.de)"
                  disabled={wiederbeschaffungswertLoading}
                >
                  {wiederbeschaffungswertLoading ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <span>🤖 KI</span>
                  )}
                </Button>
                
                {/* DAT Bewertung Button */}
                <Button
                  type="button"
                  size="sm"
                  onClick={async () => {
                    if (!formData.marke || !formData.modell) {
                      toast.error('Fahrzeugdaten fehlen', {
                        description: 'Marke und Modell werden benötigt'
                      });
                      return;
                    }
                    
                    try {
                      setWiederbeschaffungswertLoading(true);
                      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
                      toast.loading('🚗 DAT SilverDAT berechnet...', { id: 'dat-bewertung' });
                      // Verwende Test-Europa-Code falls verfügbar, sonst Fahrzeugdaten
                      const requestBody = {
                        dat_europacode: formData.dat_europacode || '015700722040003', // Test-Code
                        hersteller: formData.marke,
                        modell: formData.modell,
                        erstzulassung: formData.erstzulassung || '01.01.2020',
                        kilometerstand: formData.laufleistung ? parseInt(formData.laufleistung) : 50000,
                        kraftstoff: formData.kraftstoff,
                        hubraum: formData.hubraum,
                        leistung_kw: formData.leistung_kw,
                        vin: formData.vin
                      };
                      
                      const response = await fetch(`${BACKEND_URL}/api/dat-wiederbeschaffungswert/berechnen`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(requestBody)
                      });
                      
                      if (!response.ok) throw new Error('API-Fehler');
                      
                      const data = await response.json();
                      
                      // Konvertiere DAT Response in das erwartete Format
                      const convertedData = {
                        success: data.success,
                        durchschnittspreis: data.wiederbeschaffungswert,
                        min_preis: data.haendler_einkauf,
                        max_preis: data.haendler_verkauf,
                        quellen: [{
                          quelle: data.quelle || "DAT SilverDAT",
                          preis: data.wiederbeschaffungswert,
                          preisspanne_min: data.haendler_einkauf,
                          preisspanne_max: data.haendler_verkauf,
                          erklaerung: "Offizielle DAT Bewertung nach Europacode"
                        }],
                        vergleichsfahrzeuge: [],
                        fahrzeug: data.fahrzeugdaten,
                        analyse_details: {
                          methode: "DAT SilverDAT valuateFinance",
                          analysedatum: data.bewertungsdatum,
                          analysierte_quellen: 1
                        },
                        hinweise: [
                          data.quelle || "DAT SilverDAT - Richtwert nach Europa-Code + Marktindex",
                          "Berücksichtigt: Fahrzeugalter, Kilometerstand, Marktsituation",
                          data.fahrzeugdaten?.dat_europacode ? `Europa-Code: ${data.fahrzeugdaten.dat_europacode}` : "Algorithmische Berechnung"
                        ]
                      };
                      
                      setWiederbeschaffungswertErgebnis(convertedData);
                      const neuesBewertungData = {
                        ...bewertungData,
                        wiederbeschaffungswert: Math.round(data.wiederbeschaffungswert).toString()
                      };
                      setBewertungData(neuesBewertungData);
                      
                      // Auto-Save nach DAT-Bewertung
                      if (akteId) {
                        await autoSaveAkte({ 
                          wiederbeschaffungswert: convertedData,
                          bewertung: neuesBewertungData
                        });
                      }
                      
                      toast.success('✅ DAT Bewertung abgeschlossen!', {
                        id: 'dat-bewertung',
                        description: `Wiederbeschaffungswert: ${data.wiederbeschaffungswert.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})} €`
                      });
                    } catch (error) {
                      toast.error('DAT Bewertung fehlgeschlagen', { id: 'dat-bewertung' });
                      setWiederbeschaffungswertErgebnis(null);
                    } finally {
                      setWiederbeschaffungswertLoading(false);
                    }
                  }}
                  className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white px-4 font-medium shadow-md hover:shadow-lg transition-all"
                  title="🚗 DAT SilverDAT Bewertung (Europacode)"
                  disabled={wiederbeschaffungswertLoading}
                >
                  {wiederbeschaffungswertLoading ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <span>🚗 DAT</span>
                  )}
                </Button>
              </div>
            </div>

            {/* Wiederbeschaffungswert Ergebnisanzeige */}
            {wiederbeschaffungswertErgebnis && (
              <div className="col-span-2 mt-6 p-6 bg-blue-50 border-2 border-blue-200 rounded-lg">
                <h4 className="text-lg font-bold text-blue-900 mb-4 flex items-center">
                  <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Wiederbeschaffungswert ermittelt
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  {wiederbeschaffungswertErgebnis.quellen && Array.isArray(wiederbeschaffungswertErgebnis.quellen) && wiederbeschaffungswertErgebnis.quellen.map((quelle, index) => (
                    <div key={index} className="bg-white p-4 rounded-lg border border-blue-200">
                      <p className="text-xs text-gray-600 mb-1">{quelle.quelle}</p>
                      <p className="text-2xl font-bold text-blue-700">
                        {quelle.preis && quelle.preis.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})} €
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{quelle.kategorie}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {quelle.anzahl_vergleichsfahrzeuge || 0} Vergleichsfahrzeuge
                      </p>
                      {quelle.preisspanne_min && quelle.preisspanne_max && (
                        <p className="text-xs text-gray-500 mt-2">
                          Spanne: {quelle.preisspanne_min.toLocaleString('de-DE')} € - {quelle.preisspanne_max.toLocaleString('de-DE')} €
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                <div className="pt-4 border-t border-blue-300">
                  <p className="text-sm text-gray-600 mb-2">Durchschnittlicher Wiederbeschaffungswert</p>
                  <p className="text-3xl font-bold text-blue-700">
                    {wiederbeschaffungswertErgebnis.durchschnittspreis.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})} €
                  </p>
                  <div className="mt-2 flex items-center gap-4 text-sm text-gray-600">
                    <span>Min: {wiederbeschaffungswertErgebnis.min_preis.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})} €</span>
                    <span>•</span>
                    <span>Max: {wiederbeschaffungswertErgebnis.max_preis.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})} €</span>
                  </div>
                </div>

                {/* Vergleichsfahrzeuge mit Bildern */}
                {wiederbeschaffungswertErgebnis.vergleichsfahrzeuge && Array.isArray(wiederbeschaffungswertErgebnis.vergleichsfahrzeuge) && wiederbeschaffungswertErgebnis.vergleichsfahrzeuge.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-blue-300">
                    <h5 className="text-base font-bold text-blue-900 mb-4">📸 Vergleichsfahrzeuge</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {wiederbeschaffungswertErgebnis.vergleichsfahrzeuge.map((fzg, idx) => (
                        <div key={idx} className="bg-white rounded-lg border border-blue-200 overflow-hidden hover:shadow-lg transition-shadow">
                          <img 
                            src={fzg.bild_url} 
                            alt={`${fzg.marke} ${fzg.modell}`}
                            className="w-full h-40 object-cover"
                          />
                          <div className="p-3">
                            <p className="font-bold text-gray-900">{fzg.marke} {fzg.modell}</p>
                            <p className="text-sm text-gray-600">Bj. {fzg.baujahr} • {fzg.kilometerstand && fzg.kilometerstand.toLocaleString('de-DE')} km</p>
                            <p className="text-xl font-bold text-blue-700 mt-2">{fzg.preis && fzg.preis.toLocaleString('de-DE')} €</p>
                            <div className="flex items-center justify-between mt-2">
                              <p className="text-xs text-gray-500">📍 {fzg.standort}</p>
                              <p className="text-xs text-blue-600">{fzg.quelle}</p>
                            </div>
                            <a 
                              href={fzg.anzeige_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 block text-center text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 py-1 rounded transition-colors"
                            >
                              Anzeige ansehen →
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-4 text-xs text-gray-500 bg-white p-4 rounded-lg border border-blue-100">
                  <p className="font-semibold mb-2 text-gray-700">📋 Analysierte Fahrzeugdaten:</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-gray-600">Fahrzeug:</p>
                      <p className="font-medium">{wiederbeschaffungswertErgebnis.fahrzeug.hersteller || wiederbeschaffungswertErgebnis.fahrzeug.marke} {wiederbeschaffungswertErgebnis.fahrzeug.modell}</p>
                    </div>
                    {(wiederbeschaffungswertErgebnis.fahrzeug.erstzulassung || wiederbeschaffungswertErgebnis.fahrzeug.jahr) && (
                      <div>
                        <p className="text-gray-600">Baujahr:</p>
                        <p className="font-medium">{wiederbeschaffungswertErgebnis.fahrzeug.jahr || wiederbeschaffungswertErgebnis.fahrzeug.erstzulassung}</p>
                        {wiederbeschaffungswertErgebnis.fahrzeug.alter && (
                          <p className="text-xs text-gray-500">({wiederbeschaffungswertErgebnis.fahrzeug.alter} Jahre alt)</p>
                        )}
                      </div>
                    )}
                    {wiederbeschaffungswertErgebnis.fahrzeug.kilometerstand && (
                      <div>
                        <p className="text-gray-600">Kilometerstand:</p>
                        <p className="font-medium">{wiederbeschaffungswertErgebnis.fahrzeug.kilometerstand.toLocaleString('de-DE')} km</p>
                      </div>
                    )}
                    {wiederbeschaffungswertErgebnis.fahrzeug.kraftstoff && (
                      <div>
                        <p className="text-gray-600">Kraftstoff:</p>
                        <p className="font-medium">{wiederbeschaffungswertErgebnis.fahrzeug.kraftstoff}</p>
                      </div>
                    )}
                  </div>
                  
                  {wiederbeschaffungswertErgebnis.analyse_details && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="font-semibold mb-1 text-purple-700">🤖 KI-Analyse Details:</p>
                      <p className="text-xs">{wiederbeschaffungswertErgebnis.analyse_details.methode}</p>
                      <p className="text-xs mt-1">
                        📊 {wiederbeschaffungswertErgebnis.analyse_details.analysierte_quellen} Quellen • 
                        🚗 {wiederbeschaffungswertErgebnis.analyse_details.anzahl_vergleichsfahrzeuge} Vergleichsfahrzeuge
                      </p>
                      {wiederbeschaffungswertErgebnis.analyse_details.analysedatum && (
                        <p className="text-xs text-gray-400 mt-1">Analysiert am: {wiederbeschaffungswertErgebnis.analyse_details.analysedatum}</p>
                      )}
                    </div>
                  )}
                  
                  {wiederbeschaffungswertErgebnis.hinweise && Array.isArray(wiederbeschaffungswertErgebnis.hinweise) && wiederbeschaffungswertErgebnis.hinweise.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="font-semibold mb-1 text-gray-700">ℹ️ Hinweise:</p>
                      <ul className="list-disc list-inside text-xs space-y-1">
                        {wiederbeschaffungswertErgebnis.hinweise.map((hinweis, idx) => (
                          <li key={idx}>{hinweis}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {wiederbeschaffungswertErgebnis.hinweis && !wiederbeschaffungswertErgebnis.hinweise && (
                    <p className="mt-2 text-orange-600">⚠️ {wiederbeschaffungswertErgebnis.hinweis}</p>
                  )}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Restwert (EUR)
              </label>
              <Input
                type="number"
                name="restwert"
                placeholder="z.B. 5000"
                value={bewertungData.restwert}
                onChange={handleBewertungChange}
                className="h-12"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Berechnung / Begründung
            </label>
            <Textarea
              name="restwert_berechnung"
              placeholder="Beschreibung der Restwert-Berechnung..."
              value={bewertungData.restwert_berechnung}
              onChange={handleBewertungChange}
              rows={6}
            />
          </div>

          {/* Restwert Zusammenfassung */}
          {bewertungData.wiederbeschaffungswert && bewertungData.restwert && (
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">Zusammenfassung</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Wiederbeschaffungswert:</p>
                  <p className="font-bold text-blue-900">{parseFloat(bewertungData.wiederbeschaffungswert).toLocaleString('de-DE')} €</p>
                </div>
                <div>
                  <p className="text-gray-600">Restwert:</p>
                  <p className="font-bold text-blue-900">{parseFloat(bewertungData.restwert).toLocaleString('de-DE')} €</p>
                </div>
                <div className="col-span-2 pt-2 border-t border-blue-200">
                  <p className="text-gray-600">Netto-Schaden:</p>
                  <p className="font-bold text-blue-900 text-lg">
                    {(parseFloat(bewertungData.wiederbeschaffungswert) - parseFloat(bewertungData.restwert)).toLocaleString('de-DE')} €
                  </p>
                </div>
              </div>
            </div>
          )}
            </div>
          </div>
        </TabsContent>

        {/* Minderwert Tab */}
        <TabsContent value="minderwert" className="space-y-6">
          <div className="bg-white rounded-md border border-gray-200">
        <div className="px-4 py-3 bg-orange-50">
          <h3 className="flex items-center space-x-2 text-orange-900">
            <AlertCircle className="w-5 h-5" />
            <span>Minderwert</span>
          </h3>
        </div>
        <div className="pt-6 space-y-6">
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <p className="text-sm text-orange-900">
              <strong>Was ist Minderwert?</strong> Der Minderwert ist die Wertminderung eines reparierten Fahrzeugs 
              aufgrund eines Unfallschadens, auch wenn es fachgerecht repariert wurde.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minderwert in % <span className="text-gray-500">(typisch 5-15%)</span>
              </label>
              <select
                name="minderwert_prozent"
                value={bewertungData.minderwert_prozent}
                onChange={handleBewertungChange}
                className="w-full h-12 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="0">0% - Kein Minderwert</option>
                <option value="5">5% - Geringer Schaden</option>
                <option value="10">10% - Mittlerer Schaden</option>
                <option value="15">15% - Erheblicher Schaden</option>
                <option value="20">20% - Schwerer Schaden</option>
                <option value="25">25% - Sehr schwerer Schaden</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minderwert-Betrag (EUR)
              </label>
              <Input
                type="number"
                name="minderwert_betrag"
                placeholder="Wird automatisch berechnet"
                value={bewertungData.minderwert_betrag || (bewertungData.wiederbeschaffungswert ? (parseFloat(bewertungData.wiederbeschaffungswert) * parseFloat(bewertungData.minderwert_prozent) / 100).toFixed(2) : '')}
                onChange={handleBewertungChange}
                className="h-12 bg-gray-50"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Begründung des Minderwerts
            </label>
            <Textarea
              name="minderwert_begruendung"
              placeholder="Begründung für den angesetzten Minderwert..."
              value={bewertungData.minderwert_begruendung}
              onChange={handleBewertungChange}
              rows={6}
            />
            <p className="text-xs text-gray-500 mt-2">
              Berücksichtigen Sie: Fahrzeugalter, Laufleistung, Schadensumfang, Reparaturqualität, Marktakzeptanz
            </p>
          </div>

          {/* Minderwert Berechnung */}
          {bewertungData.wiederbeschaffungswert && bewertungData.minderwert_prozent !== '0' && (
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h4 className="font-semibold text-orange-900 mb-2">Berechnung</h4>
              <div className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-700">Wiederbeschaffungswert:</span>
                  <span className="font-semibold">{parseFloat(bewertungData.wiederbeschaffungswert).toLocaleString('de-DE')} €</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Minderwert ({bewertungData.minderwert_prozent}%):</span>
                  <span className="font-semibold">{(parseFloat(bewertungData.wiederbeschaffungswert) * parseFloat(bewertungData.minderwert_prozent) / 100).toLocaleString('de-DE')} €</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-orange-300">
                  <span className="text-orange-900 font-bold">Gesamter Minderwert:</span>
                  <span className="font-bold text-orange-900 text-lg">
                    {(parseFloat(bewertungData.wiederbeschaffungswert) * parseFloat(bewertungData.minderwert_prozent) / 100).toLocaleString('de-DE')} €
                  </span>
                </div>
              </div>
            </div>
          )}
            </div>
          </div>
        </TabsContent>

        {/* Beurteilung Tab */}
        <TabsContent value="beurteilung" className="space-y-6">
          <div className="bg-white rounded-md border border-gray-200">
            <div className="px-4 py-3 bg-blue-50 border-b border-blue-100">
              <h3 className="flex items-center space-x-2 text-blue-900">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>Gutachterliche Beurteilung</span>
              </h3>
            </div>
            <div className="pt-6 space-y-6">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-900">
                  <strong>Hinweis:</strong> Erfassen Sie hier die gutachterliche Beurteilung des Schadens, 
                  einschließlich technischer Bewertung, Schadensursache und Reparaturempfehlungen.
                </p>
              </div>

              {/* Schadenumfang */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  1. Schadenumfang und -beschreibung
                </label>
                <Textarea
                  name="beurteilung_schadenumfang"
                  placeholder="Detaillierte Beschreibung des festgestellten Schadens, betroffene Bauteile, Schadensart und -ausmaß..."
                  value={bewertungData.beurteilung_schadenumfang}
                  onChange={handleBewertungChange}
                  rows={6}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Beschreiben Sie alle beschädigten Teile und deren Zustand detailliert
                </p>
              </div>

              {/* Reparaturkosten */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  2. Reparaturkosten und Kalkulation
                </label>
                <Textarea
                  name="beurteilung_reparaturkosten"
                  placeholder="Kalkulation der Reparaturkosten, benötigte Ersatzteile, Arbeitsaufwand, Stundenverrechnungssätze..."
                  value={bewertungData.beurteilung_reparaturkosten}
                  onChange={handleBewertungChange}
                  rows={6}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Führen Sie die Kostenposition detailliert auf
                </p>
              </div>

              {/* Unfallrekonstruktion */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  3. Unfallhergang / Schadensursache
                </label>
                <Textarea
                  name="beurteilung_unfallrekonstruktion"
                  placeholder="Rekonstruktion des Unfallhergangs basierend auf Spurenlage, Schadenbild und Angaben der Beteiligten..."
                  value={bewertungData.beurteilung_unfallrekonstruktion}
                  onChange={handleBewertungChange}
                  rows={6}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Technische Bewertung des Unfallhergangs
                </p>
              </div>

              {/* Haftungsverteilung */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  4. Haftung und Verschulden
                </label>
                <Textarea
                  name="beurteilung_haftungsverteilung"
                  placeholder="Bewertung der Haftungsfrage, Verschuldensanteile, rechtliche Einschätzung..."
                  value={bewertungData.beurteilung_haftungsverteilung}
                  onChange={handleBewertungChange}
                  rows={5}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Gutachterliche Einschätzung der Haftungsverteilung
                </p>
              </div>

              {/* Zusammenfassung */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  5. Zusammenfassung und Fazit
                </label>
                <Textarea
                  name="beurteilung_zusammenfassung"
                  placeholder="Abschließende gutachterliche Bewertung, Empfehlungen, besondere Hinweise..."
                  value={bewertungData.beurteilung_zusammenfassung}
                  onChange={handleBewertungChange}
                  rows={6}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Fassen Sie die wesentlichen Erkenntnisse zusammen
                </p>
              </div>

              {/* Info Card */}
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-200 rounded-lg p-6">
                <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Gutachterliche Standards
                </h4>
                <ul className="text-sm text-blue-800 space-y-2">
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Objektive, nachvollziehbare Beurteilung aller schadensrelevanten Aspekte</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Berücksichtigung technischer Normen und Herstellervorgaben</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Plausible Begründung der Schadenshöhe und Reparaturnotwendigkeit</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Dokumentation mit Fotos und ggf. Messwerten</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Übersicht Tab */}
        <TabsContent value="uebersicht" className="space-y-6">
          {bewertungData.wiederbeschaffungswert ? (
            <div className="border-0 shadow-lg bg-gradient-to-r from-blue-50 to-blue-100">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-blue-900">Gesamtübersicht Bewertung</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Wiederbeschaffungswert:</span>
                <span className="font-bold text-blue-900 text-lg">
                  {parseFloat(bewertungData.wiederbeschaffungswert).toLocaleString('de-DE')} €
                </span>
              </div>
              {bewertungData.restwert && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">- Restwert:</span>
                  <span className="font-bold text-red-600">
                    - {parseFloat(bewertungData.restwert).toLocaleString('de-DE')} €
                  </span>
                </div>
              )}
              {bewertungData.minderwert_prozent !== '0' && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">+ Minderwert ({bewertungData.minderwert_prozent}%):</span>
                  <span className="font-bold text-orange-600">
                    + {(parseFloat(bewertungData.wiederbeschaffungswert) * parseFloat(bewertungData.minderwert_prozent) / 100).toLocaleString('de-DE')} €
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center pt-3 border-t-2 border-blue-300">
                <span className="text-blue-900 font-bold text-lg">Gesamtschaden:</span>
                <span className="font-bold text-blue-900 text-2xl">
                  {(
                    parseFloat(bewertungData.wiederbeschaffungswert) - 
                    (bewertungData.restwert ? parseFloat(bewertungData.restwert) : 0) +
                    (bewertungData.minderwert_prozent !== '0' ? (parseFloat(bewertungData.wiederbeschaffungswert) * parseFloat(bewertungData.minderwert_prozent) / 100) : 0)
                  ).toLocaleString('de-DE')} €
                </span>
              </div>
            </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-md border border-gray-200">
              <div className="p-12 text-center">
                <BarChart3 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Keine Daten vorhanden</h3>
                <p className="text-gray-500 mb-6">
                  Bitte füllen Sie zunächst die Felder in den Tabs "Restwert" und "Minderwert" aus.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setBewertungTab('restwert')}
                  className="text-blue-600 hover:bg-blue-50"
                >
                  Zu Restwert
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Speichern Button */}
      <div className="flex justify-end space-x-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => setActiveSection('lichtbilder')}
        >
          Zurück
        </Button>
        <Button
          type="button"
          className="bg-gradient-to-r from-blue-500 to-blue-700 text-white"
          onClick={() => {
            toast.success('Gespeichert', {
        description: 'Bewertung wurde gespeichert'
      });
            setActiveSection('gutachten');
          }}
        >
          <Save className="w-4 h-4 mr-2" />
          Speichern & Weiter
        </Button>
      </div>
    </div>
  );

  const renderBeteiligteForm = () => (
    <div className="max-w-full 2xl:max-w-[1600px] mx-auto">
      {/* Modern Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-light text-gray-900 tracking-tight">Versicherungsdaten</h2>
        <p className="text-sm text-gray-400 mt-1">{new Date().toLocaleDateString('de-DE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
      </div>

      {/* Modern Card Container */}
      <div className="space-y-6">
        
        {/* Gegner-Daten Sektion */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
          <div className="px-6 py-4 border-b border-gray-50 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-sm">
                <Users className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Gegner-Daten</h3>
                <p className="text-xs text-gray-400">Unfallgegner & Kontaktdaten</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <div className="lg:col-span-1">
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Name / Firma</label>
                <Input
                  type="text"
                  name="gegner_name"
                  placeholder="Max Mustermann"
                  value={formData.gegner_name || ''}
                  onChange={handleInputChange}
                  className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.gegner_name)}
                />
              </div>
              <div className="relative lg:col-span-2">
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Adresse</label>
                <Input
                  type="text"
                  name="gegner_strasse"
                  placeholder="Straße und Hausnummer"
                  value={formData.gegner_strasse || ''}
                  onChange={handleInputChange}
                  onFocus={() => setSelectedFieldForAddress('gegner_strasse')}
                  className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.gegner_strasse)}
                  autoComplete="off"
                />
                {adressLoading && selectedFieldForAddress === 'gegner_strasse' && (
                  <div className="absolute right-3 top-9 text-gray-400">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                )}
                {showAdressSuggestions && selectedFieldForAddress === 'gegner_strasse' && adressSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-2 bg-white rounded-xl shadow-xl border border-gray-100 max-h-48 overflow-y-auto">
                    {adressSuggestions.map((adresse, index) => (
                      <div key={index} onClick={() => selectAddress(adresse)} className="px-4 py-3 hover:bg-gray-50 cursor-pointer text-sm border-b border-gray-50 last:border-b-0 transition-colors">
                        <div className="font-medium text-gray-800">{adresse.strasse && adresse.hausnummer ? `${adresse.strasse} ${adresse.hausnummer}` : adresse.strasse ? adresse.strasse : adresse.display_name.split(',')[0]}</div>
                        <div className="text-xs text-gray-400 mt-0.5">{adresse.plz} {adresse.ort}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="relative">
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">PLZ / Ort</label>
                <Input
                  type="text"
                  name="gegner_ort"
                  placeholder="10115 Berlin"
                  value={formData.gegner_ort || ''}
                  onChange={handleInputChange}
                  onFocus={() => setSelectedFieldForAddress('gegner_ort')}
                  className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.gegner_ort)}
                  autoComplete="off"
                />
                {showAdressSuggestions && selectedFieldForAddress === 'gegner_ort' && adressSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-2 bg-white rounded-xl shadow-xl border border-gray-100 max-h-48 overflow-y-auto">
                    {adressSuggestions.map((adresse, index) => (
                      <div key={index} onClick={() => selectAddress(adresse)} className="px-4 py-3 hover:bg-gray-50 cursor-pointer text-sm transition-colors">
                        <div className="font-medium text-gray-800">{adresse.plz} {adresse.ort}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Telefon</label>
                <Input
                  type="tel"
                  name="gegner_telefon"
                  placeholder="+49 176 12345678"
                  value={formData.gegner_telefon || ''}
                  onChange={handleInputChange}
                  className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.gegner_telefon)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">E-Mail</label>
                <Input
                  type="email"
                  name="gegner_email"
                  placeholder="email@beispiel.de"
                  value={formData.gegner_email || ''}
                  onChange={handleInputChange}
                  className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.gegner_email)}
                />
              </div>
              <div className="lg:col-span-3">
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Kennzeichen</label>
                <div className="flex gap-3 max-w-sm">
                  <div className="flex items-center bg-gray-50 rounded-lg overflow-hidden flex-1 focus-within:ring-2 focus-within:ring-blue-500/20 transition-all">
                    <div className="w-10 h-11 bg-gradient-to-b from-blue-600 to-blue-700 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-bold">D</span>
                    </div>
                    <input
                      type="text"
                      name="gegner_kennzeichen"
                      placeholder="B-AB 1234"
                      value={formData.gegner_kennzeichen || ''}
                      onChange={handleInputChange}
                      className="flex-1 px-3 py-2.5 text-sm font-semibold tracking-wider uppercase bg-transparent border-none focus:outline-none"
                      style={{ fontFamily: 'ui-monospace, monospace' }}
                    />
                  </div>
                  <Button type="button" onClick={handleGDVLookup} disabled={loading} className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 h-11 rounded-lg shadow-sm hover:shadow transition-all" title="GDV-Abfrage">
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Schadendaten Sektion */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
          <div className="px-6 py-4 border-b border-gray-50 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm">
                <Calendar className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Schadendaten</h3>
                <p className="text-xs text-gray-400">Informationen zum Unfall</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Schadentag</label>
                <Input
                  type="date"
                  name="schadentag"
                  value={formData.schadentag}
                  onChange={handleInputChange}
                  className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.schadentag)}
                />
              </div>
              <div className="relative">
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Schadenort</label>
                <Input
                  type="text"
                  name="schadenort"
                  placeholder="Ort des Unfalls"
                  value={formData.schadenort}
                  onChange={handleInputChange}
                  onFocus={() => setSelectedFieldForAddress('schadenort')}
                  className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.schadenort)}
                  autoComplete="off"
                />
                {showAdressSuggestions && selectedFieldForAddress === 'schadenort' && adressSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-2 bg-white rounded-xl shadow-xl border border-gray-100 max-h-48 overflow-y-auto">
                    {adressSuggestions.map((adresse, index) => (
                      <div key={index} onClick={() => selectAddress(adresse)} className="px-4 py-3 hover:bg-gray-50 cursor-pointer text-sm transition-colors">
                        <div className="font-medium text-gray-800">{adresse.display_name}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Schadennummer</label>
                <Input
                  type="text"
                  name="schadennummer"
                  placeholder="SN-2024-001"
                  value={formData.schadennummer}
                  onChange={handleInputChange}
                  className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.schadennummer)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Anfrageart</label>
                <select
                  name="anfrageart"
                  value={formData.anfrageart}
                  onChange={handleInputChange}
                  className="h-11 w-full px-3 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all cursor-pointer"
                >
                  <option value="Haftpflicht">Haftpflicht</option>
                  <option value="Kasko">Kasko</option>
                  <option value="Privat">Privat</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Versicherung Sektion */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
          <div className="px-6 py-4 border-b border-gray-50 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                <Shield className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Versicherung</h3>
                <p className="text-xs text-gray-400">Haftpflichtversicherung des Gegners</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <div className="lg:col-span-2">
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Versicherungsgesellschaft</label>
                <InsuranceSearchCombobox
                  value={formData.versicherung_gesellschaft || ''}
                  onChange={(value) => setFormData(prev => ({ ...prev, versicherung_gesellschaft: value }))}
                  onInsuranceSelect={(insurance) => {
                    if (insurance) {
                      setFormData(prev => ({
                        ...prev,
                        versicherung_gesellschaft: insurance.name,
                        versicherung_telefon: insurance.phone || prev.versicherung_telefon,
                        versicherung_email: insurance.email || prev.versicherung_email,
                        versicherung_strasse: insurance.street || prev.versicherung_strasse,
                        versicherung_hausnummer: '',
                        versicherung_plz: insurance.plz || prev.versicherung_plz,
                        versicherung_ort: insurance.city || prev.versicherung_ort,
                        versicherung_logo: insurance.logo_url || prev.versicherung_logo
                      }));
                      toast.success(`${insurance.name} ausgewählt`);
                    } else {
                      setFormData(prev => ({ ...prev, versicherung_gesellschaft: '' }));
                    }
                  }}
                  className={getInputClassName(formData.versicherung_gesellschaft)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Versicherungsnummer</label>
                <Input
                  type="text"
                  name="versicherung_nummer"
                  placeholder="VS-123456789"
                  value={formData.versicherung_nummer || ''}
                  onChange={handleInputChange}
                  className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.versicherung_nummer)}
                />
              </div>
              <div className="lg:col-span-2">
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Straße & Hausnummer</label>
                <Input
                  type="text"
                  name="versicherung_strasse"
                  placeholder="Musterstraße 123"
                  value={formData.versicherung_strasse || ''}
                  onChange={handleInputChange}
                  className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.versicherung_strasse)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">PLZ & Stadt</label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    name="versicherung_plz"
                    placeholder="10115"
                    value={formData.versicherung_plz || ''}
                    onChange={handleInputChange}
                    className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all w-24 " + getInputClassName(formData.versicherung_plz)}
                  />
                  <Input
                    type="text"
                    name="versicherung_ort"
                    placeholder="Berlin"
                    value={formData.versicherung_ort || ''}
                    onChange={handleInputChange}
                    className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all flex-1 " + getInputClassName(formData.versicherung_ort)}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Telefon</label>
                <Input
                  type="tel"
                  name="versicherung_telefon"
                  placeholder="+49 800 1234567"
                  value={formData.versicherung_telefon || ''}
                  onChange={handleInputChange}
                  className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.versicherung_telefon)}
                />
              </div>
              <div className="lg:col-span-2">
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">E-Mail</label>
                <Input
                  type="email"
                  name="versicherung_email"
                  placeholder="schaden@versicherung.de"
                  value={formData.versicherung_email || ''}
                  onChange={handleInputChange}
                  className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.versicherung_email)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modern Navigation Buttons */}
      <div className="flex justify-end gap-3 mt-8">
        <Button type="button" variant="outline" onClick={() => setActiveSection('akte')} className="text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg px-5 transition-all">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>
        <Button type="button" onClick={() => setActiveSection('abtretung')} className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg px-6 shadow-md hover:shadow-lg transition-all">
          Weiter
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );

  const renderOldBeteiligteForm = () => (
    <div className="hidden">
      {/* Alte komplexe Form - versteckt */}
      {showBeteiligterForm && (
        <div className="border-2 border-blue-200 shadow-lg">
          <div className="px-4 py-3 bg-blue-50 border-b border-blue-100">
            <h3 className="text-blue-900">Neuen Beteiligten hinzufügen</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Beteiligung <span className="text-red-500">*</span>
                </label>
                <select className="h-10"
                  value={newBeteiligter.beteiligung}
                  onChange={(e) => setNewBeteiligter({ ...newBeteiligter, beteiligung: e.target.value })}
                  className="w-full h-12 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Fahrzeughalter">Fahrzeughalter</option>
                  <option value="Versicherung">Versicherung</option>
                  <option value="Werkstatt">Werkstatt</option>
                  <option value="Gutachter">Gutachter</option>
                  <option value="Rechtsanwalt">Rechtsanwalt</option>
                  <option value="Sonstige">Sonstige</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kontakttyp
                </label>
                <select className="h-10"
                  value={newBeteiligter.kontakttyp}
                  onChange={(e) => setNewBeteiligter({ ...newBeteiligter, kontakttyp: e.target.value })}
                  className="w-full h-12 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Person">Person</option>
                  <option value="Unternehmen">Unternehmen</option>
                  <option value="Behörde">Behörde</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bezeichnung / Name <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  placeholder="z.B. Max Mustermann oder Firma GmbH"
                  value={newBeteiligter.bezeichnung}
                  onChange={(e) => setNewBeteiligter({ ...newBeteiligter, bezeichnung: e.target.value })}
                  className="h-12"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PLZ
                </label>
                <Input
                  type="text"
                  placeholder="12345"
                  value={newBeteiligter.plz}
                  onChange={(e) => setNewBeteiligter({ ...newBeteiligter, plz: e.target.value })}
                  className="h-12"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ort
                </label>
                <Input
                  type="text"
                  placeholder="Stadt"
                  value={newBeteiligter.ort}
                  onChange={(e) => setNewBeteiligter({ ...newBeteiligter, ort: e.target.value })}
                  className="h-12"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-Mail
                </label>
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={newBeteiligter.email}
                  onChange={(e) => setNewBeteiligter({ ...newBeteiligter, email: e.target.value })}
                  className="h-12"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefon
                </label>
                <Input
                  type="tel"
                  placeholder="+49 123 456789"
                  value={newBeteiligter.telefon}
                  onChange={(e) => setNewBeteiligter({ ...newBeteiligter, telefon: e.target.value })}
                  className="h-12"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowBeteiligterForm(false)}
              >
                Abbrechen
              </Button>
              <Button
                type="button"
                onClick={handleAddBeteiligter}
                className="bg-gradient-to-r from-blue-500 to-blue-700 text-white"
              >
                Hinzufügen
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderBesichtigungenForm = () => (
    <div className="max-w-full 2xl:max-w-[1500px] mx-auto space-y-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
          <h2 className="text-xl font-semibold">Besichtigungen</h2>
        </div>
        <p className="text-sm text-gray-500 mt-1">Planen und verwalten Sie Fahrzeugbesichtigungen</p>
      </div>

      {!showBesichtigungForm && (
        <>
          {/* Kalenderansicht Button (optional) */}
          <div className="flex justify-start">
            <Button
              type="button"
              variant="outline"
              className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
              onClick={() => {
                toast.success('Feature kommt bald', {
        description: 'Kalenderansicht wird demnächst verfügbar sein'
      });
              }}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Kalenderansicht
            </Button>
          </div>

          {/* Tabelle */}
          <div className="border-0 shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <span>Datum</span>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                    </svg>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <span>Uhrzeit</span>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                    </svg>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <span>Ort</span>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                    </svg>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Gutachter
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                    </svg>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notizen
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aktion
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {besichtigungen.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-32 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <svg className="w-32 h-32 text-gray-200" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" opacity="0.3"/>
                      </svg>
                      <p className="text-gray-400 text-lg mt-4">Keine Besichtigungen geplant</p>
                      <p className="text-gray-400 text-sm">Fügen Sie einen Termin hinzu</p>
                    </div>
                  </td>
                </tr>
              ) : (
                besichtigungen.map((besichtigung) => (
                  <tr key={besichtigung.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {besichtigung.vonDatum ? new Date(besichtigung.vonDatum).toLocaleDateString('de-DE') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {besichtigung.vonUhrzeit || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {besichtigung.adresse || besichtigung.ortFirma || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {besichtigung.sachverstaendiger || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        Geplant
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900  truncate">
                      {besichtigung.bemerkung || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          onClick={() => handleEditBesichtigung(besichtigung)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Bearbeiten"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteBesichtigung(besichtigung.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Löschen"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

          {/* Bottom Navigation */}
          <div className="flex items-center justify-between pt-6 border-t">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setActiveSection('beteiligte')}
              className="text-blue-600 hover:bg-blue-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück
            </Button>
            <Button
              type="button"
              onClick={() => setShowBesichtigungForm(true)}
              className="bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Neue Besichtigung
            </Button>
          </div>
        </>
      )}

      {/* Detailliertes Besichtigungs-Formular */}
      {showBesichtigungForm && (
        <div className="bg-white rounded-md border border-gray-200">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h3 className="text-gray-900 flex items-center space-x-2">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span>Besichtigung {besichtigungen.length + 1}</span>
              </h3>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowBesichtigungForm(false);
                  setEditingBesichtigung(null);
                }}
                className="text-red-600 hover:bg-red-50"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </Button>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Linke Spalte - Formular */}
              <div className="lg:col-span-2 space-y-6">
                {/* Besichtigungsort & Teilnehmer */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Besichtigungsort <span className="text-red-500">*</span>
                    </label>
                    <select className="h-10"
                      value={newBesichtigung.besichtigungsort}
                      onChange={(e) => handleBesichtigungChange('besichtigungsort', e.target.value)}
                      className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="">Auswählen...</option>
                      <option value="Werkstatt">Werkstatt</option>
                      <option value="Kundenwohnort">Kundenwohnort</option>
                      <option value="Büro">Büro</option>
                      <option value="Sonstige">Sonstige</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Besichtigungsbedingungen
                    </label>
                    <select
                      value={newBesichtigung.besichtigungsbedingungen}
                      onChange={(e) => {
                        const value = e.target.value;
                        handleBesichtigungChange('besichtigungsbedingungen', value);
                        handleBesichtigungChange('dokumentationstext', besichtigungsTexte[value] || '');
                      }}
                      className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="">Auswählen...</option>
                      <option value="ausreichend">ausreichend</option>
                      <option value="ausreichend_unten">ausreichend, von unten</option>
                      <option value="ausreichend_nachbesichtigt">ausreichend, nachbesichtigt</option>
                      <option value="eingeschraenkt">eingeschränkt</option>
                      <option value="eingeschraenkt_unbeweglich">eingeschränkt, unbeweglich</option>
                      <option value="eingeschraenkt_wetter">eingeschränkt, Wetter</option>
                      <option value="eingeschraenkt_verschmutzung">eingeschränkt, Verschmutzung</option>
                      <option value="unzureichend_totalschaden">unzureichend, Totalschaden</option>
                    </select>
                  </div>
                </div>

                {/* Dokumentationstext (automatisch generiert) */}
                {newBesichtigung.dokumentationstext && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <label className="block text-sm font-medium text-blue-800 mb-2">
                      📋 Dokumentation (automatisch generiert)
                    </label>
                    <Textarea
                      value={newBesichtigung.dokumentationstext}
                      onChange={(e) => handleBesichtigungChange('dokumentationstext', e.target.value)}
                      className="min-h-[120px] bg-white border-blue-300 text-gray-700"
                      placeholder="Der Dokumentationstext wird automatisch basierend auf den Besichtigungsbedingungen generiert..."
                    />
                  </div>
                )}

                {/* Teilnehmer & Weitere Teilnehmer */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Teilnehmer
                    </label>
                    <select className="h-10"
                      value={newBesichtigung.teilnehmer}
                      onChange={(e) => handleBesichtigungChange('teilnehmer', e.target.value)}
                      className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="">Auswählen...</option>
                      <option value="Kunde">Kunde</option>
                      <option value="Versicherung">Versicherung</option>
                      <option value="Werkstatt">Werkstatt</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Weitere Teilnehmer
                    </label>
                    <Input
                      type="text"
                      placeholder="Weitere Personen"
                      value={newBesichtigung.weitereTeilnehmer}
                      onChange={(e) => handleBesichtigungChange('weitereTeilnehmer', e.target.value)}
                      className="h-10"
                    />
                  </div>
                </div>

                {/* Adresse & Externer Sachverständiger */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Adresse <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Input
                        type="text"
                        placeholder="Straße, PLZ, Ort"
                        value={newBesichtigung.adresse}
                        onChange={(e) => handleBesichtigungChange('adresse', e.target.value)}
                        className="h-10 pr-10"
                      />
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-blue-600"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Externer Sachverständiger
                    </label>
                    <select className="h-10"
                      value={newBesichtigung.externerSachverstaendiger}
                      onChange={(e) => handleBesichtigungChange('externerSachverstaendiger', e.target.value)}
                      className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="">Keiner</option>
                      <option value="Externer Gutachter 1">Externer Gutachter 1</option>
                      <option value="Externer Gutachter 2">Externer Gutachter 2</option>
                    </select>
                  </div>
                </div>

                {/* Von & Bis Datum/Zeit */}
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Von <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2">
                        <Input
                          type="date"
                          value={newBesichtigung.vonDatum}
                          onChange={(e) => handleBesichtigungChange('vonDatum', e.target.value)}
                          className="h-10 flex-1"
                        />
                        <Input
                          type="time"
                          value={newBesichtigung.vonUhrzeit}
                          onChange={(e) => handleBesichtigungChange('vonUhrzeit', e.target.value)}
                          className="h-10 w-32"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Bis
                      </label>
                      <div className="flex gap-2">
                        <Input
                          type="date"
                          value={newBesichtigung.bisDatum}
                          onChange={(e) => handleBesichtigungChange('bisDatum', e.target.value)}
                          className="h-10 flex-1"
                        />
                        <Input
                          type="time"
                          value={newBesichtigung.bisUhrzeit}
                          onChange={(e) => handleBesichtigungChange('bisUhrzeit', e.target.value)}
                          className="h-10 w-32"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Ersteller & Sachverständiger */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ersteller <span className="text-red-500">*</span>
                    </label>
                    <select className="h-10"
                      value={newBesichtigung.ersteller}
                      onChange={(e) => handleBesichtigungChange('ersteller', e.target.value)}
                      className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="Herr Özer Kanatli">Herr Özer Kanatli</option>
                      <option value="Max Gutachter">Max Gutachter</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sachverständiger
                    </label>
                    <select className="h-10"
                      value={newBesichtigung.sachverstaendiger}
                      onChange={(e) => handleBesichtigungChange('sachverstaendiger', e.target.value)}
                      className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="Herr Özer Kanatli">Herr Özer Kanatli</option>
                      <option value="Max Gutachter">Max Gutachter</option>
                    </select>
                  </div>
                </div>

                {/* Checkboxen */}
                <div className="space-y-4">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newBesichtigung.teilnehmerBenachrichtigen}
                      onChange={(e) => handleBesichtigungChange('teilnehmerBenachrichtigen', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="text-sm text-gray-700">Teilnehmer benachrichtigen</span>
                  </label>
                </div>

                {/* Bemerkung */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bemerkung
                  </label>
                  <Textarea
                    placeholder="Zusätzliche Informationen zur Besichtigung..."
                    value={newBesichtigung.bemerkung}
                    onChange={(e) => handleBesichtigungChange('bemerkung', e.target.value)}
                    rows={6}
                    className="text-sm"
                  />
                </div>

                {/* Erinnerung aktivieren */}
                <div>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newBesichtigung.erinnerungAktivieren}
                      onChange={(e) => handleBesichtigungChange('erinnerungAktivieren', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="text-sm text-gray-700">Erinnerung aktivieren</span>
                  </label>
                </div>
              </div>

              {/* Rechte Spalte - Karte & Info */}
              <div className="space-y-4">
                {/* Karte Placeholder */}
                <div className="border-2 border-gray-200 overflow-hidden">
                  <div className="bg-gray-100 p-2 border-b flex items-center justify-center space-x-2">
                    <button className="px-3 py-1 text-sm bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50">
                      Karte
                    </button>
                    <button className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-200 rounded">
                      Satellit
                    </button>
                  </div>
                  <div className="h-64 bg-gradient-to-br from-blue-100 to-green-100 flex items-center justify-center">
                    <div className="text-center text-gray-500">
                      <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <p className="text-sm">Karte wird geladen...</p>
                      <p className="text-xs mt-1">Adresse eingeben für Standort</p>
                    </div>
                  </div>
                </div>

                {/* Entfernung & Fahrtzeit */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-semibold text-gray-700">
                      🚗 Entfernung & Fahrtzeit
                    </label>
                    <Button
                      type="button"
                      onClick={calculateDistance}
                      disabled={distanceLoading}
                      variant="outline"
                      size="sm"
                      className="text-blue-600 border-blue-400 hover:bg-blue-50"
                    >
                      {distanceLoading ? (
                        <>
                          <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Berechne...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Neu berechnen
                        </>
                      )}
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Entfernung
                      </label>
                      <Input
                        type="text"
                        placeholder="Automatisch berechnet"
                        value={newBesichtigung.entfernung}
                        onChange={(e) => handleBesichtigungChange('entfernung', e.target.value)}
                        className="h-10 bg-green-50 border-green-300"
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Fahrtzeit
                      </label>
                      <Input
                        type="text"
                        placeholder="Automatisch berechnet"
                        value={newBesichtigung.fahrtzeit}
                        onChange={(e) => handleBesichtigungChange('fahrtzeit', e.target.value)}
                        className="h-10 bg-green-50 border-green-300"
                        readOnly
                      />
                    </div>
                  </div>
                  
                  {/* Google Maps Karte */}
                  {mapUrl && newBesichtigung.entfernung && (
                    <div className="rounded-lg overflow-hidden border-2 border-blue-300 shadow-lg">
                      <div className="bg-blue-600 text-white px-3 py-2 text-sm font-semibold">
                        📍 Route: Büro → Kunde
                      </div>
                      <iframe
                        width="100%"
                        height="350"
                        style={{ border: 0 }}
                        loading="lazy"
                        allowFullScreen
                        referrerPolicy="no-referrer-when-downgrade"
                        src={mapUrl}
                        title="Google Maps Route"
                      />
                    </div>
                  )}
                  
                  <div className="bg-green-50 border border-green-200 rounded p-2">
                    <p className="text-xs text-green-700">
                      ✅ Die Entfernung wird automatisch von Ihrem Büro zum Kunden berechnet
                    </p>
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">💡 Hinweis</h4>
                  <p className="text-xs text-blue-700">
                    Alle Teilnehmer erhalten automatisch eine E-Mail-Benachrichtigung, wenn Sie die Option aktivieren.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between mt-6 pt-6 border-t">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setShowBesichtigungForm(false);
                  setEditingBesichtigung(null);
                }}
                className="text-blue-600 hover:bg-blue-50"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zurück
              </Button>

              <div className="flex items-center space-x-3">
                {besichtigungen.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      // Reset and stay in form for additional inspection
                      setNewBesichtigung({
                        besichtigungsort: '',
                        besichtigungsbedingungen: '',
                        dokumentationstext: '',
                        teilnehmer: '',
                        ortFirma: '',
                        weitereTeilnehmer: '',
                        adresse: '',
                        externerSachverstaendiger: '',
                        vonDatum: '',
                        vonUhrzeit: '',
                        bisDatum: '',
                        bisUhrzeit: '',
                        ersteller: 'Herr Özer Kanatli',
                        sachverstaendiger: 'Herr Özer Kanatli',
                        teilnehmerBenachrichtigen: false,
                        bemerkung: '',
                        erinnerungAktivieren: false,
                        entfernung: '',
                        fahrtzeit: ''
                      });
                      setEditingBesichtigung(null);
                      toast.success('Formular zurückgesetzt', {
        description: 'Bereit für neue Besichtigung'
      });
                    }}
                    className="bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700"
                  >
                    Weitere Besichtigung
                  </Button>
                )}
                
                <Button
                  type="button"
                  onClick={handleAddBesichtigung}
                  className="bg-gradient-to-r from-blue-500 to-blue-700 text-white"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Speichern
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderGutachtenForm = () => (
    <div className="max-w-full 2xl:max-w-[1500px] mx-auto">
      <GutachtenFormPDF
        formData={formData}
        gutachtenData={gutachtenData}
        setGutachtenData={setGutachtenData}
        onBack={() => setActiveSection('dokumente')}
        onSave={() => {
          toast.success('Speichern', {
          description: 'Gutachten-Daten wurden gespeichert'
        });
        }}
        onGeneratePDF={() => {
          const gutachtenTypLabel = formData.auftragstyp === 'Gutachten' ? 'Haftpflichtgutachten' : 
                                    formData.auftragstyp === 'Kasko' ? 'Kasko-Gutachten' : 
                                    formData.auftragstyp || 'Gutachten';
          toast.success('PDF wird generiert', {
          description: `Ihr ${gutachtenTypLabel} wird als PDF erstellt...`
        });
          // TODO: Backend-API call to generate PDF
        }}
      />
    </div>
  );

  const renderEmailForm = () => (
    <div className="max-w-full 2xl:max-w-[1500px] mx-auto space-y-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3">
          <Mail className="w-6 h-6" />
          <h2 className="text-xl font-semibold">Email versenden</h2>
        </div>
        <p className="text-sm text-gray-500 mt-1">Gutachten und Dokumente per Email versenden</p>
      </div>

      {/* Email Form */}
      <div className="bg-white rounded-md border border-gray-200">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
          <h3 className="text-gray-900">Email-Daten</h3>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              Empfänger *
            </label>
            <Input
              type="email"
              placeholder="empfaenger@beispiel.de"
              value={emailData.empfaenger}
              onChange={(e) => setEmailData({ ...emailData, empfaenger: e.target.value })}
              className={getInputClassName(emailData.empfaenger)}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              CC (optional)
            </label>
            <Input
              type="email"
              placeholder="cc@beispiel.de"
              value={emailData.cc}
              onChange={(e) => setEmailData({ ...emailData, cc: e.target.value })}
              className={getInputClassName(emailData.cc)}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              Betreff *
            </label>
            <Input
              type="text"
              placeholder="Gutachten - Fall Nr. ..."
              value={emailData.betreff}
              onChange={(e) => setEmailData({ ...emailData, betreff: e.target.value })}
              className={getInputClassName(emailData.betreff)}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              Nachricht *
            </label>
            <textarea
              rows={8}
              placeholder="Ihre Nachricht..."
              value={emailData.nachricht}
              onChange={(e) => setEmailData({ ...emailData, nachricht: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Anhänge</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="attach-gutachten" className="rounded" defaultChecked />
                <label htmlFor="attach-gutachten">Gutachten (PDF)</label>
              </div>
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="attach-lichtbilder" className="rounded" />
                <label htmlFor="attach-lichtbilder">Lichtbilder</label>
              </div>
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="attach-dokumente" className="rounded" />
                <label htmlFor="attach-dokumente">Weitere Dokumente</label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between space-x-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => setActiveSection('gutachten')}
        >
          Zurück
        </Button>
        <Button
          type="button"
          className="bg-gradient-to-r from-blue-500 to-blue-700 text-white"
          onClick={() => {
            if (!emailData.empfaenger || !emailData.betreff || !emailData.nachricht) {
              toast.success('Fehlende Daten', {
        description: 'Bitte füllen Sie alle Pflichtfelder aus'
      });
              return;
            }
            toast.error('Email wird versendet', {
        description: 'Ihre Email wird gerade versendet...'
            });
            // TODO: Backend-API call to send email
          }}
        >
          <Mail className="w-4 h-4 mr-2" />
          Email senden
        </Button>
      </div>
    </div>
  );

  // Header-Komponente für alle Seiten
  // Hilfsfunktion: Versicherungslogo automatisch laden
  const getVersicherungsLogo = (versicherungsname) => {
    if (!versicherungsname) return null;
    
    // Falls bereits ein Logo hochgeladen wurde
    if (formData.versicherung_logo) {
      return formData.versicherung_logo;
    }
    
    // Bereinige Versicherungsname für Backend-Lookup
    const versicherungNameClean = versicherungsname.toLowerCase()
      .replace(/\s+/g, '')
      .replace('versicherung', '')
      .replace('ag', '')
      .replace('se', '')
      .trim();
    
    // Versuche zuerst vom Backend zu laden (falls hochgeladen)
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const backendLogoUrl = `${BACKEND_URL}/api/versicherungslogos/logo/${versicherungNameClean}`;
    
    // Bekannte Versicherungen mit Domain-Mapping für Clearbit
    const versicherungDomains = {
      'allianz': 'allianz.de',
      'huk': 'huk.de',
      'hukcoburg': 'huk.de',
      'devk': 'devk.de',
      'ergo': 'ergo.de',
      'axa': 'axa.de',
      'signal': 'signal-iduna.de',
      'signalid': 'signal-iduna.de',
      'r+v': 'ruv.de',
      'rv': 'ruv.de',
      'vgh': 'vgh.de',
      'sparkasse': 'sparkassenversicherung.de',
      'debeka': 'debeka.de',
      'gothaer': 'gothaer.de',
      'provinzial': 'provinzial.de',
      'lvm': 'lvm.de',
      'cosmos': 'cosmos.de',
      'direct': 'directline.de',
      'volkswagen': 'volkswagen-versicherung.de',
      'vw': 'volkswagen-versicherung.de',
      'zurich': 'zurich.de',
      'zürich': 'zurich.de',
      'generali': 'generali.de',
      'württembergische': 'wuerttembergische.de',
      'wuerttembergische': 'wuerttembergische.de'
    };
    
    const domain = versicherungDomains[versicherungNameClean] || `${versicherungNameClean}.de`;
    const clearbitLogoUrl = `https://logo.clearbit.com/${domain}`;
    
    // Zurückgeben: Versuche zuerst Clearbit (schneller), falls Fehler wird onError handler Backend versuchen
    return clearbitLogoUrl;
  };

  const DocumentHeader = () => {
    return (
      <div className="bg-white">
        {/* Header mit Logo und Slogan zentriert */}
        <div className="px-8 pt-8 pb-6">
          <div className="flex flex-col justify-center items-center">
            {/* Logo zentriert und größer */}
            <img 
              src="/logo-new.png" 
              alt="Auto Gutachter24" 
              className="h-28 mb-0.5"
              style={{ width: 'auto' }}
            />
            {/* Slogan bis an die rechte Kante vom Logo */}
            <div className="w-full flex justify-center">
              <div className="relative" style={{ width: 'auto' }}>
                <p className="text-gray-500 text-sm italic tracking-wide font-light text-right">
                  Ihr Partner bei Autounfällen
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const DocumentFooter = ({ pageNumber }) => {
    // Profil-Daten aus localStorage holen
    const getUserData = () => {
      const storedUser = localStorage.getItem('autogutachter24_user');
      if (storedUser) {
        return JSON.parse(storedUser);
      }
      return {};
    };
    
    const userData = getUserData();
    
    return (
      <div className="bg-white border-t border-gray-300 w-full document-footer" style={{ position: 'absolute', bottom: '0', left: '0', right: '0' }}>
        <div className="px-6 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-xs mb-1">
            {/* Linke Spalte - Adressdaten */}
            <div className="flex flex-col justify-start space-y-0.5">
              <p className="font-semibold text-gray-700 text-xs">
                {userData.vorname && userData.nachname 
                  ? `${userData.vorname} ${userData.nachname}${userData.rolle === 'admin' ? ' (Admin)' : ''}` 
                  : userData.firma || 'Admin Demo'}
              </p>
              <p className="text-gray-600 text-[10px]">
                {userData.strasse || 'Musterstraße'} {userData.hausnummer || '123'}
              </p>
              <p className="text-gray-600 text-[10px]">
                {userData.plz || '80331'} {userData.ort || 'München'}, Deutschland
              </p>
              {pageNumber && (
                <p className="text-gray-500 font-medium text-xs mt-1">Seite {pageNumber}</p>
              )}
            </div>

            {/* Mittlere Spalte - Kontaktdaten (kein Logo mehr) */}
            <div className="flex flex-col justify-start space-y-0.5 items-center text-center">
              <p className="text-gray-600 text-[10px]">{userData.email || 'info@autogutachter24.de'}</p>
              <p className="text-gray-600 text-[10px]">{userData.website || 'www.autogutachter24.com'}</p>
              <p className="text-gray-600 text-[10px]">Tel: {userData.telefon || '+49 89 1234567'}</p>
              <p className="text-gray-600 text-[10px]">Mobil: {userData.mobil || '+49 170 1234567'}</p>
            </div>

            {/* Rechte Spalte - Bankdaten */}
            <div className="flex flex-col justify-start space-y-0.5 text-right">
              <p className="text-gray-600 text-[10px]">{userData.bankname || 'Sparkasse München'}</p>
              <p className="text-gray-600 text-[10px]">IBAN: {userData.iban || 'DE89 3704 0044 0532 0130 00'}</p>
              <p className="text-gray-600 text-[10px]">BIC: {userData.bic || 'COBADEFFXXX'}</p>
              <p className="text-gray-600 text-[10px]">UST-ID: {userData.ust_id || 'DE123456789'}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };


  // renderLeerForm - Jetzt als Wrapper für die extrahierte LeerFormContent Komponente
  const renderLeerForm = () => (
    <LeerFormContent 
      formData={{
        ...formData, 
        lichtbilder: lichtbilder,
        // Erste Besichtigung für Besichtigungsdaten verwenden
        besichtigungsdatum: besichtigungen?.[0]?.vonDatum || formData.besichtigungsdatum,
        besichtigungsort: besichtigungen?.[0]?.besichtigungsort || formData.besichtigungsort,
      }} 
      aktenzeichen={aktenzeichen}
      getVersicherungsLogo={getVersicherungsLogo}
    />
  );

  // renderLeer2Form wurde entfernt

  const renderSignatureForm = () => (
    <div className="max-w-full 2xl:max-w-[1500px] mx-auto">
      {/* Header im Auftrag-Stil */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white flex items-center justify-center font-bold text-xl mr-4 shadow-md">3</div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Abtretung</h2>
            <p className="text-sm text-gray-500">Abtretungserklärung und Honorarvereinbarung</p>
          </div>
        </div>
      </div>

      {/* Unterschrift Sektion */}
      <div className="bg-gray-50 rounded-lg p-5 mb-6">
        <h3 className="text-sm font-bold text-blue-700 uppercase tracking-wide mb-4 pb-2 border-b-2 border-blue-200">Digitale Unterschrift</h3>
        
        <>
          {signatureImage ? (
            <div className="bg-white rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3 text-green-700">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">Unterschrift vorhanden</span>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                <img src={signatureImage} alt="Unterschrift" className="max-h-24 mx-auto" />
              </div>
              <div className="flex gap-3">
                <Button type="button" variant="outline" size="sm" onClick={handleClearSignature} className="text-red-600 border-red-200 hover:bg-red-50">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Löschen
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowSignaturePad(true)} className="text-blue-600 border-blue-200 hover:bg-blue-50">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  Neu zeichnen
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button type="button" variant="outline" onClick={() => setShowSignaturePad(true)} className="h-20 border-2 border-dashed border-blue-300 text-blue-600 hover:bg-blue-50 bg-white">
                <div className="text-center">
                  <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  <span className="text-sm font-medium">Zeichnen</span>
                </div>
              </Button>
              
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleSignatureUpload}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div className="h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-50">
                  <div className="text-center">
                    <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span className="text-sm font-medium">Hochladen</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Signature Pad Modal */}
          {showSignaturePad && (
            <div className="bg-slate-50 rounded-lg p-5 mb-6">
              <h3 className="text-sm font-bold text-blue-700 uppercase tracking-wide mb-4 pb-2 border-b-2 border-blue-200">Unterschrift zeichnen</h3>
              <div className="bg-white rounded-lg p-4">
                <canvas
                  ref={canvasRef}
                  width={800}
                  height={250}
                  className="border border-gray-200 rounded-lg cursor-crosshair w-full"
                  style={{ touchAction: 'none', maxWidth: '800px', height: 'auto' }}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
                <div className="flex justify-between mt-4">
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={clearCanvas} className="text-gray-600">Löschen</Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => setShowSignaturePad(false)} className="text-gray-600">Abbrechen</Button>
                  </div>
                  <Button
                    type="button"
                    onClick={saveSignature}
                    className="bg-gradient-to-r from-blue-500 to-blue-700 text-white"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Speichern
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-6 border-t">
        <Button
          type="button"
          variant="ghost"
          onClick={() => setActiveSection('abtretung')}
          className="text-blue-600 hover:bg-blue-50"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>
        <Button
          type="button"
          onClick={() => setActiveSection('kalkulation')}
          className="bg-gradient-to-r from-blue-500 to-blue-700 text-white"
        >
          Weiter zu Kalkulation
          <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Button>
      </div>
    </div>
  );
  const renderUnterschriftForm = () => (
    <div className="max-w-full 2xl:max-w-[1500px] mx-auto space-y-6">

      {/* Header im Auftrag-Stil */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white flex items-center justify-center font-bold text-xl mr-4 shadow-md">3</div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Abtretung</h2>
            <p className="text-sm text-gray-500">Abtretungserklärung und Honorarvereinbarung</p>
          </div>
        </div>
      </div>

      {/* 1. Auftraggeber (Geschädigter) - Graue Karte ohne Rand */}
      <div className="bg-gray-50 rounded-lg p-5 mb-5">
        <h3 className="text-sm font-bold text-blue-700 uppercase tracking-wide mb-4 pb-2 border-b-2 border-blue-200">1. Auftraggeber (Geschädigter)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Name</label>
            <Input
              type="text"
              name="auftraggeber_name"
              value={formData.auftraggeber_name || formData.kunde || ''}
              onChange={handleInputChange}
              placeholder="Name"
              className={`h-10 text-sm border-gray-200 rounded-lg bg-white ${(formData.auftraggeber_name || formData.kunde) ? 'bg-filled-field' : ''}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Straße</label>
            <Input
              type="text"
              name="auftraggeber_strasse"
              value={formData.auftraggeber_strasse || formData.adresse1 || ''}
              onChange={handleInputChange}
              placeholder="Straße"
              className={`h-10 text-sm border-gray-200 rounded-lg bg-white ${(formData.auftraggeber_strasse || formData.adresse1) ? 'bg-filled-field' : ''}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">PLZ / Ort</label>
            <Input
              type="text"
              name="auftraggeber_ort"
              value={formData.auftraggeber_ort || formData.adresse2 || ''}
              onChange={handleInputChange}
              placeholder="PLZ Ort"
              className={`h-10 text-sm border-gray-200 rounded-lg bg-white ${(formData.auftraggeber_ort || formData.adresse2) ? 'bg-filled-field' : ''}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Amtl. Kennzeichen</label>
            <Input
              type="text"
              name="auftraggeber_kennzeichen"
              value={formData.auftraggeber_kennzeichen || formData.kennzeichen || ''}
              onChange={handleInputChange}
              placeholder="Kennzeichen"
              className={`h-10 text-sm border-gray-200 rounded-lg bg-white ${(formData.auftraggeber_kennzeichen || formData.kennzeichen) ? 'bg-filled-field' : ''}`}
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-500 mb-2">Vorsteuerabzugsberechtigt</label>
          <div className="flex items-center space-x-6">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input type="radio" name="vorsteuerabzug" value="ja" checked={formData.vorsteuerabzug === 'ja'} onChange={handleInputChange} className="w-4 h-4 text-blue-600" />
              <span className={`text-sm font-medium px-2 py-0.5 rounded ${formData.vorsteuerabzug === 'ja' ? 'bg-green-100 text-green-700' : 'text-gray-700'}`}>Ja</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input type="radio" name="vorsteuerabzug" value="nein" checked={formData.vorsteuerabzug === 'nein'} onChange={handleInputChange} className="w-4 h-4 text-blue-600" />
              <span className={`text-sm font-medium px-2 py-0.5 rounded ${formData.vorsteuerabzug === 'nein' ? 'bg-gray-200 text-gray-600' : 'text-gray-700'}`}>Nein</span>
            </label>
          </div>
        </div>
      </div>

      {/* 2. Versicherter (Gegner) - Graue Karte ohne Rand */}
      <div className="bg-slate-50 rounded-lg p-5 mb-5">
        <h3 className="text-sm font-bold text-red-600 uppercase tracking-wide mb-4 pb-2 border-b-2 border-red-200">2. Versicherter (Unfallgegner)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Name</label>
            <Input
              type="text"
              name="gegner_name"
              value={formData.gegner_name || ''}
              onChange={handleInputChange}
              placeholder="Name"
              className={`h-10 text-sm border-gray-200 rounded-lg bg-white ${formData.gegner_name ? 'bg-filled-field' : ''}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Straße</label>
            <Input
              type="text"
              name="gegner_strasse"
              value={formData.gegner_strasse || ''}
              onChange={handleInputChange}
              placeholder="Straße"
              className={`h-10 text-sm border-gray-200 rounded-lg bg-white ${formData.gegner_strasse ? 'bg-filled-field' : ''}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">PLZ / Ort</label>
            <Input
              type="text"
              name="gegner_ort"
              value={formData.gegner_ort || ''}
              onChange={handleInputChange}
              placeholder="PLZ Ort"
              className={`h-10 text-sm border-gray-200 rounded-lg bg-white ${formData.gegner_ort ? 'bg-filled-field' : ''}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Amtl. Kennzeichen</label>
            <Input
              type="text"
              name="gegner_kennzeichen"
              value={formData.gegner_kennzeichen || ''}
              onChange={handleInputChange}
              placeholder="Kennzeichen"
              className={`h-10 text-sm border-gray-200 rounded-lg bg-white ${formData.gegner_kennzeichen ? 'bg-filled-field' : ''}`}
            />
          </div>
        </div>
      </div>

      {/* 3. Versicherung - Graue Karte ohne Rand */}
      <div className="bg-gray-50 rounded-lg p-5 mb-5">
        <h3 className="text-sm font-bold text-purple-700 uppercase tracking-wide mb-4 pb-2 border-b-2 border-purple-200">3. Gegnerische Versicherung</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Versicherungsname</label>
            <Input
              type="text"
              name="versicherungsname"
              value={formData.versicherung_gesellschaft || formData.versicherungsname || ''}
              onChange={handleInputChange}
              placeholder="Name der Versicherung"
              className={`h-10 text-sm border-gray-200 rounded-lg bg-white ${(formData.versicherung_gesellschaft || formData.versicherungsname) ? 'bg-filled-field' : ''}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Schadentag</label>
            <Input
              type="date"
              name="schadentag"
              value={formData.schadentag || ''}
              onChange={handleInputChange}
              className={`h-10 text-sm border-gray-200 rounded-lg bg-white ${formData.schadentag ? 'bg-filled-field' : ''}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Versicherungsscheinnr.</label>
            <Input
              type="text"
              name="versicherungsscheinnummer"
              value={formData.versicherung_nummer || formData.versicherungsscheinnummer || ''}
              onChange={handleInputChange}
              placeholder="Versicherungsscheinnummer"
              className={`h-10 text-sm border-gray-200 rounded-lg bg-white ${(formData.versicherung_nummer || formData.versicherungsscheinnummer) ? 'bg-filled-field' : ''}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Schadennummer</label>
            <Input
              type="text"
              name="schadennummer"
              value={formData.schadennummer || ''}
              onChange={handleInputChange}
              placeholder="Schadennummer"
              className={`h-10 text-sm border-gray-200 rounded-lg bg-white ${formData.schadennummer ? 'bg-filled-field' : ''}`}
            />
          </div>
        </div>
      </div>

      {/* 4. Sachverständiger - Graue Karte ohne Rand */}
      <div className="bg-slate-50 rounded-lg p-5 mb-5">
        <h3 className="text-sm font-bold text-green-700 uppercase tracking-wide mb-4 pb-2 border-b-2 border-green-200">4. Sachverständiger</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Firmenname</label>
            <Input
              type="text"
              name="sv_firmenname"
              value={formData.sv_firmenname || ''}
              onChange={handleInputChange}
              placeholder="Firmenname"
              className={`h-10 text-sm border-gray-200 rounded-lg bg-white ${formData.sv_firmenname ? 'bg-filled-field' : ''}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Aktenzeichen</label>
            <Input
              type="text"
              name="aktenzeichen"
              value={formData.aktenzeichen || ''}
              onChange={handleInputChange}
              placeholder="Aktenzeichen"
              className={`h-10 text-sm border-gray-200 rounded-lg bg-white ${formData.aktenzeichen ? 'bg-filled-field' : ''}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Name</label>
            <Input
              type="text"
              name="sv_name"
              value={formData.sv_name || ''}
              onChange={handleInputChange}
              placeholder="Nachname"
              className={`h-10 text-sm border-gray-200 rounded-lg bg-white ${formData.sv_name ? 'bg-filled-field' : ''}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Vorname</label>
            <Input
              type="text"
              name="sv_vorname"
              value={formData.sv_vorname || ''}
              onChange={handleInputChange}
              placeholder="Vorname"
              className={`h-10 text-sm border-gray-200 rounded-lg bg-white ${formData.sv_vorname ? 'bg-filled-field' : ''}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Straße</label>
            <Input
              type="text"
              name="sv_strasse"
              value={formData.sv_strasse || ''}
              onChange={handleInputChange}
              placeholder="Straße"
              className={`h-10 text-sm border-gray-200 rounded-lg bg-white ${formData.sv_strasse ? 'bg-filled-field' : ''}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">PLZ / Ort</label>
            <Input
              type="text"
              name="sv_ort"
              value={formData.sv_ort || ''}
              onChange={handleInputChange}
              placeholder="PLZ Ort"
              className={`h-10 text-sm border-gray-200 rounded-lg bg-white ${formData.sv_ort ? 'bg-filled-field' : ''}`}
            />
          </div>
        </div>
      </div>

      {/* 5. Fahrzeug/Schadenbereich - Graue Karte ohne Rand */}
      <div className="bg-gray-50 rounded-lg p-5 mb-5">
        <h3 className="text-sm font-bold text-blue-700 uppercase tracking-wide mb-4 pb-2 border-b-2 border-blue-200">5. Fahrzeug / Schadenbereich</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Marke</label>
            <Input
              type="text"
              name="marke"
              value={formData.marke || ''}
              onChange={handleInputChange}
              placeholder="Fahrzeugmarke"
              className={`h-10 text-sm border-gray-200 rounded-lg bg-white ${formData.marke ? 'bg-filled-field' : ''}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Modell</label>
            <Input
              type="text"
              name="modell"
              value={formData.modell || ''}
              onChange={handleInputChange}
              placeholder="Fahrzeugmodell"
              className={`h-10 text-sm border-gray-200 rounded-lg bg-white ${formData.modell ? 'bg-filled-field' : ''}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Kennzeichen</label>
            <Input
              type="text"
              name="kennzeichen"
              value={formData.kennzeichen || ''}
              onChange={handleInputChange}
              placeholder="BM TU19E"
              className={`h-10 text-sm border-gray-200 rounded-lg bg-white ${formData.kennzeichen ? 'bg-filled-field' : ''}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Schadenzeitpunkt</label>
            <Input
              type="date"
              name="schadenzeitpunkt"
              value={formData.schadenzeitpunkt || formData.schadentag || ''}
              onChange={handleInputChange}
              className={`h-10 text-sm border-gray-200 rounded-lg bg-white ${(formData.schadenzeitpunkt || formData.schadentag) ? 'bg-filled-field' : ''}`}
            />
          </div>
        </div>
      </div>

      {/* Schadens- und Abtretungserklärung - Graue Karte ohne Rand */}
      <div className="bg-slate-50 rounded-lg p-5 mb-5">
        <h3 className="text-sm font-bold text-blue-700 uppercase tracking-wide mb-3 pb-2 border-b-2 border-blue-200">Schadens- und Abtretungserklärung</h3>
        <div className="bg-blue-50 rounded p-2 mb-3">
          <p className="text-xs text-blue-700 flex items-center">
            <Sparkles className="w-4 h-4 mr-2" />
            Tipp: Nutzen Sie &quot;Vorlage generieren&quot; für einen vordefinierten professionellen Text
          </p>
        </div>
        <Textarea
          value={signatureData.schadenbeschreibung || ''}
          onChange={(e) => setSignatureData({ ...signatureData, schadenbeschreibung: e.target.value })}
          rows={12}
          placeholder="Beschreibung des Schadens..."
          className="text-sm bg-white"
        />
        <div className="flex justify-end mt-3">
          <Button
            type="button"
            onClick={handleGenerateVorlage}
            size="sm"
            className="bg-blue-600 text-white hover:bg-blue-700"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Vorlage generieren
          </Button>
        </div>
      </div>

      {/* Honorarvereinbarung - Graue Karte ohne Rand */}
      <div className="bg-gray-50 rounded-lg p-5 mb-5">
        <h3 className="text-sm font-bold text-blue-700 uppercase tracking-wide mb-4 pb-2 border-b-2 border-blue-200">Honorarvereinbarung zur Gutachten Erstellung</h3>
        
        {/* Fee Table - 3 Spaltengruppen */}
        <div className="bg-white rounded-lg overflow-hidden mb-4 shadow-sm">
          <div className="grid grid-cols-3 gap-0">
            {/* Spalte 1 */}
            <div>
              <div className="bg-blue-600 text-white px-3 py-2 text-center">
                <span className="font-bold text-xs">Schadenhöhe* bis</span>
              </div>
              <div className="divide-y divide-gray-100 text-xs">
                <div className="flex justify-between px-3 py-1.5 bg-gray-50"><span>€500</span><span className="font-medium">€281</span></div>
                <div className="flex justify-between px-3 py-1.5"><span>€1.000</span><span className="font-medium">€434</span></div>
                <div className="flex justify-between px-3 py-1.5 bg-gray-50"><span>€1.500</span><span className="font-medium">€540</span></div>
                <div className="flex justify-between px-3 py-1.5"><span>€2.000</span><span className="font-medium">€614</span></div>
                <div className="flex justify-between px-3 py-1.5 bg-gray-50"><span>€3.000</span><span className="font-medium">€733</span></div>
                <div className="flex justify-between px-3 py-1.5"><span>€5.000</span><span className="font-medium">€959</span></div>
                <div className="flex justify-between px-3 py-1.5 bg-gray-50"><span>€7.500</span><span className="font-medium">€1.150</span></div>
                <div className="flex justify-between px-3 py-1.5"><span>€10.000</span><span className="font-medium">€1.333</span></div>
                <div className="flex justify-between px-3 py-1.5 bg-gray-50"><span>€12.500</span><span className="font-medium">€1.536</span></div>
                <div className="flex justify-between px-3 py-1.5"><span>€15.000</span><span className="font-medium">€1.732</span></div>
              </div>
            </div>
            
            {/* Spalte 2 */}
            <div className="border-l-2 border-r-2 border-blue-100">
              <div className="bg-blue-700 text-white px-3 py-2 text-center">
                <span className="font-bold text-xs">Schadenhöhe* bis</span>
              </div>
              <div className="divide-y divide-gray-100 text-xs">
                <div className="flex justify-between px-3 py-1.5 bg-gray-50"><span>€17.500</span><span className="font-medium">€1.905</span></div>
                <div className="flex justify-between px-3 py-1.5"><span>€20.000</span><span className="font-medium">€2.081</span></div>
                <div className="flex justify-between px-3 py-1.5 bg-gray-50"><span>€25.000</span><span className="font-medium">€2.446</span></div>
                <div className="flex justify-between px-3 py-1.5"><span>€30.000</span><span className="font-medium">€2.796</span></div>
                <div className="flex justify-between px-3 py-1.5 bg-gray-50"><span>€35.000</span><span className="font-medium">€3.163</span></div>
                <div className="flex justify-between px-3 py-1.5"><span>€40.000</span><span className="font-medium">€3.756</span></div>
                <div className="flex justify-between px-3 py-1.5 bg-gray-50"><span>€45.000</span><span className="font-medium">€4.132</span></div>
                <div className="flex justify-between px-3 py-1.5"><span>€50.000</span><span className="font-medium">€4.597</span></div>
                <div className="flex justify-between px-3 py-1.5 bg-gray-50"><span>€60.000</span><span className="font-medium">€4.695</span></div>
                <div className="flex justify-between px-3 py-1.5"><span>€70.000</span><span className="font-medium">€4.972</span></div>
              </div>
            </div>
            
            {/* Spalte 3 */}
            <div>
              <div className="bg-blue-800 text-white px-3 py-2 text-center">
                <span className="font-bold text-xs">Schadenhöhe* bis</span>
              </div>
              <div className="divide-y divide-gray-100 text-xs">
                <div className="flex justify-between px-3 py-1.5 bg-gray-50"><span>€80.000</span><span className="font-medium">€5.275</span></div>
                <div className="flex justify-between px-3 py-1.5"><span>€90.000</span><span className="font-medium">€5.448</span></div>
                <div className="flex justify-between px-3 py-1.5 bg-gray-50"><span>€100.000</span><span className="font-medium">€5.945</span></div>
                <div className="flex justify-between px-3 py-1.5"><span>€125.000</span><span className="font-medium">€6.412</span></div>
                <div className="flex justify-between px-3 py-1.5 bg-gray-50"><span>€150.000</span><span className="font-medium">€6.784</span></div>
                <div className="flex justify-between px-3 py-1.5"><span>€175.000</span><span className="font-medium">€7.097</span></div>
                <div className="flex justify-between px-3 py-1.5 bg-gray-50"><span>€200.000</span><span className="font-medium">€7.384</span></div>
                <div className="flex justify-between px-3 py-1.5"><span></span><span></span></div>
                <div className="flex justify-between px-3 py-1.5 bg-gray-50"><span></span><span></span></div>
                <div className="flex justify-between px-3 py-1.5"><span></span><span></span></div>
              </div>
            </div>
          </div>
        </div>

        {/* Definition */}
        <div className="bg-blue-50 rounded p-2 mb-4">
          <p className="text-xs text-gray-700">
            <span className="font-bold text-blue-800">*Schadenhöhe:</span> Bruttobetrag der Reparaturkosten bzw. Wertminderung
          </p>
        </div>

        {/* Nebenkosten - Kompakt */}
        <div className="bg-white rounded-lg overflow-hidden shadow-sm">
          <div className="bg-blue-600 text-white px-3 py-2">
            <h4 className="font-bold text-xs">Nebenkosten</h4>
          </div>
          <div className="grid grid-cols-2 gap-0 text-xs">
            <div className="divide-y divide-gray-100">
              <div className="flex justify-between px-3 py-1.5"><span>Lichtbilder/Stück</span><span className="font-medium">€2,38</span></div>
              <div className="flex justify-between px-3 py-1.5 bg-gray-50"><span>Fahrtkosten (15 km)</span><span className="font-medium">€20,83</span></div>
              <div className="flex justify-between px-3 py-1.5"><span>Fahrtkosten (30 km)</span><span className="font-medium">€41,65</span></div>
              <div className="flex justify-between px-3 py-1.5 bg-gray-50"><span>Fahrtkosten (40 km)</span><span className="font-medium">€55,34</span></div>
              <div className="flex justify-between px-3 py-1.5"><span>Fahrtkosten (&gt;40 km)</span><span className="font-medium">€69,02</span></div>
            </div>
            <div className="divide-y divide-gray-100 border-l border-gray-200">
              <div className="flex justify-between px-3 py-1.5"><span>Schreibkosten/Seite</span><span className="font-medium">€2,14</span></div>
              <div className="flex justify-between px-3 py-1.5 bg-gray-50"><span>Porto & Telefon</span><span className="font-medium">€17,85</span></div>
              <div className="flex justify-between px-3 py-1.5"><span>Datenbanken</span><span className="font-medium">€23,80</span></div>
              <div className="flex justify-between px-3 py-1.5 bg-gray-50"><span>Restwertermittlung</span><span className="font-medium">€29,75</span></div>
              <div className="flex justify-between px-3 py-1.5"><span>Hebebühne</span><span className="font-medium">€70,21</span></div>
            </div>
          </div>
          <div className="bg-blue-50 px-3 py-2">
            <p className="text-xs text-blue-800 font-medium">Alle Preise inkl. 19% MwSt.</p>
          </div>
        </div>
      </div>

      {/* Unterschrift Versicherungsnehmer - Graue Karte ohne Rand */}
      <div className="bg-slate-50 rounded-lg p-5 mb-5">
        <h3 className="text-sm font-bold text-blue-700 uppercase tracking-wide mb-4 pb-2 border-b-2 border-blue-200">Unterschrift Versicherungsnehmer</h3>
        
        {/* VN Info */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Vor/Nachname</label>
            <div className="p-2 bg-white rounded-lg text-sm text-gray-800 font-medium">
              {formData.kunde || '-'}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Mobilnr.</label>
            <div className="p-2 bg-white rounded-lg text-sm text-gray-800 font-medium">
              {formData.mobilnummer || '-'}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Kennzeichen</label>
            <div className="p-2 bg-white rounded-lg text-sm text-gray-800 font-medium bg-yellow-50">
              {formData.kennzeichen || '-'}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Fahrzeug</label>
            <div className="p-2 bg-white rounded-lg text-sm text-gray-800 font-medium">
              {`${formData.marke || ''} ${formData.modell || ''}`.trim() || '-'}
            </div>
          </div>
        </div>

        {/* Unterschrift Canvas */}
        <div className="flex items-center justify-between mb-4">
          <label className="block text-sm font-bold text-gray-900">Unterschrift zeichnen</label>
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-500">oder</span>
            <div className="relative">
              <input
                type="file"
                accept="image/*"
                onChange={handleSignatureUpload}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              />
              <Button type="button" variant="outline" size="sm" className="text-blue-600 border-blue-400 hover:bg-blue-50">
                <Upload className="w-4 h-4 mr-2" />
                Bild hochladen
              </Button>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg overflow-hidden shadow-sm">
          <div className="p-4">
            <div className="relative bg-white border-2 border-gray-200 rounded-lg">
              <canvas
                ref={canvasRef}
                width={700}
                height={180}
                className="w-full cursor-crosshair bg-white"
                style={{ touchAction: 'none' }}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
              <div className="absolute bottom-3 left-4 right-4 border-b-2 border-gray-400 pointer-events-none"></div>
              <div className="absolute bottom-0 left-4 text-xs text-gray-500 pointer-events-none">Unterschrift Versicherungsnehmer</div>
              <div className="absolute bottom-0 right-4 text-xs text-gray-500 pointer-events-none">{new Date().toLocaleDateString('de-DE')}</div>
            </div>
          </div>
          
          <div className="bg-gray-100 border-t border-gray-200 px-4 py-2 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-xs text-gray-600">Mit Maus oder Touchscreen</span>
              {signatureImage && (
                <div className="flex items-center space-x-1 text-green-600 text-xs font-medium">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Gespeichert</span>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <Button type="button" variant="ghost" size="sm" onClick={clearCanvas} className="text-gray-600 hover:text-gray-800 h-8">
                Löschen
              </Button>
              <Button type="button" onClick={saveSignature} className="bg-blue-600 text-white hover:bg-blue-700 h-8" size="sm">
                <Save className="w-4 h-4 mr-2" />
                Speichern
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-end gap-3 pt-6">
        <Button
          type="button"
          variant="outline"
          onClick={() => setActiveSection('beteiligte')}
          className="text-gray-600 hover:text-gray-800 hover:bg-gray-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>
        <Button
          type="button"
          onClick={() => setActiveSection('begutachtung')}
          className="bg-gradient-to-r from-blue-500 to-blue-700 text-white"
        >
          Weiter zu Begutachtung
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );

  const renderBegutachtungForm = () => (
    <div className="max-w-full 2xl:max-w-[1600px] mx-auto">
      {/* Header im Auftrag-Stil */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white flex items-center justify-center font-bold text-xl mr-4 shadow-md">5</div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Begutachtung</h2>
            <p className="text-sm text-gray-500">Fahrzeugbegutachtung und Schadensdokumentation</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-6">
          <Tabs defaultValue="begutachtung" className="w-full">
            <TabsList className="inline-flex h-10 bg-gray-100 p-1 rounded-lg mb-6">
              <TabsTrigger value="begutachtung" className="text-sm px-4 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all">
                Begutachtung
              </TabsTrigger>
              <TabsTrigger value="beurteilung" className="text-sm px-4 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all">
                Beurteilung
              </TabsTrigger>
            </TabsList>

            {/* Begutachtung Tab */}
            <TabsContent value="begutachtung" className="mt-0">
              {/* Sub-Tabs */}
              <Tabs defaultValue="vor-ort" className="w-full">
                <TabsList className="w-full justify-start bg-transparent border-b border-gray-200 rounded-none h-auto p-0 mb-6">
                  <TabsTrigger value="vor-ort" className="text-sm px-5 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none">Vor Ort</TabsTrigger>
                  <TabsTrigger value="lackschicht" className="text-sm px-5 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none">Lackschicht</TabsTrigger>
                  <TabsTrigger value="vorschaeden" className="text-sm px-5 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none">Vorschäden</TabsTrigger>
                  <TabsTrigger value="schadenfest" className="text-sm px-5 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none">Schadenfeststellung</TabsTrigger>
                </TabsList>

                {/* Vor Ort Tab - Auftrag-Design */}
                <TabsContent value="vor-ort" className="mt-0">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    
                    {/* Spalte 1: Besichtigung */}
                    <div className="bg-gray-50 rounded-lg p-5">
                      <h3 className="text-sm font-bold text-blue-700 uppercase tracking-wide mb-4 pb-2 border-b-2 border-blue-200">Besichtigung</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1.5">Besichtigungsbedingungen</label>
                          <select 
                            className="h-10 w-full px-3 py-2 text-sm border-0 rounded-lg bg-white focus:ring-2 focus:ring-blue-500/20 transition-colors"
                            value={formData.besichtigungsbedingungen || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                besichtigungsbedingungen: value,
                                dokumentationstext: besichtigungsTexte[value] || ''
                              }));
                            }}
                          >
                            <option value="">Auswählen...</option>
                            <option value="ausreichend">ausreichend</option>
                            <option value="ausreichend_unten">ausreichend, von unten</option>
                            <option value="ausreichend_nachbesichtigt">ausreichend, nachbesichtigt</option>
                            <option value="eingeschraenkt">eingeschränkt</option>
                            <option value="eingeschraenkt_unbeweglich">eingeschränkt, unbeweglich</option>
                            <option value="eingeschraenkt_wetter">eingeschränkt, Wetter</option>
                            <option value="eingeschraenkt_verschmutzung">eingeschränkt, Verschmutzung</option>
                            <option value="unzureichend_totalschaden">unzureichend, Totalschaden</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1.5">Besichtigungszustand</label>
                          <select 
                            className="h-10 w-full px-3 py-2 text-sm border-0 rounded-lg bg-white focus:ring-2 focus:ring-blue-500/20 transition-colors"
                            value={formData.besichtigungszustand || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                besichtigungszustand: value,
                                zustandstext: besichtigungszustandTexte[value] || ''
                              }));
                            }}
                          >
                            <option value="">Auswählen...</option>
                            <option value="undemontiert">undemontiert</option>
                            <option value="undemontiert_risiko">undemontiert mit Risiko</option>
                            <option value="teilweise_demontiert">teilweise demontiert</option>
                            <option value="demontiert">demontiert</option>
                            <option value="teilweise_repariert">teilweise repariert</option>
                            <option value="repariert">repariert</option>
                            <option value="unrepariert">unrepariert</option>
                            <option value="repariert_ohne_altteile">repariert ohne Altteile</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1.5">Identifizierung</label>
                          <select 
                            className="h-10 w-full px-3 py-2 text-sm border-0 rounded-lg bg-white focus:ring-2 focus:ring-blue-500/20 transition-colors"
                            value={formData.identifizierung || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                identifizierung: value,
                                identifizierungstext: identifizierungTexte[value] || ''
                              }));
                            }}
                          >
                            <option value="">Auswählen...</option>
                            <option value="fz_schein_teil1">Fzg.-Schein Teil I</option>
                            <option value="fz_brief_teil2">Fzg.-Brief Teil II</option>
                            <option value="fz_schein_kopie">Fzg.-Schein Kopie</option>
                            <option value="werkstattunterlagen">Werkstattunterlagen</option>
                            <option value="muendliche_angaben">mündliche Angaben</option>
                            <option value="auslaendische_papiere">ausländische Papiere</option>
                            <option value="amtliche_papiere">amtliche Papiere</option>
                            <option value="nicht_amtliche_papiere">nicht amtliche Papiere</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1.5">Scheckheftgepflegt</label>
                          <select 
                            className="h-10 w-full px-3 py-2 text-sm border-0 rounded-lg bg-white focus:ring-2 focus:ring-blue-500/20 transition-colors"
                            value={formData.scheckheftgepflegt || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                scheckheftgepflegt: value,
                                scheckhefttext: scheckheftTexte[value] || ''
                              }));
                            }}
                          >
                            <option value="">Auswählen...</option>
                            <option value="nein">Nein</option>
                            <option value="nicht_pruefbar">nicht prüfbar</option>
                            <option value="markengebundene_werkstatt">markengebundene Werkstatt</option>
                            <option value="fachwerkstatt">Fachwerkstatt</option>
                            <option value="vertragswerkstatt">Vertragswerkstatt</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    
                    {/* Spalte 2: Prüfung */}
                    <div className="bg-slate-50 rounded-lg p-5">
                      <h3 className="text-sm font-bold text-blue-700 uppercase tracking-wide mb-4 pb-2 border-b-2 border-blue-200">Prüfung</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1.5">Probelauf Antrieb</label>
                          <select 
                            className="h-10 w-full px-3 py-2 text-sm border-0 rounded-lg bg-white focus:ring-2 focus:ring-blue-500/20 transition-colors"
                            value={formData.probelauf || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                probelauf: value,
                                probelauftext: probelaufTexte[value] || ''
                              }));
                            }}
                          >
                            <option value="">Auswählen...</option>
                            <option value="durchgefuehrt">durchgeführt</option>
                            <option value="nicht_notwendig">nicht notwendig</option>
                            <option value="nicht_moeglich">nicht möglich</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1.5">Probefahrt</label>
                          <select 
                            className="h-10 w-full px-3 py-2 text-sm border-0 rounded-lg bg-white focus:ring-2 focus:ring-blue-500/20 transition-colors"
                            value={formData.probefahrt || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                probefahrt: value,
                                probefahrttext: probefahrtTexte[value] || ''
                              }));
                            }}
                          >
                            <option value="">Auswählen...</option>
                            <option value="durchgefuehrt">durchgeführt</option>
                            <option value="nicht_notwendig">nicht notwendig</option>
                            <option value="nicht_moeglich">nicht möglich</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1.5">Fehlerspeicher</label>
                          <select 
                            className="h-10 w-full px-3 py-2 text-sm border-0 rounded-lg bg-white focus:ring-2 focus:ring-blue-500/20 transition-colors"
                            value={formData.fehlerspeicher || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                fehlerspeicher: value,
                                fehlerspeichertext: fehlerspeicherTexte[value] || ''
                              }));
                            }}
                          >
                            <option value="">Auswählen...</option>
                            <option value="ja">Ja</option>
                            <option value="nein">Nein</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    
                    {/* Spalte 3: Fahrzeugzustand */}
                    <div className="bg-gray-50 rounded-lg p-5">
                      <h3 className="text-sm font-bold text-blue-700 uppercase tracking-wide mb-4 pb-2 border-b-2 border-blue-200">Fahrzeugzustand</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1.5">Allgemeinzustand</label>
                          <select 
                            className="h-10 w-full px-3 py-2 text-sm border-0 rounded-lg bg-white focus:ring-2 focus:ring-blue-500/20 transition-colors"
                            value={formData.allgemeinzustand || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                allgemeinzustand: value,
                                allgemeinzustandtext: allgemeinzustandTexte[value] || '',
                                // Automatisch andere Zustandsfelder setzen
                                karosseriezustand: value,
                                karosseriezustandtext: karosseriezustandTexte[value] || '',
                                lackzustand: value,
                                lackzustandtext: lackzustandTexte[value] || '',
                                innenraumzustand: value,
                                innenraumzustandtext: innenraumzustandTexte[value] || ''
                              }));
                            }}
                          >
                            <option value="">Auswählen...</option>
                            <option value="neuwertig">neuwertig</option>
                            <option value="sehr_gut">sehr gut</option>
                            <option value="gut">gut</option>
                            <option value="gepflegt">gepflegt</option>
                            <option value="maessig">mäßig</option>
                            <option value="ungepflegt">ungepflegt</option>
                            <option value="sehr_ungepflegt">sehr ungepflegt</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1.5">Karosseriezustand</label>
                          <select 
                            className="h-10 w-full px-3 py-2 text-sm border-0 rounded-lg bg-white focus:ring-2 focus:ring-blue-500/20 transition-colors"
                            value={formData.karosseriezustand || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                karosseriezustand: value,
                                karosseriezustandtext: karosseriezustandTexte[value] || ''
                              }));
                            }}
                          >
                            <option value="">Auswählen...</option>
                            <option value="neuwertig">neuwertig</option>
                            <option value="sehr_gut">sehr gut</option>
                            <option value="gut">gut</option>
                            <option value="gepflegt">gepflegt</option>
                            <option value="maessig">mäßig</option>
                            <option value="ungepflegt">ungepflegt</option>
                            <option value="sehr_ungepflegt">sehr ungepflegt</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1.5">Lackzustand</label>
                          <select 
                            className="h-10 w-full px-3 py-2 text-sm border-0 rounded-lg bg-white focus:ring-2 focus:ring-blue-500/20 transition-colors"
                            value={formData.lackzustand || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                lackzustand: value,
                                lackzustandtext: lackzustandTexte[value] || ''
                              }));
                            }}
                          >
                            <option value="">Auswählen...</option>
                            <option value="neuwertig">neuwertig</option>
                            <option value="sehr_gut">sehr gut</option>
                            <option value="gut">gut</option>
                            <option value="gepflegt">gepflegt</option>
                            <option value="maessig">mäßig</option>
                            <option value="ungepflegt">ungepflegt</option>
                            <option value="sehr_ungepflegt">sehr ungepflegt</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1.5">Innenraumzustand</label>
                          <select 
                            className="h-10 w-full px-3 py-2 text-sm border-0 rounded-lg bg-white focus:ring-2 focus:ring-blue-500/20 transition-colors"
                            value={formData.innenraumzustand || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                innenraumzustand: value,
                                innenraumzustandtext: innenraumzustandTexte[value] || ''
                              }));
                            }}
                          >
                            <option value="">Auswählen...</option>
                            <option value="neuwertig">neuwertig</option>
                            <option value="sehr_gut">sehr gut</option>
                            <option value="gut">gut</option>
                            <option value="gepflegt">gepflegt</option>
                            <option value="maessig">mäßig</option>
                            <option value="ungepflegt">ungepflegt</option>
                            <option value="sehr_ungepflegt">sehr ungepflegt</option>
                          </select>
                        </div>
                        
                        {/* Zustandsbeschreibung Textarea */}
                        <div className="pt-1">
                          <label className="block text-xs font-medium text-gray-500 mb-1.5">Zustandsbeschreibung</label>
                          <Textarea
                            placeholder="Ergänzende Beschreibung..."
                            value={formData.zustandsbeschreibung || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, zustandsbeschreibung: e.target.value }))}
                            className="min-h-[60px] text-xs border-0 rounded-lg bg-white focus:ring-2 focus:ring-blue-500/20 transition-colors"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Zweite Zeile: Schadenhergang - 2 Spalten */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                    {/* Linke Spalte: Schadenhergang Details */}
                    <div className="bg-gray-50 rounded-lg p-5">
                      <h3 className="text-sm font-bold text-blue-700 uppercase tracking-wide mb-4 pb-2 border-b-2 border-blue-200">Schadenhergang</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1.5">Schilderung</label>
                          <select 
                            className="h-10 w-full px-3 py-2 text-sm border-0 rounded-lg bg-white focus:ring-2 focus:ring-blue-500/20 transition-colors"
                            value={formData.schadenhergang || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                schadenhergang: value,
                                schadenhergangtext: schadenhergangTexte[value] || ''
                              }));
                            }}
                          >
                            <option value="">Auswählen...</option>
                            <option value="unfall">Unfall</option>
                            <option value="unfall_ueberschlag">Unfall mit Überschlag</option>
                            <option value="verkehrsunfall">Verkehrsunfall</option>
                            <option value="auffahrunfall">Auffahrunfall</option>
                            <option value="parkplatzunfall">Parkplatzunfall</option>
                            <option value="vorfahrtsmissachtung">Vorfahrtsmissachtung</option>
                            <option value="unwetterschaeden">Unwetterschäden</option>
                            <option value="vandalismusschaeden">Vandalismusschäden</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1.5">Unfallprotokoll</label>
                          <select 
                            className="h-10 w-full px-3 py-2 text-sm border-0 rounded-lg bg-white focus:ring-2 focus:ring-blue-500/20 transition-colors"
                            value={formData.unfallprotokoll || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                unfallprotokoll: value,
                                unfallprotokolltext: unfallprotokollTexte[value] || ''
                              }));
                            }}
                          >
                            <option value="">Auswählen...</option>
                            <option value="nicht_vorhanden">nicht vorhanden</option>
                            <option value="beim_anspruchsteller">liegt beim Anspruchsteller</option>
                            <option value="kopie_im_gutachten">Kopie im Gutachten</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1.5">Plausibilität</label>
                          <select 
                            className="h-10 w-full px-3 py-2 text-sm border-0 rounded-lg bg-white focus:ring-2 focus:ring-blue-500/20 transition-colors"
                            value={formData.plausibilitaet || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                plausibilitaet: value,
                                plausibilitaettext: plausibilitaetTexte[value] || ''
                              }));
                            }}
                          >
                            <option value="">Auswählen...</option>
                            <option value="plausibel">plausibel</option>
                            <option value="nicht_plausibel">nicht plausibel</option>
                            <option value="bestimmung_nicht_moeglich">Bestimmung nicht möglich</option>
                            <option value="sonstige">Sonstige</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1.5">Kompatibilität</label>
                          <select 
                            className="h-10 w-full px-3 py-2 text-sm border-0 rounded-lg bg-white focus:ring-2 focus:ring-blue-500/20 transition-colors"
                            value={formData.kompatibilitaet || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                kompatibilitaet: value,
                                kompatibilitaettext: kompatibilitaetTexte[value] || ''
                              }));
                            }}
                          >
                            <option value="">Auswählen...</option>
                            <option value="kompatibel">kompatibel</option>
                            <option value="teilweise_kompatibel">teilweise kompatibel</option>
                            <option value="nicht_kompatibel">nicht kompatibel</option>
                            <option value="nicht_beurteilbar">nicht beurteilbar</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    
                    {/* Rechte Spalte: Detaillierte Schilderung mit KI */}
                    <div className="bg-slate-50 rounded-lg p-5">
                      <h3 className="text-sm font-bold text-blue-700 uppercase tracking-wide mb-4 pb-2 border-b-2 border-blue-200">Detaillierte Schilderung</h3>
                      
                      {/* KI Keywords Eingabe */}
                      <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-4 h-4 text-blue-600" />
                          <span className="text-xs font-semibold text-blue-700">KI-Assistent</span>
                        </div>
                        <div className="flex gap-2">
                          <Input
                            type="text"
                            placeholder="Keywords eingeben: z.B. Auffahrunfall, Ampel, Stoßstange hinten..."
                            value={formData.ki_keywords || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, ki_keywords: e.target.value }))}
                            className="flex-1 text-sm h-9 bg-white border-0"
                          />
                          <Button
                            type="button"
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-4"
                            disabled={!formData.ki_keywords || formData.ki_generating}
                            onClick={async () => {
                              if (!formData.ki_keywords) return;
                              
                              setFormData(prev => ({ ...prev, ki_generating: true }));
                              
                              try {
                                const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
                                const response = await fetch(`${BACKEND_URL}/api/ai/generate-schadenhergang`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({
                                    keywords: formData.ki_keywords,
                                    context: {
                                      marke: formData.marke,
                                      modell: formData.modell,
                                      kennzeichen: formData.kennzeichen,
                                      schadenart: formData.schadenart
                                    }
                                  })
                                });
                                
                                if (response.ok) {
                                  const data = await response.json();
                                  setFormData(prev => ({ 
                                    ...prev, 
                                    detaillierte_schilderung: data.description,
                                    ki_generating: false 
                                  }));
                                  toast.success('Schilderung generiert!');
                                } else {
                                  throw new Error('Generierung fehlgeschlagen');
                                }
                              } catch (error) {
                                console.error('KI-Fehler:', error);
                                toast.error('Fehler bei der KI-Generierung');
                                setFormData(prev => ({ ...prev, ki_generating: false }));
                              }
                            }}
                          >
                            {formData.ki_generating ? (
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                <span>Generiere...</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <Sparkles className="w-4 h-4" />
                                <span>Generieren</span>
                              </div>
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">Geben Sie Stichworte zum Unfallhergang ein und lassen Sie die KI eine professionelle Beschreibung erstellen.</p>
                      </div>
                      
                      <Textarea
                        placeholder="Ausführliche Beschreibung des Schadenhergangs..."
                        value={formData.detaillierte_schilderung || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, detaillierte_schilderung: e.target.value }))}
                        className="min-h-[140px] text-sm border-0 rounded-lg bg-white focus:ring-2 focus:ring-blue-500/20 transition-colors"
                      />
                    </div>
                  </div>
                </TabsContent>


                {/* Lackschicht Tab */}
                <TabsContent value="lackschicht" className="space-y-4">
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <p className="text-gray-600">Lackschichtmessung-Daten</p>
                    <p className="text-sm text-gray-500 mt-2">Funktion wird ergänzt</p>
                  </div>
                </TabsContent>

                {/* Vorschäden Tab */}
                <TabsContent value="vorschaeden" className="space-y-4">
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <p className="text-gray-600">Vorschäden-Dokumentation</p>
                    <p className="text-sm text-gray-500 mt-2">Funktion wird ergänzt</p>
                  </div>
                </TabsContent>

                {/* Schadenfeststellung Tab */}
                <TabsContent value="schadenfest" className="space-y-4">
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <p className="text-gray-600">Schadenfeststellung</p>
                    <p className="text-sm text-gray-500 mt-2">Funktion wird ergänzt</p>
                  </div>
                </TabsContent>
              </Tabs>
            </TabsContent>

            {/* Beurteilung Tab */}
            <TabsContent value="beurteilung" className="space-y-6">
              <div className="bg-white rounded-md border border-gray-200">
                <div className="px-4 py-3 bg-blue-50 border-b border-blue-100">
                  <h3 className="flex items-center space-x-2 text-blue-900">
                    <ClipboardList className="w-5 h-5" />
                    <span>Gutachterliche Beurteilung</span>
                  </h3>
                </div>
                <div className="pt-6 space-y-6">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-900">
                      <strong>Hinweis:</strong> Erfassen Sie hier die gutachterliche Beurteilung des Schadens, 
                      einschließlich technischer Bewertung, Schadensursache und Reparaturempfehlungen.
                    </p>
                  </div>

                  {/* Schadenumfang */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      1. Schadenumfang und -beschreibung
                    </label>
                    <Textarea
                      name="beurteilung_schadenumfang"
                      placeholder="Detaillierte Beschreibung des festgestellten Schadens, betroffene Bauteile, Schadensart und -ausmaß..."
                      value={bewertungData.beurteilung_schadenumfang}
                      onChange={handleBewertungChange}
                      rows={6}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Beschreiben Sie alle beschädigten Teile und deren Zustand detailliert
                    </p>
                  </div>

                  {/* Reparaturkosten */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      2. Reparaturkosten und Kalkulation
                    </label>
                    <Textarea
                      name="beurteilung_reparaturkosten"
                      placeholder="Kalkulation der Reparaturkosten, benötigte Ersatzteile, Arbeitsaufwand, Stundenverrechnungssätze..."
                      value={bewertungData.beurteilung_reparaturkosten}
                      onChange={handleBewertungChange}
                      rows={6}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Führen Sie die Kostenposition detailliert auf
                    </p>
                  </div>

                  {/* Unfallrekonstruktion */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      3. Unfallhergang / Schadensursache
                    </label>
                    <Textarea
                      name="beurteilung_unfallrekonstruktion"
                      placeholder="Rekonstruktion des Unfallhergangs basierend auf Spurenlage, Schadenbild und Angaben der Beteiligten..."
                      value={bewertungData.beurteilung_unfallrekonstruktion}
                      onChange={handleBewertungChange}
                      rows={6}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Technische Bewertung des Unfallhergangs
                    </p>
                  </div>

                  {/* Haftungsverteilung */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      4. Haftung und Verschulden
                    </label>
                    <Textarea
                      name="beurteilung_haftungsverteilung"
                      placeholder="Bewertung der Haftungsfrage, Verschuldensanteile, rechtliche Einschätzung..."
                      value={bewertungData.beurteilung_haftungsverteilung}
                      onChange={handleBewertungChange}
                      rows={5}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Gutachterliche Einschätzung der Haftungsverteilung
                    </p>
                  </div>

                  {/* Zusammenfassung */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      5. Zusammenfassung und Fazit
                    </label>
                    <Textarea
                      name="beurteilung_zusammenfassung"
                      placeholder="Abschließende gutachterliche Bewertung, Empfehlungen, besondere Hinweise..."
                      value={bewertungData.beurteilung_zusammenfassung}
                      onChange={handleBewertungChange}
                      rows={6}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Fassen Sie die wesentlichen Erkenntnisse zusammen
                    </p>
                  </div>

                  {/* Info Card */}
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-200 rounded-lg p-6">
                    <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Gutachterliche Standards
                    </h4>
                    <ul className="text-sm text-blue-800 space-y-2">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Objektive, nachvollziehbare Beurteilung aller schadensrelevanten Aspekte</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Berücksichtigung technischer Normen und Herstellervorgaben</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Plausible Begründung der Schadenshöhe und Reparaturnotwendigkeit</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Dokumentation mit Fotos und ggf. Messwerten</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-end gap-3 mt-6">
        <Button 
          type="button" 
          variant="outline"
          onClick={() => setActiveSection('abtretung')} 
          className="text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg px-5 transition-all"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>
        <Button 
          type="button" 
          onClick={() => setActiveSection('lichtbilder')} 
          className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg px-6 shadow-md hover:shadow-lg transition-all flex items-center gap-2"
        >
          Weiter zu Lichtbilder
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );


  // DAT myClaim Handler Function
  // Store DAT credentials for auto-fill
  const [datCredentials, setDatCredentials] = useState(null);

  // Auto-fill DAT credentials function  
  const autoFillDATCredentials = async (iframe) => {
    if (!datCredentials) {
      console.log('❌ No DAT credentials available for auto-fill');
      return;
    }

    try {
      console.log('🔑 Attempting to auto-fill DAT credentials...');
      
      // Wait for iframe to fully load
      setTimeout(() => {
        try {
          // Try to access iframe content
          const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
          
          // Find login form fields (try multiple selectors)
          const customerField = iframeDoc.querySelector(
            'input[name*="customer"], input[id*="customer"], input[placeholder*="Kunde"], input[placeholder*="Customer"]'
          );
          const userField = iframeDoc.querySelector(
            'input[name*="user"], input[id*="user"], input[name*="username"], input[placeholder*="Benutzer"], input[placeholder*="User"]'
          );
          const passwordField = iframeDoc.querySelector(
            'input[type="password"], input[name*="password"], input[placeholder*="Passwort"], input[placeholder*="Password"]'
          );

          if (customerField && userField && passwordField) {
            // Fill the fields
            customerField.value = datCredentials.customer_number;
            userField.value = datCredentials.requestor;
            passwordField.value = datCredentials.password;

            // Trigger events to notify the form
            ['input', 'change', 'keyup'].forEach(eventType => {
              customerField.dispatchEvent(new Event(eventType, { bubbles: true }));
              userField.dispatchEvent(new Event(eventType, { bubbles: true }));
              passwordField.dispatchEvent(new Event(eventType, { bubbles: true }));
            });

            console.log('✅ Credentials auto-filled successfully');
            
            // Show success message
            toast.success('DAT-Zugangsdaten automatisch eingetragen!');

            // Try to auto-submit after a short delay
            setTimeout(() => {
              const loginBtn = iframeDoc.querySelector(
                'button[type="submit"], input[type="submit"], button:contains("Anmelden"), button:contains("Login"), .login-button, #login-button'
              );
              if (loginBtn) {
                loginBtn.click();
                console.log('✅ Login button clicked automatically');
                toast.success('Automatische Anmeldung durchgeführt!');
              }
            }, 1000);

          } else {
            console.log('❌ Login fields not found in iframe');
            // Show manual instruction
            toast.info('DAT-Zugangsdaten: Kunde: ' + datCredentials.customer_number + ', Benutzer: ' + datCredentials.requestor);
          }
          
        } catch (corsError) {
          console.log('⚠️ CORS blocked iframe access:', corsError);
          // Show credentials to user for manual entry
          toast.info(`Bitte manuell eingeben - Kunde: ${datCredentials.customer_number}, Benutzer: ${datCredentials.requestor}`);
        }
      }, 3000); // Wait 3 seconds for iframe to fully load
      
    } catch (error) {
      console.error('❌ Error in auto-fill function:', error);
    }
  };

  const handleOpenDATmyClaim = async () => {
    if (!formData.aktenzeichen) {
      alert('Bitte generieren Sie zuerst ein Aktenzeichen');
      return;
    }

    setDatLoading(true);
    try {
      // Get authentication token
      const token = localStorage.getItem('autogutachter24_token');
      if (!token) {
        alert('Bitte loggen Sie sich zuerst ein');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dat/myclaim/generate-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          aktenzeichen: formData.aktenzeichen,
          hersteller: formData.marke || formData.hersteller,
          kennzeichen: formData.kennzeichen,
          vin: formData.vin || formData.fahrgestellnummer
        })
      });
      
      const data = await response.json();
      if (data.success && data.myclaim_url) {
        // Store credentials for auto-fill
        if (data.credentials) {
          setDatCredentials(data.credentials);
          console.log('✅ DAT credentials received for auto-fill');
        }
        
        // Open DAT myClaim in Vollbild-iframe innerhalb der Anwendung
        setDatMyClaimUrl(data.myclaim_url);
        setShowDatIframe(true);
      } else {
        alert('Fehler beim Öffnen von DAT myClaim: ' + (data.error || 'Unbekannter Fehler'));
      }
    } catch (error) {
      console.error('DAT myClaim Error:', error);
      alert('Fehler beim Öffnen von DAT myClaim');
    } finally {
      setDatLoading(false);
    }
  };

  const handleCloseDatIframe = () => {
    setShowDatIframe(false);
    setDatMyClaimUrl(null);
  };

  // Minderwert Berechnungsfunktionen
  const berechneMinderwert = () => {
    const { wiederbeschaffungswert, reparaturkosten, fahrzeugalter_monate, laufleistung_km, neupreis } = minderwertData;
    
    // Validierung
    if (!wiederbeschaffungswert || !reparaturkosten || !fahrzeugalter_monate) {
      alert('Bitte füllen Sie alle erforderlichen Felder aus');
      return;
    }

    // Ruhkopf/Sahm Berechnung
    const ruhkopfErgebnis = berechneRuhkopfSahm(
      parseFloat(wiederbeschaffungswert),
      parseFloat(reparaturkosten),
      parseInt(fahrzeugalter_monate)
    );

    // Halbgewachs Berechnung
    const halbgewachsErgebnis = berechneHalbgewachs(
      parseFloat(wiederbeschaffungswert),
      parseFloat(reparaturkosten),
      parseInt(fahrzeugalter_monate),
      parseFloat(laufleistung_km || 0)
    );

    setMinderwertErgebnis({
      ruhkopf_sahm: ruhkopfErgebnis,
      halbgewachs: halbgewachsErgebnis
    });
  };

  const berechneRuhkopfSahm = (wbw, reparaturkosten, alter_monate) => {
    // Reparaturkostenverhältnis berechnen
    const rkVerhaeltnis = (reparaturkosten / wbw) * 100;
    
    // Faktor aus Tabelle bestimmen
    let faktor = 0;
    const alter_jahre = Math.ceil(alter_monate / 12);
    
    if (alter_jahre <= 1) {
      // 1. Zulassungsjahr
      if (rkVerhaeltnis >= 10 && rkVerhaeltnis <= 30) faktor = 5;
      else if (rkVerhaeltnis > 30 && rkVerhaeltnis <= 60) faktor = 6;
      else if (rkVerhaeltnis > 60 && rkVerhaeltnis <= 90) faktor = 7;
    } else if (alter_jahre <= 2) {
      // 2. Zulassungsjahr
      if (rkVerhaeltnis >= 10 && rkVerhaeltnis <= 30) faktor = 4;
      else if (rkVerhaeltnis > 30 && rkVerhaeltnis <= 60) faktor = 5;
      else if (rkVerhaeltnis > 60 && rkVerhaeltnis <= 90) faktor = 6;
    } else {
      // ab 3. Zulassungsjahr
      if (rkVerhaeltnis >= 10 && rkVerhaeltnis <= 30) faktor = 3;
      else if (rkVerhaeltnis > 30 && rkVerhaeltnis <= 60) faktor = 4;
      else if (rkVerhaeltnis > 60 && rkVerhaeltnis <= 90) faktor = 5;
    }

    // Bagatellschadengrenze prüfen (RK/Zeitwert < 10%)
    if (rkVerhaeltnis < 10) {
      return {
        minderwert: 0,
        faktor: 0,
        rkVerhaeltnis: rkVerhaeltnis.toFixed(2),
        hinweis: 'Bagatellschadengrenze: Reparaturkostenverhältnis < 10%'
      };
    }

    // Minderwert berechnen: (WBW + RK) × Faktor%
    const minderwert = (wbw + reparaturkosten) * (faktor / 100);

    return {
      minderwert: minderwert.toFixed(2),
      faktor: faktor,
      rkVerhaeltnis: rkVerhaeltnis.toFixed(2),
      hinweis: null
    };
  };

  // Nutzungsausfall Berechnungsfunktion
  const berechneNutzungsausfall = () => {
    const { fahrzeugklasse, ausfalltage, fahrzeugalter_jahre } = nutzungsausfallData;
    
    // Stille Prüfung - kein Alert bei automatischer Berechnung
    if (!fahrzeugklasse || !ausfalltage) {
      console.log('Nutzungsausfall-Berechnung: Felder nicht vollständig ausgefüllt');
      return;
    }

    // Schwacke/Sanden-Danner Tabelle 2025
    const schwackeTabelle = {
      'A': 23,
      'B': 29,
      'C': 35,
      'D': 38,
      'E': 42,
      'F': 50,
      'G': 59,
      'H': 65,
      'J': 79,
      'K': 100,
      'L': 175
    };

    let basisTagessatz = schwackeTabelle[fahrzeugklasse] || 0;
    let tatsaechlicheKlasse = fahrzeugklasse;
    
    // Altersrabatt anwenden
    const alter = parseInt(fahrzeugalter_jahre) || 0;
    if (alter >= 10) {
      // 2 Klassen niedriger
      const klassen = Object.keys(schwackeTabelle);
      const aktuellerIndex = klassen.indexOf(fahrzeugklasse);
      if (aktuellerIndex >= 2) {
        tatsaechlicheKlasse = klassen[aktuellerIndex - 2];
        basisTagessatz = schwackeTabelle[tatsaechlicheKlasse];
      }
    } else if (alter >= 5) {
      // 1 Klasse niedriger
      const klassen = Object.keys(schwackeTabelle);
      const aktuellerIndex = klassen.indexOf(fahrzeugklasse);
      if (aktuellerIndex >= 1) {
        tatsaechlicheKlasse = klassen[aktuellerIndex - 1];
        basisTagessatz = schwackeTabelle[tatsaechlicheKlasse];
      }
    }

    const tage = parseInt(ausfalltage);
    const gesamtsumme = basisTagessatz * tage;

    setNutzungsausfallErgebnis({
      tagessatz: basisTagessatz,
      ausfalltage: tage,
      gesamtsumme: gesamtsumme.toFixed(2),
      urspruenglicheKlasse: fahrzeugklasse,
      angewendeteKlasse: tatsaechlicheKlasse,
      altersrabatt: tatsaechlicheKlasse !== fahrzeugklasse
    });
  };

  const berechneHalbgewachs = (wbw, reparaturkosten, alter_monate, laufleistung) => {
    // Basiswert bestimmen (basierend auf Alter und WBW)
    let basiswert = 0;
    const alter_jahre = alter_monate / 12;
    
    if (alter_jahre <= 3 && wbw > 20000) {
      basiswert = 2000;
    } else if (alter_jahre <= 3 && wbw <= 20000) {
      basiswert = 1500;
    } else if (alter_jahre <= 5 && wbw > 20000) {
      basiswert = 1500;
    } else if (alter_jahre <= 5 && wbw <= 20000) {
      basiswert = 1000;
    } else {
      basiswert = 500;
    }

    // Altersfaktor: max(0.5; 1.0 − 0.05 × (Alter − 1))
    const altersfaktor = Math.max(0.5, 1.0 - 0.05 * (alter_jahre - 1));

    // Laufleistungsfaktor: max(0.3; 1.0 − Laufleistung / 200.000)
    const laufleistungsfaktor = Math.max(0.3, 1.0 - laufleistung / 200000);

    // Reparaturfaktor: min(1.0; Reparaturkosten / WBW)
    const reparaturfaktor = Math.min(1.0, reparaturkosten / wbw);

    // Wertminderung berechnen
    const minderwert = basiswert * altersfaktor * laufleistungsfaktor * reparaturfaktor;

    return {
      minderwert: minderwert.toFixed(2),
      basiswert: basiswert,
      altersfaktor: altersfaktor.toFixed(3),
      laufleistungsfaktor: laufleistungsfaktor.toFixed(3),
      reparaturfaktor: reparaturfaktor.toFixed(3)
    };
  };

  const renderKalkulationForm = () => {
    // Kalkulation einreichen
    const handleSubmitKalkulation = async () => {
      if (!formData.id) {
        toast.error('Bitte speichern Sie die Akte zuerst');
        return;
      }
      
      setSubmittingKalkulation(true);
      try {
        const token = localStorage.getItem('autogutachter24_token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/kalkulation/einreichen`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            akte_id: formData.id,
            typ: 'reparatur',
            notizen: ''
          })
        });
        
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Fehler beim Einreichen');
        }
        
        const data = await response.json();
        toast.success('Kalkulation erfolgreich eingereicht!');
        setKalkulationStatus('eingereicht');
      } catch (error) {
        toast.error(error.message);
      } finally {
        setSubmittingKalkulation(false);
      }
    };
    
    // Render Kalkulations-Status Card
    const renderKalkulationStatusCard = () => {
      if (!kalkulationStatus || kalkulationStatus === 'nicht_eingereicht') {
        return (
          <div className="border-2 border-dashed border-orange-300 bg-orange-50 rounded-lg">
            <div className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
                <Upload className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Zur Kalkulation einreichen</h3>
              <p className="text-gray-600 mb-6 max-w-lg mx-auto">
                Reichen Sie diese Akte zur professionellen Kalkulation ein. 
                Unser Team wird die Schadenskalkulation durchführen und die Ergebnisse hier bereitstellen.
              </p>
              
              {/* Checkliste */}
              <div className="bg-white rounded-lg p-4 mb-6 max-w-md mx-auto text-left">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Vor dem Einreichen prüfen:</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center">
                    {formData.marke ? <CheckCircle className="w-4 h-4 text-green-500 mr-2" /> : <AlertCircle className="w-4 h-4 text-gray-300 mr-2" />}
                    <span className={formData.marke ? 'text-gray-900' : 'text-gray-400'}>Fahrzeugdaten erfasst</span>
                  </li>
                  <li className="flex items-center">
                    {formData.lichtbilder?.length > 0 ? <CheckCircle className="w-4 h-4 text-green-500 mr-2" /> : <AlertCircle className="w-4 h-4 text-gray-300 mr-2" />}
                    <span className={formData.lichtbilder?.length > 0 ? 'text-gray-900' : 'text-gray-400'}>Lichtbilder hochgeladen ({formData.lichtbilder?.length || 0})</span>
                  </li>
                  <li className="flex items-center">
                    {formData.schadentag ? <CheckCircle className="w-4 h-4 text-green-500 mr-2" /> : <AlertCircle className="w-4 h-4 text-gray-300 mr-2" />}
                    <span className={formData.schadentag ? 'text-gray-900' : 'text-gray-400'}>Schadendatum angegeben</span>
                  </li>
                </ul>
              </div>
              
              <Button
                onClick={handleSubmitKalkulation}
                disabled={submittingKalkulation || !formData.id}
                size="lg"
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8"
              >
                {submittingKalkulation ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    Wird eingereicht...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 mr-2" />
                    Jetzt zur Kalkulation einreichen
                  </>
                )}
              </Button>
            </div>
          </div>
        );
      }
      
      if (kalkulationStatus === 'eingereicht' || kalkulationStatus === 'in_bearbeitung') {
        return (
          <div className="border-2 border-blue-200 bg-blue-50 rounded-lg">
            <div className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <Clock className="w-8 h-8 text-blue-600 animate-pulse" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {kalkulationStatus === 'eingereicht' ? 'Kalkulation eingereicht' : 'In Bearbeitung'}
              </h3>
              <p className="text-gray-600 mb-4">
                {kalkulationStatus === 'eingereicht' 
                  ? 'Ihre Akte wurde zur Kalkulation eingereicht und wartet auf Bearbeitung.'
                  : 'Die Kalkulation wird gerade von unserem Team bearbeitet.'}
              </p>
              <div className="inline-flex items-center px-4 py-2 bg-blue-100 rounded-full text-blue-800 text-sm font-medium">
                <Clock className="w-4 h-4 mr-2" />
                Eingereicht am: {kalkulationData?.eingereicht_am ? new Date(kalkulationData.eingereicht_am).toLocaleDateString('de-DE') : '-'}
              </div>
            </div>
          </div>
        );
      }
      
      if (kalkulationStatus === 'abgeschlossen' && kalkulationData?.ergebnis) {
        const e = kalkulationData.ergebnis;
        return (
          <div className="border-2 border-green-200 bg-green-50 rounded-lg">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-green-900">Kalkulation abgeschlossen</h3>
                    <p className="text-sm text-green-600">Fertiggestellt am: {kalkulationData.abgeschlossen_am ? new Date(kalkulationData.abgeschlossen_am).toLocaleDateString('de-DE') : '-'}</p>
                  </div>
                </div>
                {kalkulationData.pdf_url && (
                  <Button 
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${kalkulationData.pdf_url}`, '_blank')}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    PDF Download
                  </Button>
                )}
              </div>
              
              {/* Ergebnis-Übersicht */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {e.reparaturkosten_brutto && (
                  <div className="bg-white rounded-lg p-4 border border-green-100">
                    <p className="text-xs text-gray-500 uppercase font-medium">Reparaturkosten (Brutto)</p>
                    <p className="text-2xl font-bold text-gray-900">{new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(e.reparaturkosten_brutto)}</p>
                  </div>
                )}
                {e.wiederbeschaffungswert && (
                  <div className="bg-white rounded-lg p-4 border border-green-100">
                    <p className="text-xs text-gray-500 uppercase font-medium">Wiederbeschaffungswert</p>
                    <p className="text-2xl font-bold text-gray-900">{new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(e.wiederbeschaffungswert)}</p>
                  </div>
                )}
                {e.restwert && (
                  <div className="bg-white rounded-lg p-4 border border-green-100">
                    <p className="text-xs text-gray-500 uppercase font-medium">Restwert</p>
                    <p className="text-2xl font-bold text-gray-900">{new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(e.restwert)}</p>
                  </div>
                )}
                {e.reparaturdauer_tage && (
                  <div className="bg-white rounded-lg p-4 border border-green-100">
                    <p className="text-xs text-gray-500 uppercase font-medium">Reparaturdauer</p>
                    <p className="text-2xl font-bold text-gray-900">{e.reparaturdauer_tage} Tage</p>
                  </div>
                )}
              </div>
              
              {(e.totalschaden || e.wirtschaftlicher_totalschaden) && (
                <div className="mt-4 p-4 bg-red-100 border border-red-200 rounded-lg">
                  <p className="font-semibold text-red-800">
                    ⚠️ {e.wirtschaftlicher_totalschaden ? 'Wirtschaftlicher Totalschaden' : 'Totalschaden'}
                  </p>
                </div>
              )}
              
              {e.bemerkungen && (
                <div className="mt-4 p-4 bg-gray-100 rounded-lg">
                  <p className="text-sm text-gray-600">{e.bemerkungen}</p>
                </div>
              )}
            </div>
          </div>
        );
      }
      
      return null;
    };
    
    return (
    <div className="max-w-full 2xl:max-w-[1600px] mx-auto">
      {/* Modern Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-light text-gray-900 tracking-tight">Kalkulation</h2>
        <p className="text-sm text-gray-400 mt-1">Kosten und Reparaturkalkulation</p>
      </div>

      {/* Modus-Auswahl - Nur anzeigen wenn noch kein Modus gewählt */}
      {!kalkulationsModus && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Option 1: Wir kalkulieren */}
          <div 
            onClick={() => setKalkulationsModus('wir_kalkulieren')}
            className="bg-white rounded-xl shadow-sm border-2 border-gray-100 hover:border-blue-500 hover:shadow-lg p-8 cursor-pointer transition-all duration-200 group"
          >
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <UserCog className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Wir kalkulieren für Sie</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Reichen Sie Ihre Akte ein und unser Team erstellt die professionelle Schadenskalkulation für Sie.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Professionelle Kalkulation durch Experten
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Keine DAT-Kenntnisse erforderlich
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-gray-100">
              <span className="text-sm font-medium text-blue-600 group-hover:text-blue-700 flex items-center gap-2">
                Diese Option wählen
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </div>
          </div>

          {/* Option 2: Selbst kalkulieren */}
          <div 
            onClick={() => setKalkulationsModus('selbst_kalkulieren')}
            className="bg-white rounded-xl shadow-sm border-2 border-gray-100 hover:border-emerald-500 hover:shadow-lg p-8 cursor-pointer transition-all duration-200 group"
          >
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Selbst kalkulieren (DAT)</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Nutzen Sie DAT myClaim direkt hier, um Ihre eigene professionelle Schadenskalkulation zu erstellen.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Direkte Kontrolle über alle Details
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    DAT SilverDAT Integration
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-gray-100">
              <span className="text-sm font-medium text-emerald-600 group-hover:text-emerald-700 flex items-center gap-2">
                Diese Option wählen
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </div>
          </div>

          {/* Option 3: Restwertbörse */}
          <div 
            onClick={() => setKalkulationsModus('restwertboerse')}
            className="bg-white rounded-xl shadow-sm border-2 border-gray-100 hover:border-orange-500 hover:shadow-lg p-8 cursor-pointer transition-all duration-200 group"
          >
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <TrendingDown className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Restwertbörse (net.casion)</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Ermitteln Sie den Restwert des Fahrzeugs über die net.casion Restwertbörse.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Bundesweite Händlerangebote
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Marktgerechte Restwertermittlung
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-gray-100">
              <span className="text-sm font-medium text-orange-600 group-hover:text-orange-700 flex items-center gap-2">
                Diese Option wählen
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Zurück-Button wenn Modus gewählt */}
      {kalkulationsModus && (
        <div className="mb-6">
          <button
            onClick={() => setKalkulationsModus(null)}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-2 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Auswahl
          </button>
        </div>
      )}

      {/* Modus: Wir kalkulieren */}
      {kalkulationsModus === 'wir_kalkulieren' && (
        <>
          {/* Kalkulations-Status Card */}
          {renderKalkulationStatusCard()}
        </>
      )}

      {/* Modus: Selbst kalkulieren mit DAT */}
      {kalkulationsModus === 'selbst_kalkulieren' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-50 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                <Calculator className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800">DAT myClaim Kalkulation</h3>
                <p className="text-xs text-gray-400">Professionelle Schadenskalkulation selbst erstellen</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            {/* Kalkulations-Typ Auswahl */}
            <div className="mb-6">
              <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">Kalkulationsart wählen</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button
                  onClick={() => {/* Wird über ContractForm gehandelt */}}
                  className="p-4 rounded-lg border-2 border-blue-500 bg-blue-50 text-center transition-all"
                >
                  <Calculator className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-900">Schadenkalkulation</p>
                  <p className="text-xs text-gray-500">Karosserie & Lack</p>
                </button>
                <button
                  onClick={() => {/* Glasberechnung */}}
                  className="p-4 rounded-lg border-2 border-gray-200 hover:border-blue-300 bg-white text-center transition-all"
                >
                  <Car className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-900">Glasberechnung</p>
                  <p className="text-xs text-gray-500">Frontscheibe etc.</p>
                </button>
                <button
                  onClick={() => {/* Fahrzeugbewertung */}}
                  className="p-4 rounded-lg border-2 border-gray-200 hover:border-blue-300 bg-white text-center transition-all"
                >
                  <BarChart3 className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-900">Fahrzeugbewertung</p>
                  <p className="text-xs text-gray-500">Wiederbeschaffung</p>
                </button>
              </div>
            </div>

            {/* DAT ContractForm Komponente */}
            <ContractForm
              type="vro_calculation"
              initialData={{
                name: formData.aktenzeichen || `Auftrag_${Date.now()}`,
                owner: {
                  firstName: formData.kunde?.split(' ')[0] || '',
                  lastName: formData.kunde?.split(' ').slice(1).join(' ') || '',
                  street: formData.adresse1 || '',
                  zipCode: formData.adresse2?.match(/\d{5}/)?.[0] || '',
                  city: formData.adresse2?.replace(/\d{5}\s*/, '') || ''
                },
                vehicle: {
                  vin: formData.vin || '',
                  licensePlate: formData.kennzeichen || '',
                  mileage: formData.laufleistung || ''
                }
              }}
              onContractCreated={(claimId, type) => {
                console.log('DAT Contract erstellt:', claimId);
                setDatClaimId(claimId);
                toast.success('DAT Kalkulation gestartet');
              }}
              onCalculationComplete={(data, claimId, type) => {
                console.log('Kalkulation erhalten:', data);
                setDatKalkulationsDaten(data);
                // Speichere die Kalkulationsdaten in formData
                setFormData(prev => ({
                  ...prev,
                  dat_claim_id: claimId,
                  kalkulation_brutto: data.calculation?.totalGross,
                  kalkulation_netto: data.calculation?.totalNet,
                  kalkulation_mwst: data.calculation?.totalVat,
                  kalkulation_ersatzteile: data.calculation?.sparePartsTotal,
                  kalkulation_arbeitslohn: data.calculation?.labourTotal,
                  kalkulation_lackierung: data.calculation?.lacquerTotal
                }));
              }}
              onSaved={(data, claimId, type) => {
                toast.success('Kalkulation gespeichert');
              }}
            />

            {/* Kalkulationsergebnis Zusammenfassung */}
            {datKalkulationsDaten?.calculation && (
              <div className="mt-6 p-6 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
                <h4 className="text-sm font-semibold text-emerald-800 mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Kalkulationsergebnis übernommen
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Gesamt Brutto</p>
                    <p className="text-lg font-bold text-gray-900">
                      {parseFloat(datKalkulationsDaten.calculation.totalGross || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
                    </p>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Gesamt Netto</p>
                    <p className="text-lg font-bold text-gray-900">
                      {parseFloat(datKalkulationsDaten.calculation.totalNet || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
                    </p>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Ersatzteile</p>
                    <p className="text-lg font-bold text-gray-900">
                      {parseFloat(datKalkulationsDaten.calculation.sparePartsTotal || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
                    </p>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Arbeitslohn</p>
                    <p className="text-lg font-bold text-gray-900">
                      {parseFloat(datKalkulationsDaten.calculation.labourTotal || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modus: Restwertbörse (net.casion) */}
      {kalkulationsModus === 'restwertboerse' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-50 bg-gradient-to-r from-orange-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-sm">
                <TrendingDown className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Restwertbörse (net.casion)</h3>
                <p className="text-xs text-gray-400">Fahrzeug bei Händlern inserieren und Angebote erhalten</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            {/* Workflow-Erklärung */}
            <div className="mb-6 p-4 bg-orange-50 rounded-lg border border-orange-100">
              <h4 className="font-medium text-orange-800 mb-2 flex items-center gap-2">
                <Info className="w-4 h-4" />
                So funktioniert die Restwertbörse
              </h4>
              <ol className="text-sm text-orange-700 space-y-1 list-decimal list-inside">
                <li>Fahrzeugdaten und Bilder werden an net.casion übermittelt</li>
                <li>Händler deutschlandweit können Angebote für das Fahrzeug abgeben</li>
                <li>Sie können die eingegangenen Angebote hier abrufen und vergleichen</li>
              </ol>
            </div>

            {/* Fahrzeug-Zusammenfassung */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Fahrzeugdaten für Restwertermittlung</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Hersteller</p>
                  <p className="font-medium">{formData.hersteller || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Modell</p>
                  <p className="font-medium">{formData.modell || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">FIN</p>
                  <p className="font-medium">{formData.vin || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Laufleistung</p>
                  <p className="font-medium">{formData.laufleistung ? `${formData.laufleistung} km` : '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Erstzulassung</p>
                  <p className="font-medium">{formData.erstzulassung || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Kraftstoff</p>
                  <p className="font-medium">{formData.kraftstoff || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Standort PLZ</p>
                  <p className="font-medium">{formData.adresse2?.match(/\d{5}/)?.[0] || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Reparaturkosten</p>
                  <p className="font-medium">{formData.reparaturkosten ? `${formData.reparaturkosten} €` : '-'}</p>
                </div>
              </div>
            </div>

            {/* Bilder-Auswahl Info */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-sm text-blue-700">
                <strong>Hinweis:</strong> Die Lichtbilder aus dem Tab "Lichtbilder" werden automatisch mit übermittelt. 
                Mehr Bilder = bessere Angebote!
              </p>
              {lichtbilder && lichtbilder.length > 0 && (
                <p className="text-sm text-blue-600 mt-2 font-medium">
                  ✓ {lichtbilder.filter(b => b.type === 'image').length} Bilder vorhanden und bereit zum Senden
                </p>
              )}
              {(!lichtbilder || lichtbilder.length === 0) && (
                <p className="text-sm text-orange-600 mt-2">
                  ⚠️ Noch keine Lichtbilder hochgeladen. Fügen Sie Bilder unter "Lichtbilder" hinzu für bessere Angebote.
                </p>
              )}
            </div>

            {/* Aktions-Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={async () => {
                  if (!formData.vin || !formData.hersteller || !formData.modell) {
                    toast.error('Bitte zuerst Fahrzeugdaten eingeben (FIN, Hersteller, Modell)');
                    return;
                  }
                  
                  try {
                    const API_URL = process.env.REACT_APP_BACKEND_URL;
                    const token = localStorage.getItem('autogutachter24_token');
                    
                    if (!token) {
                      toast.error('Sitzung abgelaufen. Bitte melden Sie sich erneut an.');
                      return;
                    }
                    
                    // Bilder aus dem lichtbilder State sammeln
                    const images = [];
                    if (lichtbilder && Array.isArray(lichtbilder) && lichtbilder.length > 0) {
                      toast.loading(`${lichtbilder.length} Bilder werden vorbereitet...`, { id: 'restwert' });
                      
                      for (const bild of lichtbilder.slice(0, 15)) { // Max 15 Bilder
                        // Prüfe ob es ein Bild ist (type === 'image' oder url enthält image)
                        if (bild.type === 'image' || (bild.url && bild.url.includes('image'))) {
                          if (bild.url) {
                            if (bild.url.startsWith('data:image')) {
                              // Base64 Data-URL - extrahiere den Base64-Teil
                              const base64 = bild.url.split(',')[1];
                              if (base64) images.push(base64);
                            } else if (bild.url.startsWith('http')) {
                              // URL zu einem Bild - lade und konvertiere zu Base64
                              try {
                                const imgResponse = await fetch(bild.url);
                                const blob = await imgResponse.blob();
                                const reader = new FileReader();
                                const base64Promise = new Promise((resolve) => {
                                  reader.onloadend = () => {
                                    const base64 = reader.result.split(',')[1];
                                    resolve(base64);
                                  };
                                });
                                reader.readAsDataURL(blob);
                                const base64 = await base64Promise;
                                if (base64) images.push(base64);
                              } catch (imgError) {
                                console.warn('Konnte Bild nicht laden:', bild.url, imgError);
                              }
                            }
                          }
                        }
                      }
                    }
                    
                    console.log(`📸 ${images.length} Bilder für Restwertbörse vorbereitet`);
                    
                    toast.loading(`Fahrzeug wird an Restwertbörse übermittelt${images.length > 0 ? ` (mit ${images.length} Bildern)` : ''}...`, { id: 'restwert' });
                    
                    const response = await fetch(`${API_URL}/api/restwert/submit-vehicle`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                      },
                      body: JSON.stringify({
                        vin: formData.vin,
                        manufacturer: formData.hersteller,
                        model: formData.modell,
                        vehicle_type: formData.fahrzeugart || 'PKW',
                        mileage: parseInt(formData.laufleistung) || 0,
                        year_of_construction: formData.erstzulassung ? parseInt(formData.erstzulassung.split('.').pop() || formData.erstzulassung.split('-')[0]) : new Date().getFullYear() - 5,
                        engine_type: formData.kraftstoff || 'Benzin',
                        location_postal_code: formData.adresse2?.match(/\d{5}/)?.[0] || '10115',
                        cost_of_repair: parseFloat(formData.reparaturkosten) || undefined,
                        images: images.length > 0 ? images : undefined
                      })
                    });
                    
                    const result = await response.json();
                    
                    if (result.success) {
                      const bildInfo = images.length > 0 ? ` (${images.length} Bilder übermittelt)` : '';
                      toast.success(`Fahrzeug erfolgreich eingestellt!${bildInfo} ID: ${result.vehicle_id_extern}`, { id: 'restwert' });
                      // Speichere die IDs im formData für späteren Abruf
                      setFormData(prev => ({
                        ...prev,
                        restwert_vehicle_id: result.vehicle_id,
                        restwert_vehicle_id_extern: result.vehicle_id_extern
                      }));
                    } else {
                      toast.error(result.message || 'Fehler beim Einreichen', { id: 'restwert' });
                    }
                  } catch (error) {
                    toast.error('Verbindungsfehler: ' + error.message, { id: 'restwert' });
                  }
                }}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-lg font-medium hover:from-orange-600 hover:to-red-700 transition-all flex items-center justify-center gap-2"
                data-testid="restwert-submit-btn"
              >
                <Upload className="w-5 h-5" />
                Fahrzeug in Restwertbörse einstellen
              </button>
              
              {formData.restwert_vehicle_id && (
                <button
                  onClick={async () => {
                    try {
                      const API_URL = process.env.REACT_APP_BACKEND_URL;
                      const token = localStorage.getItem('autogutachter24_token');
                      
                      if (!token) {
                        toast.error('Sitzung abgelaufen. Bitte melden Sie sich erneut an.');
                        return;
                      }
                      
                      toast.loading('Angebote werden abgerufen...', { id: 'offers' });
                      
                      const response = await fetch(
                        `${API_URL}/api/restwert/retrieve-offers/${formData.restwert_vehicle_id}?vehicle_id_extern=${formData.restwert_vehicle_id_extern}`,
                        {
                          headers: { 'Authorization': `Bearer ${token}` }
                        }
                      );
                      
                      const result = await response.json();
                      
                      if (result.success) {
                        if (result.offers && result.offers.length > 0) {
                          toast.success(`${result.offers.length} Angebot(e) gefunden!`, { id: 'offers' });
                          setFormData(prev => ({
                            ...prev,
                            restwert_angebote: result.offers
                          }));
                        } else {
                          toast.info('Noch keine Angebote eingegangen. Bitte später erneut prüfen.', { id: 'offers' });
                        }
                      } else {
                        toast.error(result.message || 'Fehler beim Abrufen', { id: 'offers' });
                      }
                    } catch (error) {
                      toast.error('Verbindungsfehler: ' + error.message, { id: 'offers' });
                    }
                  }}
                  className="px-6 py-3 bg-white border-2 border-orange-500 text-orange-600 rounded-lg font-medium hover:bg-orange-50 transition-all flex items-center justify-center gap-2"
                  data-testid="restwert-refresh-btn"
                >
                  <RefreshCw className="w-5 h-5" />
                  Angebote aktualisieren
                </button>
              )}
            </div>

            {/* Status-Anzeige wenn Fahrzeug eingestellt */}
            {formData.restwert_vehicle_id && (
              <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-green-800">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Fahrzeug in Restwertbörse eingestellt</span>
                  </div>
                  {/* Auto-Polling Toggle */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Auto-Abfrage:</span>
                    <button
                      onClick={() => setRestwertAutoPolling(!restwertAutoPolling)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        restwertAutoPolling ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        restwertAutoPolling ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-green-700">
                      net.casion ID: <strong>{formData.restwert_vehicle_id_extern}</strong>
                    </p>
                  </div>
                  <div className="text-right">
                    {restwertPollingActive && (
                      <span className="inline-flex items-center gap-1 text-blue-600 text-xs">
                        <RefreshCw className="w-3 h-3 animate-spin" />
                        Prüfe auf neue Angebote...
                      </span>
                    )}
                    {!restwertPollingActive && restwertLastCheck && (
                      <span className="text-xs text-gray-500">
                        Letzte Prüfung: {restwertLastCheck.toLocaleTimeString('de-DE')}
                      </span>
                    )}
                  </div>
                </div>
                
                {restwertAutoPolling && (
                  <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Automatische Abfrage alle 5 Minuten aktiv
                  </p>
                )}
                {!restwertAutoPolling && (
                  <p className="text-xs text-gray-500 mt-2">
                    Automatische Abfrage deaktiviert. Klicken Sie auf "Angebote aktualisieren" für manuelle Prüfung.
                  </p>
                )}
              </div>
            )}

            {/* Angebote-Liste - Verbesserte Darstellung */}
            {formData.restwert_angebote && formData.restwert_angebote.length > 0 && (
              <div className="mt-6">
                {/* Header mit Anzahl und höchstem Angebot */}
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                    <TrendingDown className="w-5 h-5 text-orange-500" />
                    Eingegangene Angebote
                    <span className="bg-orange-500 text-white text-sm px-2 py-0.5 rounded-full">
                      {formData.restwert_angebote.length}
                    </span>
                  </h4>
                  
                  {/* Höchstes Angebot Badge */}
                  {formData.restwert_angebote.length > 0 && (
                    <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-lg shadow-md">
                      <p className="text-xs opacity-90">Höchstes Angebot</p>
                      <p className="text-xl font-bold">
                        {Math.max(...formData.restwert_angebote.map(a => parseFloat(a.offer_value_eur || a.offer_value || 0))).toLocaleString('de-DE')} €
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Angebote-Karten */}
                <div className="grid gap-3">
                  {formData.restwert_angebote
                    .sort((a, b) => parseFloat(b.offer_value_eur || b.offer_value || 0) - parseFloat(a.offer_value_eur || a.offer_value || 0))
                    .map((angebot, idx) => {
                      const isHighest = idx === 0;
                      const offerValue = parseFloat(angebot.offer_value_eur || angebot.offer_value || 0);
                      
                      return (
                        <div 
                          key={angebot.offer_id || idx} 
                          className={`p-4 rounded-lg border-2 transition-all ${
                            isHighest 
                              ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-400 shadow-md' 
                              : 'bg-white border-gray-200 hover:border-orange-300'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-4">
                              {/* Rang-Badge */}
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                                isHighest 
                                  ? 'bg-green-500 text-white' 
                                  : 'bg-gray-200 text-gray-600'
                              }`}>
                                {idx + 1}
                              </div>
                              
                              <div>
                                <p className={`font-semibold ${isHighest ? 'text-green-800' : 'text-gray-900'}`}>
                                  {angebot.company_name || 'Händler'}
                                  {isHighest && <span className="ml-2 text-xs bg-green-500 text-white px-2 py-0.5 rounded">BESTES ANGEBOT</span>}
                                </p>
                                <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                                  {angebot.location && (
                                    <span className="flex items-center gap-1">
                                      📍 {angebot.postal_code} {angebot.location}
                                    </span>
                                  )}
                                  {angebot.distance && (
                                    <span>🚗 {angebot.distance} km</span>
                                  )}
                                </div>
                                {angebot.valid_until && (
                                  <p className="text-xs text-gray-400 mt-1">
                                    ⏰ Gültig bis: {new Date(angebot.valid_until).toLocaleDateString('de-DE')}
                                  </p>
                                )}
                              </div>
                            </div>
                            
                            <div className="text-right">
                              <p className={`text-2xl font-bold ${isHighest ? 'text-green-600' : 'text-orange-600'}`}>
                                {offerValue.toLocaleString('de-DE')} €
                              </p>
                              {angebot.creation_time && (
                                <p className="text-xs text-gray-400">
                                  {new Date(angebot.creation_time).toLocaleDateString('de-DE')}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bestehende Tabs - Nur anzeigen wenn Modus "wir_kalkulieren" gewählt */}
      {kalkulationsModus === 'wir_kalkulieren' && (kalkulationStatus === 'nicht_eingereicht' || !kalkulationStatus || kalkulationStatus === 'abgeschlossen') && (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mt-6">
        <div className="p-6">
          <Tabs defaultValue="instandsetzung" className="w-full">
            <TabsList className="w-full justify-start bg-transparent border-b border-gray-100 rounded-none h-auto p-0 mb-6">
              <TabsTrigger value="instandsetzung" className="text-sm px-4 py-2.5 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none transition-all">
                Instandsetzung
              </TabsTrigger>
              <TabsTrigger value="restwert" className="text-sm px-4 py-2.5 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none transition-all">
                Restwert
              </TabsTrigger>
              <TabsTrigger value="minderwert" className="text-sm px-4 py-2.5 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none transition-all">
                Minderwert
              </TabsTrigger>
              <TabsTrigger value="nutzungsausfall" className="text-sm px-4 py-2.5 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none transition-all">
                Nutzungsausfall
              </TabsTrigger>
              <TabsTrigger value="wiederbeschaffungswert" className="text-sm px-4 py-2.5 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none">
                Wiederbeschaffungswert
              </TabsTrigger>
            </TabsList>

            {/* Instandsetzung Tab */}
            <TabsContent value="instandsetzung" className="space-y-6">
              <div className="text-center py-8">
                <p className="text-gray-500">Instandsetzung-Funktionalität wird hier implementiert.</p>
              </div>
            </TabsContent>

            {/* Restwert Tab */}
            <TabsContent value="restwert" className="space-y-6">
              <RestwertAbfrage formData={formData} />
            </TabsContent>

            {/* Minderwert Tab */}
            <TabsContent value="minderwert" className="space-y-6">
              <div className="grid grid-cols-12 gap-6">
                {/* Linke Spalte - Methodenauswahl */}
                <div className="col-span-3 space-y-2">
                  <h3 className="text-sm font-bold text-gray-900 mb-3">Methodenauswahl</h3>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-gray-50 rounded">
                      <input type="checkbox" className="w-4 h-4 text-blue-600 rounded" defaultChecked />
                      <span className="text-sm text-gray-700">BVSK</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-gray-50 rounded">
                      <input type="checkbox" className="w-4 h-4 text-blue-600 rounded" defaultChecked />
                      <span className="text-sm text-gray-700">MFM</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-gray-50 rounded">
                      <input type="checkbox" className="w-4 h-4 text-blue-600 rounded" defaultChecked />
                      <span className="text-sm text-gray-700">Hälfteverechs</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-gray-50 rounded">
                      <input type="checkbox" className="w-4 h-4 text-blue-600 rounded" defaultChecked />
                      <span className="text-sm text-gray-700">Dr. Trömer</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-gray-50 rounded">
                      <input type="checkbox" className="w-4 h-4 text-blue-600 rounded" defaultChecked />
                      <span className="text-sm text-gray-700">Ruhkopf/Sohn</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-gray-50 rounded">
                      <input type="checkbox" className="w-4 h-4 text-blue-600 rounded" />
                      <span className="text-sm text-gray-700">Hamburger</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-gray-50 rounded">
                      <input type="checkbox" className="w-4 h-4 text-blue-600 rounded" />
                      <span className="text-sm text-gray-700">HTS</span>
                    </label>
                  </div>
                </div>

                {/* Rechte Spalte - Formulare */}
                <div className="col-span-9 space-y-6">
                  {/* Basiswerte */}
                  <div className="bg-white border rounded-lg p-4">
                    <h3 className="text-sm font-bold text-gray-900 mb-4">Basiswerte</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Neupreis *</label>
                        <Input type="number" placeholder="0" className="h-10 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Fahrzeugsalter (Monate) *</label>
                        <Input type="number" placeholder="0" className="h-10 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Neuere Basiswerte verwenden</label>
                        <label className="flex items-center space-x-2 mt-2">
                          <input type="checkbox" className="w-4 h-4 text-blue-600 rounded" />
                          <span className="text-xs text-gray-600">Aktivieren</span>
                        </label>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Wiederbeschaffungswert *</label>
                        <Input type="number" placeholder="0" className="h-10 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Gesamt Reparaturkosten *</label>
                        <Input type="number" placeholder="0" className="h-10 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Verkaufssumme *</label>
                        <Input type="number" placeholder="0" className="h-10 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Lackierung *</label>
                        <Input type="number" placeholder="0" className="h-10 text-sm" />
                      </div>
                    </div>
                  </div>

                  {/* BVSK */}
                  <div className="bg-white border rounded-lg p-4">
                    <h3 className="text-sm font-bold text-gray-900 mb-4">BVSK</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Fahrzeugtyp *</label>
                        <select className="h-10 w-full text-sm px-3 py-2 border border-gray-300 rounded-md">
                          <option>PKW</option>
                          <option>LKW</option>
                          <option>Motorrad</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Schadensklasse *</label>
                        <select className="h-10 w-full text-sm px-3 py-2 border border-gray-300 rounded-md">
                          <option>Leicht</option>
                          <option>Mittel</option>
                          <option>Schwer</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Karosserieteilar *</label>
                        <Input type="number" placeholder="0" className="h-10 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Marktgängigkeit *</label>
                        <select className="h-10 w-full text-sm px-3 py-2 border border-gray-300 rounded-md">
                          <option>Durchschnittlich</option>
                          <option>Gut</option>
                          <option>Sehr gut</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Marktrelevanz- und Faktormethode (MFM) */}
                  <div className="bg-white border rounded-lg p-4">
                    <h3 className="text-sm font-bold text-gray-900 mb-4">Marktrelevanz- und Faktormethode (MFM)</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Schadenumfang *</label>
                        <select className="h-10 w-full text-sm px-3 py-2 border border-gray-300 rounded-md">
                          <option>Gering</option>
                          <option>Mittel</option>
                          <option>Hoch</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Arbeitslohn *</label>
                        <Input type="number" placeholder="0" className="h-10 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Vorschäden *</label>
                        <Input type="number" placeholder="0" className="h-10 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Materialkosten *</label>
                        <Input type="number" placeholder="0" className="h-10 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Marktgängigkeit *</label>
                        <select className="h-10 w-full text-sm px-3 py-2 border border-gray-300 rounded-md">
                          <option>Durchschnittlich</option>
                          <option>Gut</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Hälfteverechs */}
                  <div className="bg-white border rounded-lg p-4">
                    <h3 className="text-sm font-bold text-gray-900 mb-4">Hälfteverechs</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Arbeitslohn *</label>
                        <Input type="number" placeholder="0" className="h-10 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Materialkosten *</label>
                        <Input type="number" placeholder="0" className="h-10 text-sm" />
                      </div>
                    </div>
                  </div>

                  {/* Dr. Trömer */}
                  <div className="bg-white border rounded-lg p-4">
                    <h3 className="text-sm font-bold text-gray-900 mb-4">Dr. Trömer</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Fahrzeugschäden *</label>
                        <Input type="number" placeholder="0" className="h-10 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Marktgängigkeit *</label>
                        <Input type="number" placeholder="0" className="h-10 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Verschleißen *</label>
                        <Input type="number" placeholder="0" className="h-10 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Neuvertreibelt *</label>
                        <Input type="number" placeholder="0" className="h-10 text-sm" />
                      </div>
                    </div>
                  </div>

                  {/* Hamburger Modell */}
                  <div className="bg-white border rounded-lg p-4">
                    <h3 className="text-sm font-bold text-gray-900 mb-4">Hamburger Modell</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Erstellete Reparaturkosten *</label>
                        <Input type="number" placeholder="0" className="h-10 text-sm" />
                      </div>
                    </div>
                  </div>

                  {/* HTS */}
                  <div className="bg-white border rounded-lg p-4">
                    <h3 className="text-sm font-bold text-gray-900 mb-4">HTS</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Werteeinsteufung *</label>
                        <select className="h-10 w-full text-sm px-3 py-2 border border-gray-300 rounded-md">
                          <option>Standard</option>
                          <option>Gehoben</option>
                          <option>Luxus</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Schadenintensität-Klasse *</label>
                        <div className="grid grid-cols-2 gap-2">
                          <Input type="number" placeholder="K1" className="h-10 text-sm" />
                          <Input type="number" placeholder="K2" className="h-10 text-sm" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Kennzahlentabelle *</label>
                        <Input type="text" placeholder="Standard" className="h-10 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Marktlage *</label>
                        <select className="h-10 w-full text-sm px-3 py-2 border border-gray-300 rounded-md">
                          <option>Normal</option>
                          <option>Gut</option>
                          <option>Sehr gut</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Ruhkopf/Sohn */}
                  <div className="bg-white border rounded-lg p-4">
                    <h3 className="text-sm font-bold text-gray-900 mb-4">Ruhkopf/Sohn</h3>
                    <div className="text-sm text-gray-500 text-center py-4">
                      Keine zusätzlichen Parameter erforderlich
                    </div>
                  </div>

                  {/* Berechnen Button */}
                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button type="button" variant="outline">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Zurück
                    </Button>
                    <Button type="button" className="bg-blue-600 hover:bg-blue-700 text-white px-8">
                      Berechnen
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Nutzungsausfall Tab */}
            <TabsContent value="nutzungsausfall" className="space-y-6">
              <div className="border-l-4 border-blue-500 pl-4 mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Nutzungsausfallentschädigung (nach Schwacke/Sanden-Danner 2025)</h3>
                <p className="text-sm text-gray-600">Berechnung der Nutzungsausfallentschädigung nach anerkannter Tabelle</p>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-800">
                      <strong>Automatische Ermittlung:</strong> Die Fahrzeugklasse wird automatisch über die DAT Mietwagenspiegel-Schnittstelle ermittelt, 
                      wenn eine VIN/FIN vorhanden ist. Die Berechnung erfolgt nach der Schwacke/Sanden-Danner-Tabelle 2025 mit automatischer Altersabstufung.
                      <br />
                      Fahrzeuge ab 5 Jahren: -1 Klasse, ab 10 Jahren: -2 Klassen.
                    </p>
                  </div>
                </div>
              </div>

              {/* Eingabeformular */}
              <div className="bg-white rounded-md border border-gray-200">
                <div className="p-6">
                  <div className="space-y-6">
                    {/* Fahrzeugklasse nach Schwacke-Tabelle */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">
                        Fahrzeugklasse (Schwacke/Sanden-Danner) *
                        {mietwagenspiegelLoading && (
                          <span className="ml-2 text-xs text-blue-600">
                            <span className="inline-block animate-spin mr-1">⟳</span>
                            Wird über DAT ermittelt...
                          </span>
                        )}
                      </label>
                      <select 
                        className="h-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={nutzungsausfallData.fahrzeugklasse}
                        onChange={(e) => setNutzungsausfallData({...nutzungsausfallData, fahrzeugklasse: e.target.value})}
                        disabled={mietwagenspiegelLoading}
                      >
                        <option value="">
                          {mietwagenspiegelLoading ? 'Wird ermittelt...' : 'Bitte wählen oder VIN eingeben...'}
                        </option>
                        <option value="A">Klasse A - 23 €/Tag (Kleinwagen)</option>
                        <option value="B">Klasse B - 29 €/Tag (Kompaktklasse)</option>
                        <option value="C">Klasse C - 35 €/Tag (Untere Mittelklasse)</option>
                        <option value="D">Klasse D - 38 €/Tag (Mittlere Mittelklasse)</option>
                        <option value="E">Klasse E - 42 €/Tag (Obere Mittelklasse)</option>
                        <option value="F">Klasse F - 50 €/Tag (Audi A4, Mercedes C-Klasse)</option>
                        <option value="G">Klasse G - 59 €/Tag (Audi A6, BMW 5er)</option>
                        <option value="H">Klasse H - 65 €/Tag (Mercedes E-Klasse, Lexus ES)</option>
                        <option value="J">Klasse J - 79 €/Tag (BMW X5)</option>
                        <option value="K">Klasse K - 100 €/Tag (Oberklasse)</option>
                        <option value="L">Klasse L - 175 €/Tag (Luxusklasse: Porsche 911, Mercedes SL)</option>
                        <option value="klasse-b">Klasse B - Kompaktklasse</option>
                        <option value="klasse-c">Klasse C - Mittelklasse</option>
                        <option value="klasse-d">Klasse D - Obere Mittelklasse</option>
                        <option value="klasse-e">Klasse E - Oberklasse</option>
                        <option value="klasse-f">Klasse F - Luxusklasse</option>
                        <option value="suv">SUV</option>
                        <option value="van">Van / Transporter</option>
                      </select>
                    </div>

                    {/* Fahrzeugalter */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">
                        Fahrzeugalter (Jahre)
                      </label>
                      <Input 
                        type="number" 
                        placeholder="z.B. 3" 
                        className="h-10 border-gray-300"
                        value={nutzungsausfallData.fahrzeugalter_jahre}
                        onChange={(e) => setNutzungsausfallData({...nutzungsausfallData, fahrzeugalter_jahre: e.target.value})}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Fahrzeuge ab 5 Jahren: -1 Klasse | Ab 10 Jahren: -2 Klassen
                      </p>
                    </div>

                    {/* Ausfalltage */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">
                        Anzahl Ausfalltage *
                      </label>
                      <Input 
                        type="number" 
                        placeholder="z.B. 14" 
                        className="h-10 border-gray-300"
                        value={nutzungsausfallData.ausfalltage}
                        onChange={(e) => setNutzungsausfallData({...nutzungsausfallData, ausfalltage: e.target.value})}
                      />
                    </div>

                    {/* Berechnen Button */}
                    <div className="flex justify-end gap-3 pt-4 border-t">
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={() => {
                          setNutzungsausfallData({
                            fahrzeugklasse: '',
                            ausfalltage: '',
                            fahrzeugalter_jahre: ''
                          });
                          setNutzungsausfallErgebnis(null);
                        }}
                      >
                        Zurücksetzen
                      </Button>
                      <Button 
                        type="button" 
                        onClick={berechneNutzungsausfall}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-8"
                      >
                        <Calculator className="w-4 h-4 mr-2" />
                        Berechnen
                      </Button>
                    </div>

                    {/* Ergebnisanzeige */}
                    {nutzungsausfallErgebnis && (
                      <div className="mt-6 p-6 bg-green-50 border-2 border-green-200 rounded-lg">
                        <h4 className="text-lg font-bold text-green-900 mb-4 flex items-center">
                          <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Berechnungsergebnis
                        </h4>

                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <p className="text-sm text-gray-600">Fahrzeugklasse (ursprünglich)</p>
                            <p className="text-lg font-bold text-gray-900">{nutzungsausfallErgebnis.urspruenglicheKlasse}</p>
                          </div>
                          {nutzungsausfallErgebnis.altersrabatt && (
                            <div>
                              <p className="text-sm text-gray-600">Angewendete Klasse (mit Altersrabatt)</p>
                              <p className="text-lg font-bold text-orange-600">{nutzungsausfallErgebnis.angewendeteKlasse}</p>
                            </div>
                          )}
                          <div>
                            <p className="text-sm text-gray-600">Tagessatz</p>
                            <p className="text-lg font-bold text-gray-900">{nutzungsausfallErgebnis.tagessatz} €</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Ausfalltage</p>
                            <p className="text-lg font-bold text-gray-900">{nutzungsausfallErgebnis.ausfalltage} Tage</p>
                          </div>
                        </div>

                        <div className="pt-4 border-t border-green-300">
                          <p className="text-sm text-gray-600 mb-2">Gesamte Nutzungsausfallentschädigung</p>
                          <p className="text-3xl font-bold text-green-700">{nutzungsausfallErgebnis.gesamtsumme} €</p>
                          {nutzungsausfallErgebnis.altersrabatt && (
                            <p className="text-xs text-orange-600 mt-2">
                              ⚠️ Altersrabatt wurde angewendet
                            </p>
                          )}
                        </div>

                        <div className="mt-4 text-xs text-gray-500">
                          <p>Berechnung nach Schwacke/Sanden-Danner-Tabelle 2025</p>
                          <p>Formel: Tagessatz × Ausfalltage = {nutzungsausfallErgebnis.tagessatz} € × {nutzungsausfallErgebnis.ausfalltage} = {nutzungsausfallErgebnis.gesamtsumme} €</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Wiederbeschaffungswert Tab */}
            <TabsContent value="wiederbeschaffungswert" className="space-y-6">
              <DATWiederbeschaffungswert formData={formData} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-end gap-3 mt-6">
        <Button 
          type="button" 
          variant="outline" 
          onClick={() => setActiveSection('lichtbilder')} 
          className="text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg px-5 transition-all"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>
        <Button 
          type="button" 
          onClick={() => setActiveSection('rechnung')} 
          className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg px-6 shadow-md hover:shadow-lg transition-all flex items-center gap-2"
        >
          Weiter zu Rechnung
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
  };

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'akte':
        return renderAkteForm();
      case 'beteiligte':
        return renderBeteiligteForm();
      case 'abtretung':
        return renderUnterschriftForm();
      // case 'besichtigungen':  // VORERST VERSTECKT
      //   return renderBesichtigungenForm();
      case 'kalkulation':
        return renderKalkulationForm();
      case 'begutachtung':
        return renderBegutachtungForm();
      case 'lichtbilder':
        return renderLichtbilderForm();
      case 'rechnung':
        return renderRechnungForm();
      case 'dokumente':
        return renderDokumenteForm();
      case 'email':
        return renderEmailForm();
      case 'leer':
        return renderLeerForm();
      case 'abtretung':
        return renderUnterschriftForm();
      default:
        return renderAkteForm();
    }
  };

  const renderRechnungForm = () => (
    <div className="max-w-full 2xl:max-w-[1500px] mx-auto space-y-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3">
          <FileText className="w-6 h-6" />
          <h2 className="text-xl font-semibold">Rechnung</h2>
        </div>
        <p className="text-sm text-gray-500 mt-1">Rechnungsverwaltung und Zahlungsübersicht</p>
      </div>

      {/* Content Container - Angepasst an schmalere Felder */}
      <div className="border-0 shadow-lg max-w-full 2xl:max-w-[1500px] mx-auto">
        <div className="pt-4 px-6">
          <div className="flex justify-between items-start mb-6">
            {/* Linke Seite - Rechnung Details */}
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Rechnungsdetails</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-gray-600">Rechnungsnummer</span>
                  <p className="font-semibold">ZS-0493</p>
                </div>
                <div>
                  <span className="text-gray-600">Vorgang Nr.</span>
                  <p className="text-blue-600 font-semibold">ZS-0493</p>
                </div>
                <div>
                  <span className="text-gray-600">Zahlungsziel</span>
                  <p className="font-semibold">22. November 2025</p>
                </div>
                <div>
                  <span className="text-gray-600">Rechnungsdatum</span>
                  <p className="font-semibold">8. November 2025</p>
                </div>
              </div>
            </div>

            {/* Mittlere Seite - Status */}
            <div className="space-y-4">
              <div>
                <span className="text-gray-600 text-sm">Status</span>
                <div className="mt-1">
                  <span className="inline-flex px-3 py-1 text-xs font-semibold rounded bg-blue-500 text-white">
                    Offen
                  </span>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-gray-600">Letzte Zahlung</span>
                  <p className="font-semibold">-</p>
                </div>
                <div>
                  <span className="text-gray-600">Betrag</span>
                  <p className="font-semibold">988,38 EUR</p>
                </div>
                <div>
                  <span className="text-gray-600">Ausgeglichen</span>
                  <p className="font-semibold">0,00 EUR</p>
                </div>
                <div>
                  <span className="text-gray-600">Offen</span>
                  <p className="font-semibold text-red-600">988,38 EUR</p>
                </div>
              </div>
            </div>

            {/* Rechte Seite - Bemerkungen */}
            <div className="w-64">
              <label className="text-gray-600 text-sm">Bemerkungen</label>
              <Textarea rows={4} className="mt-1 text-sm" />
              <div className="mt-2">
                <label className="text-gray-600 text-sm">Arbeitsschritt</label>
                <select className="h-10 w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-md">
                  <option>Auswahl...</option>
                </select>
              </div>
              <div className="mt-2">
                <label className="text-gray-600 text-sm">Wiedervorlage</label>
                <Input type="date" className="h-10 mt-1 text-sm" />
              </div>
              <div className="mt-2 flex gap-1">
                <Button size="sm" className="text-xs">1W</Button>
                <Button size="sm" className="text-xs">2W</Button>
                <Button size="sm" className="text-xs">6W</Button>
                <Button size="sm" className="text-xs">1M</Button>
                <Button size="sm" className="text-xs">3M</Button>
                <Button size="sm" className="text-xs">6M</Button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mb-6">
            <Button className="bg-green-600 hover:bg-green-700 text-white text-sm">
              💶 Neue Zahlung
            </Button>
            <Button variant="outline" className="text-red-600 border-red-300 text-sm">
              🗑️ Storno
            </Button>
            <Button variant="outline" className="text-sm">
              📝 Aktennotiz
            </Button>
            <div className="ml-auto flex gap-2">
              <Button variant="outline" className="text-sm">📋 Vorgang</Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white text-sm">
                📧 E-Mail
              </Button>
              <Button 
                className="bg-red-600 hover:bg-red-700 text-white text-sm"
                onClick={async () => {
                  try {
                    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
                    
                    // Sammle alle Daten
                    const gutachtenDaten = {
                      aktenzeichen: formData.aktenzeichen,
                      auftragsdatum: formData.datum,
                      schadendatum: formData.schadendatum,
                      kennzeichen: formData.kennzeichen,
                      marke: formData.marke,
                      modell: formData.modell,
                      vin: formData.vin,
                      erstzulassung: formData.erstzulassung,
                      laufleistung: formData.laufleistung,
                      auftraggeber_name: formData.kunde_name,
                      auftraggeber_strasse: formData.kunde_strasse,
                      auftraggeber_plz: formData.kunde_plz,
                      auftraggeber_ort: formData.kunde_ort,
                      auftraggeber_telefon: formData.kunde_telefon,
                      auftraggeber_email: formData.kunde_email,
                      versicherung_name: formData.versicherung_name,
                      versicherung_schadensnummer: formData.schadensnummer,
                      sv_name: formData.sv_name,
                      sv_vorname: formData.sv_vorname,
                      sv_strasse: formData.sv_strasse,
                      sv_ort: formData.sv_ort,
                      sv_telefon: formData.sv_telefon,
                      sv_email: formData.sv_email,
                      wiederbeschaffungswert: bewertungData.wiederbeschaffungswert,
                      minderwert: minderwertErgebnis?.wert,
                      nutzungsausfall: nutzungsausfallErgebnis?.gesamtbetrag,
                      nutzungsausfall_tage: nutzungsausfallData.ausfalltage,
                      lichtbilder: formData.schadensbilder || []
                    };
                    
                    const response = await fetch(`${BACKEND_URL}/api/gutachten-pdf/generieren`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify(gutachtenDaten)
                    });
                    
                    if (!response.ok) throw new Error('PDF-Generierung fehlgeschlagen');
                    
                    // PDF herunterladen
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `Gutachten_${formData.aktenzeichen || 'neu'}_${new Date().toISOString().split('T')[0]}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                    
                    toast.success('PDF erstellt!', {
                      description: 'Das Gutachten wurde erfolgreich heruntergeladen.'
                    });
                  } catch (error) {
                    console.error('PDF-Fehler:', error);
                    toast.error('PDF konnte nicht erstellt werden', {
                      description: 'Bitte versuchen Sie es erneut.'
                    });
                  }
                }}
              >
                📄 PDF erstellen
              </Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white text-sm">
                ⬇️ Download
              </Button>
              <Button variant="outline" className="text-sm">💾 Speichern</Button>
            </div>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="allgemein" className="w-full">
            <TabsList className="grid w-full grid-cols-1 sm:grid-cols-3 mb-6">
              <TabsTrigger value="allgemein">Allgemein</TabsTrigger>
              <TabsTrigger value="anschrift">Rechnungsanschrift</TabsTrigger>
              <TabsTrigger value="einstellungen">Einstellungen</TabsTrigger>
            </TabsList>

            {/* Allgemein Tab */}
            <TabsContent value="allgemein" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Typ</label>
                  <div className="flex items-center space-x-2">
                    <input type="radio" name="typ" value="rechnung" checked readOnly />
                    <label className="text-sm">Rechnung</label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Alternative Benennung</label>
                  <Input type="text" className="h-10 text-sm" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Betreff</label>
                  <select 
                    className="h-10 w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                    value={formData.auftragstyp === 'Gutachten' ? 'Haftpflichtgutachten' : 
                           formData.auftragstyp === 'Kasko' ? 'Kasko-Gutachten' : 
                           formData.auftragstyp}
                    onChange={(e) => {
                      const betreffMap = {
                        'Haftpflichtgutachten': 'Gutachten',
                        'Kurzgutachten': 'Kurzgutachten',
                        'Kasko-Gutachten': 'Kasko',
                        'Wertgutachten': 'Wertgutachten',
                        'Reparaturbestätigung': 'Reparaturbestätigung',
                        'Stellungnahme': 'Stellungnahme'
                      };
                      setFormData(prev => ({
                        ...prev,
                        auftragstyp: betreffMap[e.target.value] || e.target.value
                      }));
                    }}
                  >
                    <option value="Haftpflichtgutachten">Haftpflichtgutachten</option>
                    <option value="Kurzgutachten">Kurzgutachten</option>
                    <option value="Kasko-Gutachten">Kasko-Gutachten</option>
                    <option value="Wertgutachten">Wertgutachten</option>
                    <option value="Reparaturbestätigung">Reparaturbestätigung</option>
                    <option value="Stellungnahme">Stellungnahme</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rechnungsdatum *</label>
                  <Input type="date" defaultValue="2025-11-08" className="h-10 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Zahlungsziel</label>
                  <Input type="date" defaultValue="2025-11-22" className="h-10 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Akte</label>
                  <select className="h-10 w-full px-3 py-2 text-sm border border-gray-300 rounded-md">
                    <option>@KH-CMD631125 Christina de Fatima Mendes Batista D-C...</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rechnungsprofil</label>
                  <div className="flex gap-2">
                    <select className="h-10 flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md">
                      <option>TüV Rheinland 2025</option>
                    </select>
                    <Button size="sm" variant="outline" className="text-sm">🔗</Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Schadenhonorar: 1951,77 €</p>
                </div>
              </div>
            </TabsContent>

            {/* Rechnungsanschrift Tab */}
            <TabsContent value="anschrift" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Firmenname</label>
                  <Input type="text" className="h-10 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Anrede</label>
                  <select className="h-10 w-full px-3 py-2 text-sm border border-gray-300 rounded-md">
                    <option>Frau</option>
                    <option>Herr</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vor- und Nachname *</label>
                  <Input type="text" defaultValue="Christina de Fatima Mendes Batista" className="h-10 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Land</label>
                  <select className="h-10 w-full px-3 py-2 text-sm border border-gray-300 rounded-md">
                    <option>Deutschland</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">PLZ / Ort *</label>
                    <Input type="text" defaultValue="40627" className="h-10 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">&nbsp;</label>
                    <Input type="text" defaultValue="Düsseldorf" className="h-10 text-sm" />
                  </div>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Straße, Nr *</label>
                  <Input type="text" defaultValue="Gerresheimer Landstraße 78" className="h-10 text-sm" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail</label>
                  <Input type="email" defaultValue="christopher.stepper@posten.de" className="h-10 text-sm" />
                </div>
              </div>
            </TabsContent>

            {/* Einstellungen Tab */}
            <TabsContent value="einstellungen" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Debitor</label>
                  <Input type="text" defaultValue="Frau Christina de Fatima Mendes Batista" className="h-10 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Konto</label>
                  <select className="h-10 w-full px-3 py-2 text-sm border border-gray-300 rounded-md">
                    <option>Standardkonto</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" checked readOnly className="w-4 h-4" />
                    <span className="text-sm font-medium text-gray-700">Hauptrechnung ℹ️</span>
                  </label>
                </div>
                <div className="col-span-2">
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" checked readOnly className="w-4 h-4" />
                    <span className="text-sm font-medium text-gray-700">Versicherungsdaten drucken</span>
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Format für E-Rechnung</label>
                  <select className="h-10 w-full px-3 py-2 text-sm border border-gray-300 rounded-md">
                    <option>ZUGFeRD</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kreditorennummer ℹ️</label>
                  <Input type="text" className="h-10 text-sm" />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Positionen Tabelle */}
          <div className="mt-8">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Positionen</h3>
            <div className="overflow-x-auto">
              <table className="w-full border">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">#</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Bezeichnung</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Honorar</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Einheit</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Anzahl</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Preis</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Gesamt</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-4 py-3 text-sm">1</td>
                    <td className="px-4 py-3 text-sm">*Grundhonorar</td>
                    <td className="px-4 py-3 text-sm">Honorar</td>
                    <td className="px-4 py-3 text-sm">Pauschal</td>
                    <td className="px-4 py-3 text-sm">Anzahl</td>
                    <td className="px-4 py-3 text-sm text-right">614,30 €</td>
                    <td className="px-4 py-3 text-sm text-right">614,30</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm">2</td>
                    <td className="px-4 py-3 text-sm">*Schadenkalkulation</td>
                    <td className="px-4 py-3 text-sm">Kalkulation</td>
                    <td className="px-4 py-3 text-sm">Pauschal</td>
                    <td className="px-4 py-3 text-sm">*</td>
                    <td className="px-4 py-3 text-sm text-right">23,80 €</td>
                    <td className="px-4 py-3 text-sm text-right">23,80</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm">3</td>
                    <td className="px-4 py-3 text-sm">*Fahrzeugbewertung</td>
                    <td className="px-4 py-3 text-sm">Bewertung</td>
                    <td className="px-4 py-3 text-sm">Pauschal</td>
                    <td className="px-4 py-3 text-sm">*</td>
                    <td className="px-4 py-3 text-sm text-right">18,00 €</td>
                    <td className="px-4 py-3 text-sm text-right">18,00</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm">4</td>
                    <td className="px-4 py-3 text-sm">*Fahrtkosten</td>
                    <td className="px-4 py-3 text-sm">Reise</td>
                    <td className="px-4 py-3 text-sm">km</td>
                    <td className="px-4 py-3 text-sm">86,00</td>
                    <td className="px-4 py-3 text-sm text-right">1,38 €</td>
                    <td className="px-4 py-3 text-sm text-right">118,68</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm">5</td>
                    <td className="px-4 py-3 text-sm">*Fotokosten</td>
                    <td className="px-4 py-3 text-sm">Foto</td>
                    <td className="px-4 py-3 text-sm">Stück</td>
                    <td className="px-4 py-3 text-sm">13,00</td>
                    <td className="px-4 py-3 text-sm text-right">2,38 €</td>
                    <td className="px-4 py-3 text-sm text-right">30,94</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm">6</td>
                    <td className="px-4 py-3 text-sm">*EDV-Kosten</td>
                    <td className="px-4 py-3 text-sm">EDV</td>
                    <td className="px-4 py-3 text-sm">Pauschal</td>
                    <td className="px-4 py-3 text-sm">*</td>
                    <td className="px-4 py-3 text-sm text-right">24,85 €</td>
                    <td className="px-4 py-3 text-sm text-right">24,85</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Summen */}
            <div className="mt-6 flex justify-end">
              <div className="w-96 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>ohne MwSt.</span>
                  <span className="font-semibold">830,57</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Rabatt</span>
                  <div className="flex items-center gap-2">
                    <Input type="number" defaultValue="0" className="w-16 h-8 text-xs text-right" />
                    <span>%</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span>MwSt.</span>
                  <div className="flex items-center gap-2">
                    <Input type="number" defaultValue="19" className="w-16 h-8 text-xs text-right" />
                    <span>%</span>
                    <span className="font-semibold">157,81</span>
                  </div>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="font-bold">mit MwSt.</span>
                  <span className="font-bold text-lg">988,38</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Skonto</span>
                  <div className="flex items-center gap-2">
                    <Input type="number" className="w-16 h-8 text-xs text-right" />
                    <span>%</span>
                    <span>Tage</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-end gap-3 mt-6">
        <Button 
          type="button" 
          variant="outline" 
          onClick={() => setActiveSection('kalkulation')} 
          className="text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg px-5 transition-all"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>
        <Button 
          type="button" 
          onClick={() => setActiveSection('dokumente')} 
          className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg px-6 shadow-md hover:shadow-lg transition-all flex items-center gap-2"
        >
          Weiter zu Dokumente
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  const renderDokumenteForm = () => (
    <div className="max-w-full 2xl:max-w-[1500px] mx-auto space-y-6">
      {/* Header mit Action Buttons */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <FolderOpen className="w-6 h-6" />
            <h2 className="text-xl font-semibold">Dokumente</h2>
          </div>
          <div className="flex gap-2">
            <Button type="button" className="bg-white text-blue-600 hover:bg-blue-50 text-sm">
              + Neu ▼
            </Button>
            <Button type="button" className="bg-white text-blue-600 hover:bg-blue-50 text-sm">
              📥 Importieren ▼
            </Button>
            <Button type="button" className="bg-white text-blue-600 hover:bg-blue-50 text-sm">
              📄 Unternehmensdokument ▼
            </Button>
          </div>
        </div>
        <p className="text-sm text-blue-100">Verwaltung aller Dokumente und Gutachten</p>
      </div>

      {/* Toolbar */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          {/* Left Side */}
          <div className="flex items-center gap-3">
            <div className="text-sm text-gray-600">
              <span className="font-semibold">Gestern</span> (+1 Tage)
            </div>
            <Button type="button" variant="outline" size="sm" className="text-sm">
              Generieren ▼
            </Button>
            <select className="h-10 text-sm px-3 py-1 border border-gray-300 rounded-md">
              <option>Zusammenstellung</option>
              <option>Gutachten</option>
              <option>Rechnung</option>
            </select>
            <Button type="button" variant="outline" size="sm" className="text-sm">
              📑 PDF-bündeln
            </Button>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            <select className="h-10 text-sm px-3 py-1 border border-gray-300 rounded-md">
              <option>Alle</option>
              <option>Gutachten</option>
              <option>Rechnungen</option>
              <option>Verträge</option>
            </select>
            <select className="h-10 text-sm px-3 py-1 border border-gray-300 rounded-md">
              <option>Erstellt am ↓</option>
              <option>Name</option>
              <option>Typ</option>
            </select>
          </div>
        </div>
      </div>

      {/* Dokumente Liste */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-12">
                <input type="checkbox" className="w-4 h-4 rounded" />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Dokumenttyp
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Erstellt am
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                Aktionen
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {/* Beispiel Dokument 1 */}
            <tr className="hover:bg-gray-50">
              <td className="px-6 py-4">
                <input type="checkbox" className="w-4 h-4 rounded" />
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center space-x-3">
                  <FileText className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Abtretungserklärung</p>
                    <p className="text-xs text-gray-500">PDF • 245 KB</p>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 text-sm text-gray-600">
                {new Date().toLocaleDateString('de-DE')}
              </td>
              <td className="px-6 py-4">
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                  Unterschrieben
                </span>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center justify-center space-x-2">
                  <button className="text-blue-600 hover:text-blue-800" title="Bearbeiten">
                    <PenTool className="w-4 h-4" />
                  </button>
                  <button className="text-green-600 hover:text-green-800" title="Download">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  </button>
                  <button className="text-red-600 hover:text-red-800" title="Löschen">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>

            {/* Beispiel Dokument 2 */}
            <tr className="hover:bg-gray-50">
              <td className="px-6 py-4">
                <input type="checkbox" className="w-4 h-4 rounded" />
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center space-x-3">
                  <FileText className="w-5 h-5 text-orange-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {formData.auftragstyp === 'Gutachten' ? 'Haftpflichtgutachten' : 
                       formData.auftragstyp === 'Kasko' ? 'Kasko-Gutachten' : 
                       formData.auftragstyp || 'Haftpflichtgutachten'}
                    </p>
                    <p className="text-xs text-gray-500">PDF • 1.2 MB</p>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 text-sm text-gray-600">
                {new Date().toLocaleDateString('de-DE')}
              </td>
              <td className="px-6 py-4">
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                  Entwurf
                </span>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center justify-center space-x-2">
                  <button className="text-blue-600 hover:text-blue-800" title="Bearbeiten">
                    <PenTool className="w-4 h-4" />
                  </button>
                  <button className="text-green-600 hover:text-green-800" title="Download">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  </button>
                  <button className="text-red-600 hover:text-red-800" title="Löschen">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Versandprotokoll & Actions */}
      <div className="bg-white border rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Versandprotokoll</h3>
            <p className="text-sm text-gray-600">Keine Versendungen dokumentiert</p>
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="outline" className="text-sm">
              🔒 Jetzt sperren
            </Button>
            <Button type="button" className="bg-green-600 hover:bg-green-700 text-white text-sm">
              📱 WhatsApp
            </Button>
            <Button type="button" className="bg-blue-600 hover:bg-blue-700 text-white text-sm">
              📧 E-Mail
            </Button>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-end gap-3 mt-6">
        <Button 
          type="button" 
          variant="outline" 
          onClick={() => setActiveSection('rechnung')} 
          className="text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg px-5 transition-all"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>
        <Button 
          type="button" 
          onClick={() => setActiveSection('leer')} 
          className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg px-6 shadow-md hover:shadow-lg transition-all flex items-center gap-2"
        >
          Weiter zu Gutachten
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  const renderLichtbilderForm = () => (
    <div className="max-w-full 2xl:max-w-[1600px] mx-auto">
      {/* Modern Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-light text-gray-900 tracking-tight">Lichtbilder</h2>
        <p className="text-sm text-gray-400 mt-1">Fotos und Dokumentation des Schadens</p>
      </div>

      {/* Upload-Bereich */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
        <div className="px-6 py-4 border-b border-gray-50 bg-gradient-to-r from-gray-50 to-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-sm">
              <Camera className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-800">Schadensfotos hochladen</h3>
              <p className="text-xs text-gray-400">Mehrere Bilder gleichzeitig hochladen</p>
            </div>
          </div>
          {lichtbilder.length > 0 && (
            <div className="text-sm text-gray-600">
              {lichtbilder.length} {lichtbilder.length === 1 ? 'Bild' : 'Bilder'}
            </div>
          )}
        </div>
        <div className="p-6">
          <div
            className={`border-2 border-dashed rounded-md p-8 text-center transition-all ${
              lichtbilderUploading
                ? 'border-green-500 bg-green-50'
                : isDraggingLichtbild
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDraggingLichtbild(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDraggingLichtbild(false);
            }}
            onDrop={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDraggingLichtbild(false);
              
              const files = Array.from(e.dataTransfer.files);
              if (files.length > 0) {
                await handleLichtbilderUpload(files);
              }
            }}
          >
            <input
              type="file"
              id="lichtbilder-upload"
              className="hidden"
              accept="image/*,video/*,.pdf,.zip,.rar,.7z"
              multiple
              onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                if (files.length > 0) {
                  await handleLichtbilderUpload(files);
                }
              }}
              disabled={lichtbilderUploading}
            />
            
            {lichtbilderUploading ? (
              <div className="flex items-center justify-start px-6">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mr-4 flex-shrink-0"></div>
                <p className="text-green-700 font-bold">Bilder werden hochgeladen...</p>
              </div>
            ) : isDraggingLichtbild ? (
              <>
                <Upload className="w-16 h-16 text-blue-600 mx-auto mb-3 animate-bounce" />
                <p className="text-blue-700 font-bold mb-1 text-lg">Bilder hier ablegen!</p>
                <p className="text-sm text-blue-600">Lassen Sie die Dateien los</p>
              </>
            ) : (
              <>
                <label htmlFor="lichtbilder-upload" className="cursor-pointer">
                  <Upload className="w-12 h-12 text-blue-500 mx-auto mb-3" />
                  <p className="text-gray-700 font-medium mb-1">Schadensfotos & Dokumente hochladen</p>
                  <p className="text-sm text-gray-500">Klicken zum Hochladen oder Drag & Drop</p>
                  <p className="text-xs text-gray-400 mt-2">
                    <span className="font-semibold">Alle gängigen Dateiformate:</span><br/>
                    Bilder (JPG, PNG, GIF, WEBP, BMP, TIFF, SVG, HEIC)<br/>
                    Videos (MP4, MOV, AVI, WEBM, MPEG, MKV)<br/>
                    Dokumente (PDF) • Archive (ZIP, RAR, 7Z)<br/>
                    Max. 50MB pro Datei - Mehrere Dateien möglich
                  </p>
                </label>
              </>
            )}
          </div>
          
          <div className="mt-4 flex items-center justify-center space-x-2 text-sm text-gray-600">
            <Sparkles className="w-4 h-4 text-yellow-500" />
            <span>Bilder werden automatisch im Gutachten-PDF eingebunden</span>
          </div>
        </div>
      </div>

      {/* Hochgeladene Dateien anzeigen */}
      {lichtbilder.length > 0 && (
        <div className="bg-white rounded-md border border-gray-200">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h3 className="text-gray-900">Hochgeladene Dateien ({lichtbilder.length})</h3>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                  </svg>
                  <span>Ziehen zum Sortieren</span>
                </div>
                <button
                  onClick={handleKiSortierung}
                  disabled={kiSortierungLoading || lichtbilder.filter(f => f.type === 'image').length === 0}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-500 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-all shadow-md hover:shadow-lg disabled:cursor-not-allowed"
                >
                  {kiSortierungLoading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Analysiere...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                      </svg>
                      <span>🤖 KI-Sortierung</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {lichtbilder.map((datei, index) => (
                <div 
                  key={index} 
                  className={`relative group cursor-move ${
                    draggedImageIndex === index ? 'opacity-50 scale-95' : ''
                  } ${
                    dragOverIndex === index ? 'ring-4 ring-blue-500 scale-105' : ''
                  } transition-all duration-200`}
                  draggable
                  onDragStart={(e) => handleImageDragStart(e, index)}
                  onDragOver={(e) => handleImageDragOver(e, index)}
                  onDrop={(e) => handleImageDrop(e, index)}
                  onDragEnd={handleImageDragEnd}
                >
                  {/* Drag-Handle Indicator */}
                  <div className="absolute top-2 left-2 bg-white/80 backdrop-blur-sm p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 cursor-move">
                    <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                    </svg>
                  </div>
                  
                  {/* Positions-Nummer */}
                  <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                    {index + 1}
                  </div>

                  {/* Thumbnail basierend auf Dateityp */}
                  {datei.type === 'image' ? (
                    <div 
                      onClick={(e) => {
                        e.stopPropagation();
                        openImageEditor(datei, index);
                      }}
                      className="cursor-pointer"
                    >
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${datei.url}`}
                        alt={`Lichtbild ${index + 1}`}
                        className="w-full h-48 object-cover rounded-lg border-2 border-gray-200 group-hover:border-blue-400 transition-all"
                        style={{
                          transform: `rotate(${datei.edits?.rotation || 0}deg) scaleX(${datei.edits?.flipH ? -1 : 1}) scaleY(${datei.edits?.flipV ? -1 : 1})`,
                          filter: `brightness(${datei.edits?.brightness || 100}%) contrast(${datei.edits?.contrast || 100}%) saturate(${datei.edits?.saturation || 100}%)`
                        }}
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all rounded-lg flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 bg-white rounded-full p-2">
                          <PenTool className="w-5 h-5 text-blue-600" />
                        </div>
                      </div>
                    </div>
                  ) : datei.type === 'video' ? (
                    <div className="w-full h-48 bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg border-2 border-gray-200 group-hover:border-blue-400 transition-all flex items-center justify-center">
                      <div className="text-center">
                        <svg className="w-12 h-12 text-purple-600 mx-auto mb-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                        </svg>
                        <p className="text-xs text-purple-700 font-medium">VIDEO</p>
                      </div>
                    </div>
                  ) : datei.type === 'pdf' ? (
                    <div className="w-full h-48 bg-gradient-to-br from-red-100 to-red-200 rounded-lg border-2 border-gray-200 group-hover:border-blue-400 transition-all flex items-center justify-center">
                      <div className="text-center">
                        <svg className="w-12 h-12 text-red-600 mx-auto mb-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                        </svg>
                        <p className="text-xs text-red-700 font-medium">PDF</p>
                      </div>
                    </div>
                  ) : datei.type === 'archive' ? (
                    <div className="w-full h-48 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-lg border-2 border-gray-200 group-hover:border-blue-400 transition-all flex items-center justify-center">
                      <div className="text-center">
                        <svg className="w-12 h-12 text-yellow-600 mx-auto mb-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                        </svg>
                        <p className="text-xs text-yellow-700 font-medium">ARCHIV</p>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-48 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg border-2 border-gray-200 group-hover:border-blue-400 transition-all flex items-center justify-center">
                      <div className="text-center">
                        <svg className="w-12 h-12 text-gray-600 mx-auto mb-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                        </svg>
                        <p className="text-xs text-gray-700 font-medium">DATEI</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Löschen-Button */}
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={async () => {
                        try {
                          const filename = datei.url.split('/').pop();
                          const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
                          await fetch(`${BACKEND_URL}/api/lichtbilder/delete/${filename}`, {
                            method: 'DELETE'
                          });
                          setLichtbilder(prev => prev.filter((_, i) => i !== index));
                          toast.success('Datei gelöscht');
                        } catch (error) {
                          toast.error('Löschen fehlgeschlagen');
                        }
                      }}
                      className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* Bildnummer unter der Kachel */}
                  <div className="mt-2 text-center">
                    <p className="text-sm font-bold text-gray-800">
                      {datei.type === 'image' ? `Bild ${index + 1}` : 
                       datei.type === 'video' ? `Video ${index + 1}` :
                       datei.type === 'pdf' ? `PDF ${index + 1}` :
                       `Datei ${index + 1}`}
                    </p>
                    {/* KI-erkannte Perspektive */}
                    {datei.type === 'image' && bildAnalyseErgebnisse[datei.url] && (
                      <p className="text-xs font-medium text-blue-600 mt-0.5">
                        {formatPerspektive(
                          bildAnalyseErgebnisse[datei.url].perspektive,
                          bildAnalyseErgebnisse[datei.url].schadenstyp
                        )}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Bildbearbeitungs-Modal */}
      {editingImage && (
        <SimpleImageEditor 
          image={editingImage}
          imageIndex={editingImageIndex}
          totalImages={lichtbilder.filter(f => f.type === 'image').length}
          onClose={closeImageEditor}
          onNext={goToNextImage}
          onPrevious={goToPreviousImage}
          onSave={() => {
            toast.success('Bild wurde aktualisiert');
          }}
        />
      )}

      {/* Navigation */}
      <div className="flex justify-end gap-3 pt-6 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={() => setActiveSection('begutachtung')}
          className="text-gray-600 hover:text-gray-800 hover:bg-gray-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>
        <Button
          type="button"
          onClick={() => setActiveSection('kalkulation')}
          className="bg-gradient-to-r from-blue-500 to-blue-700 text-white"
        >
          Weiter zu Kalkulation
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );

  // Drag & Drop Event-Handler
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      // Validierung: Nur Bilder und PDFs
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        toast.success('Ungültiges Dateiformat', {
        description: 'Bitte laden Sie ein Bild (JPG, PNG) oder PDF hoch'
      });
        return;
      }

      // Validierung: Max. 10MB
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Datei zu groß', {
        description: 'Die Datei darf maximal 10MB groß sein'
      });
        return;
      }

      // Fahrzeugschein scannen
      scanFahrzeugschein(file);
    }
  };

  const handleFileInputChange = (e) => {
    console.log('📁 File input changed!', e.target.files);
    const file = e.target.files[0];
    if (file) {
      console.log('✅ File selected:', file.name, file.type, file.size);
      scanFahrzeugschein(file);
    } else {
      console.log('❌ No file selected');
    }
  };

  const scanFahrzeugschein = async (file) => {
    setLoading(true);
    
    // Zeige Loading Toast und speichere die ID zum späteren Schließen
    const loadingToastId = toast.loading('Fahrzeugschein wird gescannt... ⏳', {
      description: 'Dies kann bis zu 10 Sekunden dauern',
    });
    
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      console.log('🚀 Starting Fahrzeugschein scan...');
      
      // Erhöhter Timeout für die API (90 Sekunden)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 90000);
      
      const response = await fetch(`${BACKEND_URL}/api/fahrzeugschein/scan`, {
        method: 'POST',
        body: uploadFormData,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Scan fehlgeschlagen');
      }

      const result = await response.json();
      console.log('📡 API Response:', result);
      
      if (result.success && result.data) {
        // Fahrzeugdaten aus der API-Response extrahieren
        const apiData = result.data;
        console.log('📄 API Response data:', apiData);
        
        // Mapping der fahrzeugschein-scanner.de API-Felder zu unserem Formular
        const updatedData = {};
        
        // Kennzeichen (API Feld: registrationNumber)
        if (apiData.registrationNumber && apiData.registrationNumber.trim()) {
          updatedData.kennzeichen = apiData.registrationNumber.trim();
        }
        
        // VIN / Fahrzeug-Identifikationsnummer (API Feld: vin)
        if (apiData.vin && apiData.vin.trim()) {
          updatedData.vin = apiData.vin.trim();
        }
        
        // Erstzulassung (API Feld: ez oder ez_string)
        const formatErstzulassung = (dateStr) => {
          if (!dateStr) return '';
          // Check if format is YYYY-MM-DD or YYYY/MM/DD
          if (dateStr.match(/^\d{4}[-\/]\d{2}[-\/]\d{2}$/)) {
            const parts = dateStr.split(/[-\/]/);
            return `${parts[2]}.${parts[1]}.${parts[0]}`; // Convert to DD.MM.YYYY
          }
          // Check if format is DD-MM-YYYY or DD/MM/YYYY
          if (dateStr.match(/^\d{2}[-\/]\d{2}[-\/]\d{4}$/)) {
            const parts = dateStr.split(/[-\/]/);
            return `${parts[0]}.${parts[1]}.${parts[2]}`; // Convert to DD.MM.YYYY
          }
          // Return as-is if already in correct format or unknown format
          return dateStr;
        };
        
        if (apiData.ez && apiData.ez.trim()) {
          updatedData.erstzulassung = formatErstzulassung(apiData.ez.trim());
        } else if (apiData.ez_string && apiData.ez_string.trim()) {
          updatedData.erstzulassung = formatErstzulassung(apiData.ez_string.trim());
        }
        
        // Kennzeichen (API Feld: registrationNumber, kennzeichen, plate)
        if (apiData.registrationNumber && apiData.registrationNumber.trim()) {
          updatedData.kennzeichen = apiData.registrationNumber.trim().toUpperCase();
        } else if (apiData.kennzeichen && apiData.kennzeichen.trim()) {
          updatedData.kennzeichen = apiData.kennzeichen.trim().toUpperCase();
        } else if (apiData.plate && apiData.plate.trim()) {
          updatedData.kennzeichen = apiData.plate.trim().toUpperCase();
        }
        
        // HSN / TSN (Herstellerschlüsselnummer / Typschlüsselnummer)
        if (apiData.hsn && apiData.hsn.trim()) {
          updatedData.hsn = apiData.hsn.trim();
        } else if (apiData.field_2_1 && apiData.field_2_1.trim()) {
          updatedData.hsn = apiData.field_2_1.trim();
        }
        if (apiData.tsn && apiData.tsn.trim()) {
          updatedData.tsn = apiData.tsn.trim();
        } else if (apiData.field_2_2 && apiData.field_2_2.trim()) {
          // TSN ist oft in field_2_2 (erste 3 Zeichen)
          const tsn = apiData.field_2_2.trim();
          updatedData.tsn = tsn.substring(0, 3);
        }
        
        // VSN (Versicherungskennzeichen) falls vorhanden
        if (apiData.vsn && apiData.vsn.trim()) {
          updatedData.vsn = apiData.vsn.trim();
        }
        
        // VIN/FIN aus Scanner (falls vorhanden)
        if (apiData.vin && apiData.vin.trim()) {
          updatedData.vin = apiData.vin.trim().toUpperCase();
        } else if (apiData.fin && apiData.fin.trim()) {
          updatedData.vin = apiData.fin.trim().toUpperCase();
        } else if (apiData.e && apiData.e.trim()) {
          // Feld E auf dem Fahrzeugschein = FIN
          updatedData.vin = apiData.e.trim().toUpperCase();
        }
        
        // Halter-Name (API Felder: name1, name2, firstname)
        let halterName = '';
        if (apiData.name1 && apiData.name1.trim()) {
          halterName = apiData.name1.trim();
        }
        if (apiData.name2 && apiData.name2.trim()) {
          halterName += (halterName ? ' ' : '') + apiData.name2.trim();
        }
        if (apiData.firstname && apiData.firstname.trim()) {
          halterName = apiData.firstname.trim() + (halterName ? ' ' : '') + halterName;
        }
        // Fallback: name Feld
        if (!halterName && apiData.name && apiData.name.trim()) {
          halterName = apiData.name.trim();
        }
        if (halterName) {
          updatedData.kunde = halterName;
          
          // Automatische Erkennung: Firma vs. Privatperson
          // Prüfe auf Firmenendungen (GmbH, UG, AG, KG, etc.)
          const firmenEndungen = [
            'gmbh', 'ug', 'ag', 'kg', 'ohg', 'gbr', 'eg', 'ev', 'e.v.',
            'gmbh & co. kg', 'gmbh & co kg', 'co. kg', 'co kg',
            'limited', 'ltd', 'inc', 'corp', 'corporation',
            'gesellschaft', 'unternehmen', 'betrieb', 'firma'
          ];
          
          const nameNormalized = halterName.toLowerCase().replace(/[.\s]/g, '');
          const istFirma = firmenEndungen.some(endung => 
            nameNormalized.includes(endung.replace(/[.\s]/g, ''))
          );
          
          // Setze Vorsteuerabzugsberechtigt automatisch
          updatedData.vorsteuerabzugsberechtigt = istFirma ? 'Ja' : 'Nein';
        }
        
        // Adresse (API Felder: address1, address2)
        if (apiData.address1 && apiData.address1.trim()) {
          updatedData.adresse1 = apiData.address1.trim();
        } else if (apiData.address && apiData.address.trim()) {
          updatedData.adresse1 = apiData.address.trim();
        }
        
        if (apiData.address2 && apiData.address2.trim()) {
          updatedData.adresse2 = apiData.address2.trim();
        }
        
        // Marke / Hersteller (API Feld: field_2 - PRIMÄR, dann Fallbacks)
        if (apiData.field_2 && apiData.field_2.trim()) {
          updatedData.marke = apiData.field_2.trim();
          updatedData.hersteller = apiData.field_2.trim(); // Auch als hersteller setzen
        } else if (apiData.field_2_2 && apiData.field_2_2.trim()) {
          updatedData.marke = apiData.field_2_2.trim();
          updatedData.hersteller = apiData.field_2_2.trim();
        } else if (apiData.d1 && apiData.d1.trim()) {
          updatedData.marke = apiData.d1.trim();
          updatedData.hersteller = apiData.d1.trim();
        } else if (apiData.maker && apiData.maker.trim()) {
          updatedData.marke = apiData.maker.trim();
          updatedData.hersteller = apiData.maker.trim();
        }
        
        // Modell / Typ (API Feld: d3 - PRIMÄR, dann Fallbacks)
        if (apiData.d3 && apiData.d3.trim()) {
          updatedData.modell = apiData.d3.trim();
        } else if (apiData.field_3 && apiData.field_3.trim()) {
          updatedData.modell = apiData.field_3.trim();
        } else if (apiData.d2_1 && apiData.d2_1.trim()) {
          updatedData.modell = apiData.d2_1.trim();
        } else if (apiData.model && apiData.model.trim()) {
          updatedData.modell = apiData.model.trim();
        }
        
        // Fahrzeugklasse (API Feld: j)
        if (apiData.j && apiData.j.trim()) {
          updatedData.fahrzeugklasse = apiData.j.trim();
        }
        
        // Fahrzeugart/Aufbau (API Feld: j, aufbau, vehicle_type)
        if (apiData.aufbau && apiData.aufbau.trim()) {
          updatedData.fahrzeugart = apiData.aufbau.trim();
        } else if (apiData.vehicle_type && apiData.vehicle_type.trim()) {
          updatedData.fahrzeugart = apiData.vehicle_type.trim();
        }
        
        // HU Datum (API Feld: hu)
        if (apiData.hu && apiData.hu.trim()) {
          updatedData.hu_datum = apiData.hu.trim();
        }
        
        // Laufleistung / Kilometerstand (API Felder: km, mileage, laufleistung, kilometerstand)
        if (apiData.km && apiData.km.toString().trim()) {
          updatedData.laufleistung = apiData.km.toString().trim();
        } else if (apiData.mileage && apiData.mileage.toString().trim()) {
          updatedData.laufleistung = apiData.mileage.toString().trim();
        } else if (apiData.laufleistung && apiData.laufleistung.toString().trim()) {
          updatedData.laufleistung = apiData.laufleistung.toString().trim();
        } else if (apiData.kilometerstand && apiData.kilometerstand.toString().trim()) {
          updatedData.laufleistung = apiData.kilometerstand.toString().trim();
        }
        
        // Kraftstoff / Kraftstoffart (API Feld: p3, P.3, kraftstoff, fuel_type, field_p3)
        // P.3 auf dem Fahrzeugschein = Kraftstoffart oder Energiequelle
        if (apiData.p3 && apiData.p3.trim()) {
          updatedData.kraftstoff = apiData.p3.trim();
        } else if (apiData['P.3'] && apiData['P.3'].trim()) {
          updatedData.kraftstoff = apiData['P.3'].trim();
        } else if (apiData.field_p3 && apiData.field_p3.trim()) {
          updatedData.kraftstoff = apiData.field_p3.trim();
        } else if (apiData.kraftstoff && apiData.kraftstoff.trim()) {
          updatedData.kraftstoff = apiData.kraftstoff.trim();
        } else if (apiData.fuel_type && apiData.fuel_type.trim()) {
          updatedData.kraftstoff = apiData.fuel_type.trim();
        } else if (apiData.fuel && apiData.fuel.trim()) {
          updatedData.kraftstoff = apiData.fuel.trim();
        }
        
        // Hubraum (API Feld: p1, P.1, hubraum, cubic_capacity)
        // P.1 auf dem Fahrzeugschein = Hubraum in cm³
        if (apiData.p1 && apiData.p1.toString().trim()) {
          updatedData.hubraum = apiData.p1.toString().trim();
        } else if (apiData['P.1'] && apiData['P.1'].toString().trim()) {
          updatedData.hubraum = apiData['P.1'].toString().trim();
        } else if (apiData.hubraum && apiData.hubraum.toString().trim()) {
          updatedData.hubraum = apiData.hubraum.toString().trim();
        } else if (apiData.cubic_capacity && apiData.cubic_capacity.toString().trim()) {
          updatedData.hubraum = apiData.cubic_capacity.toString().trim();
        }
        
        // Leistung in kW (API Feld: p2, P.2, leistung, power)
        // P.2 auf dem Fahrzeugschein = Nennleistung in kW
        if (apiData.p2 && apiData.p2.toString().trim()) {
          updatedData.leistung = apiData.p2.toString().trim();
        } else if (apiData['P.2'] && apiData['P.2'].toString().trim()) {
          updatedData.leistung = apiData['P.2'].toString().trim();
        } else if (apiData.leistung && apiData.leistung.toString().trim()) {
          updatedData.leistung = apiData.leistung.toString().trim();
        } else if (apiData.power && apiData.power.toString().trim()) {
          updatedData.leistung = apiData.power.toString().trim();
        }
        
        console.log('✅ Mapped data:', updatedData);
        console.log(`📊 ${Object.keys(updatedData).length} Felder gefunden`);
        console.log('🔍 Kennzeichen:', updatedData.kennzeichen || '-');
        console.log('🔍 HSN/TSN:', updatedData.hsn || '-', '/', updatedData.tsn || '-');
        console.log('🔍 Marke/Modell:', updatedData.marke || '-', '/', updatedData.modell || '-');
        console.log('🔍 Erstzulassung:', updatedData.erstzulassung || '-');
        console.log('🔍 HU bis:', updatedData.hu_datum || '-');
        console.log('🔍 Kraftstoff:', updatedData.kraftstoff || '-');
        console.log('🔍 Hubraum/Leistung:', updatedData.hubraum || '-', 'cm³ /', updatedData.leistung || '-', 'kW');
        console.log('🔍 Halter:', updatedData.kunde || '-');
        console.log('📋 Alle API-Felder:', Object.keys(apiData));
        
        if (Object.keys(updatedData).length > 0) {
          console.log('🔄 Current formData before update:', formData);
          
          // Wenn noch keine Akte erstellt wurde, erstelle jetzt eine
          if (!akteId) {
            console.log('📋 Erstelle neue Akte nach Fahrzeugschein-Scan...');
            
            // Generiere Aktenzeichen falls noch nicht vorhanden
            let neuesAktenzeichen = aktenzeichen;
            if (!neuesAktenzeichen) {
              // Extrahiere Kundenname (vom Scan oder aus formData)
              const kundenname = updatedData.kunde || formData.kunde || '';
              const initialen = extrahiereInitialen(kundenname);
              
              // Generiere Basis-Aktenzeichen mit Initialen
              const heute = new Date();
              const tag = String(heute.getDate()).padStart(2, '0');
              const monat = String(heute.getMonth() + 1).padStart(2, '0');
              const jahr = heute.getFullYear().toString().slice(-2);
              const basisAktenzeichen = `KH-${initialen} ${tag}${monat}${jahr}`;
              
              // Prüfe ob bereits existiert
              const checkResponse = await fetch(`${BACKEND_URL}/api/akten/check-aktenzeichen?basis=${basisAktenzeichen}`);
              const checkData = await checkResponse.json();
              
              if (checkData.exists) {
                // Duplikat gefunden, füge Nummer mit Schrägstrich hinzu
                neuesAktenzeichen = `${basisAktenzeichen}/${String(checkData.nextNumber).padStart(2, '0')}`;
              } else {
                // Erste Akte, keine Nummer
                neuesAktenzeichen = basisAktenzeichen;
              }
              
              setAktenzeichen(neuesAktenzeichen);
            }
            
            updatedData.aktenzeichen = neuesAktenzeichen;
            
            // Erstelle Akte in der Datenbank mit ALLEN bisherigen Daten
            try {
              const akteResponse = await fetch(`${BACKEND_URL}/api/akten/speichern`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  aktenzeichen: neuesAktenzeichen,
                  gutachtentyp: 'Haftpflichtgutachten',
                  arbeitsschritt: 'ERSTELT',
                  status: 'OFFEN',
                  ...formData,  // Alle bisherigen Daten
                  ...updatedData // + neue Scan-Daten
                })
              });
              
              if (akteResponse.ok) {
                const akteData = await akteResponse.json();
                setAkteId(akteData.akte_id);
                setFormData(prev => ({
                  ...prev,
                  id: akteData.akte_id
                }));
                console.log('✅ Akte erstellt:', akteData.akte_id);
              }
            } catch (akteError) {
              console.error('Fehler beim Erstellen der Akte:', akteError);
            }
          } else {
            // Akte existiert bereits, nur aktualisieren
            await autoSaveAkte(updatedData);
          }
          
          setFormData(prev => {
            const newData = {
              ...prev,
              ...updatedData
            };
            console.log('🆕 New formData after update:', newData);
            return newData;
          });

          // Schließe Loading Toast und zeige Erfolg
          toast.dismiss(loadingToastId);
          toast.success('Fahrzeugschein erfolgreich gescannt! ✅', {
            description: akteId 
              ? `${Object.keys(updatedData).length} Felder aktualisiert`
              : `Akte ${updatedData.aktenzeichen} erstellt mit ${Object.keys(updatedData).length} Feldern`
          });
        } else {
          // Schließe Loading Toast und zeige Warnung
          toast.dismiss(loadingToastId);
          toast.warning('Scan abgeschlossen ⚠️', {
            description: 'Keine verwertbaren Daten gefunden. Bitte verwenden Sie ein klares Foto des Fahrzeugscheins.'
          });
        }
      } else {
        throw new Error(result.error || 'Keine Daten erkannt');
      }
    } catch (error) {
      console.error('❌ Fahrzeugschein Scan Error:', error);
      
      // Schließe Loading Toast
      toast.dismiss(loadingToastId);
      
      if (error.name === 'AbortError') {
        toast.error('Timeout', {
          description: 'Der Scan hat zu lange gedauert. Bitte versuchen Sie es erneut.'
        });
      } else {
        toast.error('Scan fehlgeschlagen', {
          description: error.message || 'Der Fahrzeugschein konnte nicht gescannt werden. Bitte versuchen Sie es erneut.'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const renderAkteForm = () => (
    <div className="max-w-full 2xl:max-w-[1600px] mx-auto">
      {/* Modern Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-light text-gray-900 tracking-tight">{formData.auftragstyp || 'Neue Akte'}</h2>
        <p className="text-sm text-gray-400 mt-1">{new Date().toLocaleDateString('de-DE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
      </div>

      <div className="space-y-6">
        {/* Fahrzeugschein Scanner Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
          <div className="px-6 py-4 border-b border-gray-50 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-sm">
                <Camera className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Fahrzeugschein Scanner</h3>
                <p className="text-xs text-gray-400">Automatisch ausfüllen lassen</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div 
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                loading ? 'border-blue-400 bg-blue-50/50' : isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-gray-50/50 hover:border-blue-400 hover:bg-blue-50/30 cursor-pointer'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input type="file" accept="image/*,application/pdf" className="hidden" id="fahrzeugschein-upload" onChange={handleFileInputChange} disabled={loading} />
              {loading ? (
                <div className="flex items-center justify-center gap-4">
                  <div className="w-12 h-12 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                  <div className="text-left">
                    <p className="text-blue-700 font-medium">Wird gescannt...</p>
                    <p className="text-sm text-blue-500">Bitte warten</p>
                  </div>
                </div>
              ) : (
                <label htmlFor="fahrzeugschein-upload" className="cursor-pointer block">
                  <Upload className={`w-10 h-10 mx-auto mb-3 ${isDragging ? 'text-blue-600 animate-bounce' : 'text-gray-400'}`} />
                  <p className="text-gray-700 font-medium">{isDragging ? 'Jetzt loslassen!' : 'Fahrzeugschein hochladen'}</p>
                  <p className="text-sm text-gray-400 mt-1">Drag & Drop oder klicken</p>
                </label>
              )}
            </div>
          </div>
        </div>

        {/* Kundendaten Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
          <div className="px-6 py-4 border-b border-gray-50 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm">
                <Users className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Kundendaten</h3>
                <p className="text-xs text-gray-400">Auftraggeber & Kontakt</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Name / Firma *</label>
                <Input type="text" name="kunde" placeholder="Max Mustermann" value={formData.kunde} onChange={handleInputChange} className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.kunde)} required />
              </div>
              <div className="relative">
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Adresse</label>
                <Input type="text" name="adresse1" placeholder="Straße und Hausnummer" value={formData.adresse1} onChange={handleInputChange} onFocus={() => setSelectedFieldForAddress('adresse1')} className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.adresse1)} autoComplete="off" />
                {showAdressSuggestions && selectedFieldForAddress === 'adresse1' && adressSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-2 bg-white rounded-xl shadow-xl border border-gray-100 max-h-48 overflow-y-auto">
                    {adressSuggestions.map((adresse, index) => (
                      <div key={index} onClick={() => selectAddress(adresse)} className="px-4 py-3 hover:bg-gray-50 cursor-pointer text-sm transition-colors">
                        <div className="font-medium text-gray-800">{adresse.strasse && adresse.hausnummer ? `${adresse.strasse} ${adresse.hausnummer}` : adresse.strasse || adresse.display_name.split(',')[0]}</div>
                        <div className="text-xs text-gray-400">{adresse.plz} {adresse.ort}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="relative">
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">PLZ / Ort</label>
                <Input type="text" name="adresse2" placeholder="80331 München" value={formData.adresse2} onChange={handleInputChange} onFocus={() => setSelectedFieldForAddress('adresse2')} className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.adresse2)} autoComplete="off" />
                {showAdressSuggestions && selectedFieldForAddress === 'adresse2' && adressSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-2 bg-white rounded-xl shadow-xl border border-gray-100 max-h-48 overflow-y-auto">
                    {adressSuggestions.map((adresse, index) => (
                      <div key={index} onClick={() => selectAddress(adresse)} className="px-4 py-3 hover:bg-gray-50 cursor-pointer text-sm transition-colors">
                        <div className="font-medium text-gray-800">{adresse.plz} {adresse.ort}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Telefon</label>
                <Input type="tel" name="kunde_telefon" placeholder="+49 176 12345678" value={formData.kunde_telefon} onChange={handleInputChange} className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.kunde_telefon)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">E-Mail</label>
                <Input type="email" name="kunde_email" placeholder="kunde@beispiel.de" value={formData.kunde_email} onChange={handleInputChange} className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.kunde_email)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Vorsteuerabzug</label>
                <select name="vorsteuerabzugsberechtigt" value={formData.vorsteuerabzugsberechtigt} onChange={handleInputChange} className="h-11 w-full px-3 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all cursor-pointer">
                  <option value="Ja">Ja</option>
                  <option value="Nein">Nein</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Fahrzeugdaten Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
          <div className="px-6 py-4 border-b border-gray-50 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                <Car className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Fahrzeugdaten</h3>
                <p className="text-xs text-gray-400">Informationen zum Fahrzeug</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {/* Zeile 1: Grunddaten */}
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Fahrzeugtyp</label>
                <select name="fahrzeugtyp" value={formData.fahrzeugtyp} onChange={handleInputChange} className="h-11 w-full px-3 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all cursor-pointer">
                  <option value="PKW">PKW</option>
                  <option value="LKW">LKW</option>
                  <option value="Motorrad">Motorrad</option>
                  <option value="Transporter">Transporter</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Kennzeichen</label>
                <div className="flex items-center bg-gray-50 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500/20 transition-all">
                  <div className="w-10 h-11 bg-gradient-to-b from-blue-600 to-blue-700 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold">D</span>
                  </div>
                  <input type="text" name="kennzeichen" placeholder="M-AB 1234" value={formData.kennzeichen} onChange={handleInputChange} className="flex-1 px-3 py-2.5 text-sm font-semibold tracking-wider uppercase bg-transparent border-none focus:outline-none" style={{ fontFamily: 'ui-monospace, monospace' }} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Marke / Hersteller</label>
                <Input type="text" name="marke" placeholder="BMW, Mercedes..." value={formData.marke || ''} onChange={handleInputChange} className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.marke)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Modell / Typ</label>
                <Input type="text" name="modell" placeholder="3er, C-Klasse..." value={formData.modell || ''} onChange={handleInputChange} className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.modell)} />
              </div>
              
              {/* Zeile 2: HSN/TSN und FIN */}
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">HSN</label>
                <Input type="text" name="hsn" placeholder="0005" value={formData.hsn || ''} onChange={handleInputChange} className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-mono " + getInputClassName(formData.hsn)} maxLength={4} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">TSN</label>
                <Input type="text" name="tsn" placeholder="AKE" value={formData.tsn || ''} onChange={handleInputChange} className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-mono " + getInputClassName(formData.tsn)} maxLength={3} />
              </div>
              <div className="lg:col-span-2">
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">FIN / VIN</label>
                <div className="flex gap-2">
                  <Input type="text" name="vin" placeholder="Fahrzeug-Identifikationsnummer" value={formData.vin || ''} onChange={handleInputChange} className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all flex-1 font-mono " + getInputClassName(formData.vin)} />
                  <Button type="button" className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 h-11 rounded-lg shadow-sm" title="Suchen">
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Zeile 3: Zulassungsdaten */}
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Erstzulassung</label>
                <Input type="text" name="erstzulassung" placeholder="15.03.2020" value={formData.erstzulassung || ''} onChange={handleInputChange} className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.erstzulassung)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">HU bis</label>
                <Input type="text" name="hu_datum" placeholder="03/2025" value={formData.hu_datum || ''} onChange={handleInputChange} className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.hu_datum)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Fahrzeugklasse</label>
                <Input type="text" name="fahrzeugklasse" placeholder="M1" value={formData.fahrzeugklasse || ''} onChange={handleInputChange} className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.fahrzeugklasse)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Fahrzeugart / Aufbau</label>
                <Input type="text" name="fahrzeugart" placeholder="Limousine" value={formData.fahrzeugart || ''} onChange={handleInputChange} className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.fahrzeugart)} />
              </div>
              
              {/* Zeile 4: Technische Daten */}
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Laufleistung (km)</label>
                <Input type="number" name="laufleistung" placeholder="75000" value={formData.laufleistung || ''} onChange={(e) => { const value = e.target.value; if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 0 && parseInt(value) <= 999999)) { handleInputChange(e); } }} className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.laufleistung)} min="0" max="999999" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Kraftstoff</label>
                <select name="kraftstoff" value={formData.kraftstoff || ''} onChange={handleInputChange} className={"h-11 w-full px-3 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all cursor-pointer " + getInputClassName(formData.kraftstoff)}>
                  <option value="">-- Auswählen --</option>
                  <option value="Benzin">Benzin</option>
                  <option value="Diesel">Diesel</option>
                  <option value="Elektro">Elektro</option>
                  <option value="Hybrid">Hybrid</option>
                  <option value="Plug-in-Hybrid">Plug-in-Hybrid</option>
                  <option value="Erdgas (CNG)">Erdgas (CNG)</option>
                  <option value="Autogas (LPG)">Autogas (LPG)</option>
                  <option value="Wasserstoff">Wasserstoff</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Hubraum (cm³)</label>
                <Input type="number" name="hubraum" placeholder="1998" value={formData.hubraum || ''} onChange={handleInputChange} className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.hubraum)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Leistung (kW)</label>
                <Input type="number" name="leistung" placeholder="110" value={formData.leistung || ''} onChange={handleInputChange} className={"h-11 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all " + getInputClassName(formData.leistung)} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-end mt-8">
        <Button type="button" onClick={() => setActiveSection('beteiligte')} className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg px-6 shadow-md hover:shadow-lg transition-all">
          Weiter
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );

  const [uploadedImage, setUploadedImage] = useState(null);
  const [scanning, setScanning] = useState(false);

  const [uploadedFile, setUploadedFile] = useState(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Datei speichern für späteren Scan
    setUploadedFile(file);

    // Vorschau erstellen
    const reader = new FileReader();
    reader.onloadend = () => {
      setUploadedImage(reader.result);
    };
    reader.readAsDataURL(file);

    // Automatisch scannen starten
    await handleScan(file);
  };

  const handleScan = async (file) => {
    if (!file) return;
    
    setScanning(true);
    
    try {
      // FormData für File-Upload erstellen
      const formData = new FormData();
      formData.append('file', file);

      // API-Request
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/fahrzeugschein/scan`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('OCR-Scan fehlgeschlagen');
      }

      const extractedData = await response.json();

      // Felder automatisch befüllen
      setFormData(prevData => ({
        ...prevData,
        kennzeichen: extractedData.kennzeichen || prevData.kennzeichen,
        vin: extractedData.vin || prevData.vin,
        // Weitere Felder können hier hinzugefügt werden
      }));

      toast.success('Scan erfolgreich', {
        description: `Fahrzeugdaten erkannt: ${extractedData.marke || ''} ${extractedData.modell || ''}`
      });
    } catch (err) {
      console.error('OCR Error:', err);
      toast.error('Scan fehlgeschlagen', {
        description: 'Fahrzeugschein konnte nicht gelesen werden. Bitte versuchen Sie es erneut.'
      });
    } finally {
      setScanning(false);
    }
  };

  const renderOldAkteFormCode = () => (
    <form onSubmit={handleSubmit}>
      <div className="border-0 shadow-lg mb-6">
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="flex items-center space-x-2">
            <span>📄 Fahrzeugschein Scanner</span>
          </h3>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {/* Upload Area */}
            {!uploadedImage ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
                <input
                  type="file"
                  id="fahrzeugschein-upload"
                  accept="image/*,application/pdf"
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={loading || scanning}
                />
                <label
                  htmlFor="fahrzeugschein-upload"
                  className="cursor-pointer flex flex-col items-center space-y-3"
                >
                  <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-gray-900">Fahrzeugschein hochladen</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Klicken zum Auswählen oder Datei hierher ziehen
                    </p>
                    <p className="text-xs text-gray-400 mt-2">
                      Unterstützt: JPG, PNG, PDF
                    </p>
                  </div>
                </label>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Bildvorschau */}
                <div className="relative rounded-lg overflow-hidden border border-gray-200">
                  <img 
                    src={uploadedImage} 
                    alt="Fahrzeugschein" 
                    className="w-full h-64 object-contain bg-gray-50"
                  />
                  {scanning && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-start pl-12">
                      <div className="flex items-center text-white">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mr-4 flex-shrink-0"></div>
                        <p className="font-semibold text-lg">Scanne Fahrzeugschein...</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Aktionen */}
                <div className="flex space-x-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setUploadedImage(null);
                      setUploadedFile(null);
                    }}
                    disabled={scanning}
                    className="flex-1"
                  >
                    Anderes Bild hochladen
                  </Button>
                  <Button
                    type="button"
                    onClick={() => handleScan(uploadedFile)}
                    disabled={scanning || !uploadedFile}
                    className="flex-1 bg-gradient-to-r from-blue-500 to-blue-700 text-white"
                  >
                    {scanning ? 'Wird gescannt...' : 'Erneut scannen'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Kundendaten */}
      <div className="bg-white rounded-md border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-medium">Kundendaten</h3>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-blue-700 mb-2">
                Kunde <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                name="kunde"
                placeholder="z.B. Max Mustermann"
                value={formData.kunde}
                onChange={handleChange}
                required
                disabled={loading}
                className="h-12"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-700 mb-2">
                Fahrzeugtyp <span className="text-red-500">*</span>
              </label>
              <select
                name="fahrzeugtyp"
                value={formData.fahrzeugtyp}
                onChange={handleChange}
                required
                disabled={loading}
                className="w-full h-12 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-green-50"
              >
                <option value="PKW">PKW</option>
                <option value="LKW">LKW</option>
                <option value="Motorrad">Motorrad</option>
                <option value="Transporter">Transporter</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-700 mb-2">
                Adresse 1 <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                name="adresse1"
                placeholder="Straße und Hausnummer"
                value={formData.adresse1}
                onChange={handleChange}
                required
                disabled={loading}
                className="h-12"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-700 mb-2">
                Adresse 2 (PLZ/Ort) <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                name="adresse2"
                placeholder="PLZ Ort"
                value={formData.adresse2}
                onChange={handleChange}
                required
                disabled={loading}
                className="h-12 bg-blue-50"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Fahrzeugdaten */}
      <div className="bg-white rounded-md border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-medium">Fahrzeugdaten</h3>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-blue-700 mb-2">
                Amtl. Kennzeichen <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Input
                  type="text"
                  name="kennzeichen"
                  placeholder="K-AB 1234"
                  value={formData.kennzeichen}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="h-12 pr-10"
                />
                <button 
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-blue-600"
                >
                  🔍
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-700 mb-2">
                Vers.-Nummer <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                name="vers_nummer"
                placeholder="123456789"
                value={formData.vers_nummer}
                onChange={handleChange}
                required
                disabled={loading}
                className="h-12"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Selbstbeteiligung
              </label>
              <select
                name="selbstbeteiligung"
                value={formData.selbstbeteiligung}
                onChange={handleChange}
                disabled={loading}
                className="w-full h-12 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-green-50"
              >
                <option value="0">0 €</option>
                <option value="150">150 €</option>
                <option value="300">300 €</option>
                <option value="500">500 €</option>
                <option value="1000">1000 €</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Marke
                  </label>
                  <Input
                    type="text"
                    name="marke"
                    placeholder="Fahrzeugmarke"
                    value={formData.marke}
                    onChange={handleChange}
                    disabled={loading}
                    className="h-12"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Model
                  </label>
                  <Input
                    type="text"
                    name="modell"
                    placeholder="Model"
                    value={formData.modell}
                    onChange={handleChange}
                    disabled={loading}
                    className="h-12"
                  />
                </div>
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                VIN / Fahrgestellnummer <span className="text-gray-400">(17-stellige Fahrzeug-Identifikationsnummer)</span>
              </label>
              <Input
                type="text"
                name="vin"
                placeholder="z.B. WBAVA31030NL12345"
                value={formData.vin}
                onChange={handleChange}
                maxLength={17}
                disabled={loading}
                className="h-12"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Erstzulassung <span className="text-gray-400">(wird automatisch vom Fahrzeugschein ausgelesen)</span>
              </label>
              <Input
                type="text"
                name="erstzulassung"
                placeholder="z.B. 15.03.2020 oder 03/2020"
                value={formData.erstzulassung}
                onChange={handleChange}
                disabled={loading}
                className="h-12"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Optional: 17-stellige VIN (Vehicle Identification Number)
              </label>
              <Input
                type="text"
                name="vin_optional"
                placeholder=""
                value={formData.vin_optional}
                onChange={handleChange}
                maxLength={17}
                disabled={loading}
                className="h-12"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Auftragsdaten */}
      <div className="bg-white rounded-md border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-medium">Auftragsdaten</h3>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-blue-700 mb-2">
                Schadentag <span className="text-red-500">*</span>
              </label>
              <Input
                type="date"
                name="schadentag"
                value={formData.schadentag}
                onChange={handleChange}
                required
                disabled={loading}
                className="h-12 bg-green-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Anfrageart
              </label>
              <select
                name="anfrageart"
                value={formData.anfrageart}
                onChange={handleChange}
                disabled={loading}
                className="w-full h-12 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-green-50"
              >
                <option value="1 - Haftpflicht">1 - Haftpflicht</option>
                <option value="2 - Kasko">2 - Kasko</option>
                <option value="3 - Teilkasko">3 - Teilkasko</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-700 mb-2">
                Schadenort <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                name="schadenort"
                placeholder="Unfallort"
                value={formData.schadenort}
                onChange={handleChange}
                required
                disabled={loading}
                className="h-12 bg-blue-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Schadennummer
              </label>
              <Input
                type="text"
                name="schadennummer"
                placeholder="z.B. SN-2024-001"
                value={formData.schadennummer}
                onChange={handleChange}
                disabled={loading}
                className="h-12"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-700 mb-2">
                Welche Scheibe? <span className="text-red-500">*</span>
              </label>
              <select
                name="welche_scheibe"
                value={formData.welche_scheibe}
                onChange={handleChange}
                required
                disabled={loading}
                className="w-full h-12 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-green-50"
              >
                <option value="Frontscheibe">Frontscheibe</option>
                <option value="Heckscheibe">Heckscheibe</option>
                <option value="Seitenscheibe links">Seitenscheibe links</option>
                <option value="Seitenscheibe rechts">Seitenscheibe rechts</option>
                <option value="Schiebedach">Schiebedach</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-700 mb-2">
                Auftragstyp <span className="text-red-500">*</span>
              </label>
              <select
                name="auftragstyp"
                value={formData.auftragstyp}
                onChange={handleChange}
                required
                disabled={loading}
                className="w-full h-12 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-green-50"
              >
                <option value="Gutachten">Haftpflichtgutachten</option>
                <option value="Kurzgutachten">Kurzgutachten</option>
                <option value="Kasko">Kasko-Gutachten</option>
                <option value="Wertgutachten">Wertgutachten</option>
                <option value="Reparaturbestätigung">Reparaturbestätigung</option>
                <option value="Stellungnahme">Stellungnahme</option>
                <option value="Reparaturauftrag">Reparaturauftrag</option>
                <option value="Austauschauftrag">Austauschauftrag</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vorsteuerabzugsberechtigt
              </label>
              <select
                name="vorsteuerabzugsberechtigt"
                value={formData.vorsteuerabzugsberechtigt}
                onChange={handleChange}
                disabled={loading}
                className="w-full h-12 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-green-50"
              >
                <option value="Nein">Nein</option>
                <option value="Ja">Ja</option>
              </select>
            </div>
            </div>

            {/* Buttons */}
            <div className="flex items-center justify-end space-x-4 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/gutachten')}
                disabled={loading}
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Wird erstellt...' : 'Akte erstellen'}
              </Button>
            </div>
          </div>
        </div>
      </form>
  );

  return (
    <DashboardLayout>
      <div className="flex flex-col lg:flex-row h-full">
        {/* Akte-spezifische Sidebar */}
        <AkteSidebar 
          activeSection={activeSection}
          onSectionChange={setActiveSection}
          aktenzeichen={formData.aktenzeichen}
        />
        
        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6">
          {renderSectionContent()}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default NeueAkte;
